// Tiny typed API client. All requests go through Vite's /api proxy to FastAPI on 8001.

// ClickPay login UI: a 401 mid-session means the session cookie expired or
// was revoked while CLICKPAY_AUTH=on — notify the AuthProvider so the app
// routes to the Login page. Request logic is otherwise unchanged.
import { emitSessionExpired } from './authEvents'
import { afterPendingWrite } from './pendingEditsEvents'
import { operatorScopeHeaders } from './operatorScope'
import type { HeatmapMetric, HourlyHeatmapResponse } from './hourlyHeatmap'
import { perfRecord } from './perfMark'
import { ApiError } from './query/apiError'
import { currentRequestSignal } from './query/requestSignal'
import { noteServerDate } from './clock'
import type { Lane, PreviewRecord } from './automations/contracts'
import type { SearchTermCoverage } from './searchTermCoverage'
import type { CommandSearchResult } from './commandPalette'

import type { AuditLatest, AuditOne, AuditSnapshot } from './auditApi'
export { ApiError, isApiError } from './query/apiError'
export { withRequestSignal, isAbortError } from './query/requestSignal'

export type Rule = {
  id: number
  name: string
  description: string | null
  data_source: string
  action_type: string
  schedule_cron: string
  cooldown_tag: string | null
  cooldown_days: number | null
  account_scope: string | null
  enabled: boolean
  dry_run: boolean
  created_at: string
  last_run_status: string | null
  last_run_at: string | null
  last_run_matched: number | null
  last_run_changed: number | null
  /** Whether the last run was a test (dry) run; NULL/absent when there is no
   *  run. Feeds describeRunStatus via isTestRun. */
  last_run_dry_run?: boolean | null
  /** Ad platform — v1 always 'amazon'. See migration 033. */
  platform: string
  /** Number of times this rule fired in the last 7 days. */
  runs_7d_count: number
  /** Per-rule additive lag buffer (days) added on top of every source's
   *  default freshness lag. Null means 0 (no buffer; use raw per-source
   *  defaults). Migration 048 redefined this from an override into an
   *  additive cushion. */
  lag_days: number | null
  /** Test Run mode (migration 049). When `test_run=true` and
   *  `test_run_consumed_at` is null, the next fire is forced live regardless
   *  of `dry_run`; after that fire the runner sets `test_run_consumed_at`
   *  and flips `dry_run` to true. Operators manually promote by un-checking
   *  both Test Run and Dry Run in the editor. */
  test_run: boolean
  test_run_consumed_at: string | null
  /** v5 (migration 060) — When non-null, the rule is archived: hidden
   *  from the default Rules list, enabled forced FALSE, dry_run forced TRUE.
   *  Restore via POST /api/rules/{id}/unarchive. */
  archived_at: string | null
  /** v6 — when 'tree', this rule's body is in `action_tree_json` (decision
   *  tree). Otherwise it's a legacy flat rule (filter_json + action_type +
   *  action_params). */
  rule_kind: string | null
  /** Stable BB-PPC-NNNN / BB-TREE-XXX-NNN identifier. */
  rule_code: string | null
  /** Cross-rule priority for same-tick collisions (lower wins). */
  priority: number | null
  /** When true, the rule may use the pause_campaign action and ignores
   *  the per-ASIN goal_rules_enabled toggle. v6 — Inventory Guard only. */
  safety_rail: boolean
  /** Branch count for tree rules; null for flat. */
  branch_count: number | null
  // Phase 17 "cap-visibility" — READ-ONLY (absent from RuleInput on
  // purpose; editing needs owner-set cap values, a later item). Columns
  // already existed on bbpro_rules (migrations 007, 015); only
  // max_entity_changes_per_day was ever exposed before this. The runner
  // reads these at backend/engine/runner.py:1429-1430 / :1049.
  /** Threshold: a LIVE fire matching more than this parks for confirmation. */
  max_records_per_fire: number | null
  /** Threshold: refuses the WHOLE fire (dry or live, today) once today's
   *  already-applied + this fire's matched count exceeds it. This is the
   *  cap that has actually been blocking Bid Optimizer — see
   *  frontend/src/lib/ruleHealth.ts. */
  max_records_per_day: number | null
  max_spend_delta_per_fire: number | null
  /** Migration 153 — this rule opted out of BOTH row caps above. Writable
   *  (see RuleInput), unlike the read-only trio around it: the whole point is
   *  that an operator can clear a cap they can SEE, rather than discovering a
   *  silent refusal after the rule parks. Bypasses ONLY the two row counts —
   *  max_spend_delta_per_fire and max_entity_changes_per_day still apply. */
  bypass_record_caps: boolean
  /** Migration 156. Off (default) means a @once rule self-archives after
   *  its one fire (backend/engine/rule_archive.archive_rules with
   *  keep_dry_run=true); on means the fired row stays enabled=false but
   *  unarchived, visible on /rules and re-runnable from the toggle. */
  keep_after_run: boolean
  // Window-relative rule-health rollup (engine/rule_health.py) — feeds
  // ruleHealth.ts's inert/stopped-matching badge. Computed fresh on every
  // read, not stored.
  matched_45d: number
  changed_45d: number
  matched_14d: number
  last_matched_at: string | null
  /** PR 1c — edited after its last run started, so ruleHealth.ts does not
   *  badge it 'capped' on that run's count. Absent from an older server. */
  selection_changed_since_last_run?: boolean
  // Automations port (CONTRACTS 7.3 RuleOut; model-api M1). Served by
  // GET /api/rules once model-api is merged; nothing on the legacy pages
  // reads them.
  /** The group the rule is listed under on Automations (migration 180).
   *  Derived from its actions and data source, never from its name
   *  (CONTRACTS 1.2); null only on a row the backfill has not reached. */
  lane: Lane | null
  /** What the rule does, in one plain-English line (migration 180). */
  summary: string | null
  /** Who made the rule: 'automations-ui', 'seed', 'clickpay_suggests', ... */
  created_by: string | null
  /** Runs in the last 45 days, from the rule-health rollup. The
   *  "Never matches" health flag reads it with matched_45d. */
  runs_45d: number
  /** IANA zone the schedule is written in (migration 170). Null = a rule
   *  from before the port: its cron fires in the server's local zone with
   *  APScheduler day numbering, exactly as it always has (CONTRACTS 6.4). */
  schedule_tz: string | null
}

export type RuleDetail = Rule & {
  filter_json: unknown
  action_params: unknown
  /** Per-entity per-day cap (migration 057). Null = use config default.
   *  Backend response includes this field; without it in the type the
   *  RuleEdit page's lookup fails the production build (caught by
   *  `npm run build`, 2026-05-28). */
  max_entity_changes_per_day?: number | null
  /** Set by PUT /api/rules/{id} when the saved rule is enabled and live: every
   *  switch still stopping it from writing to Amazon ([] = it can write now).
   *  Null/absent otherwise. */
  live_write_blockers?: string[] | null
}

export type RuleInput = {
  name: string
  description?: string | null
  data_source: string
  filter_json: unknown
  action_type: string
  action_params: unknown
  schedule_cron: string
  cooldown_tag?: string | null
  cooldown_days?: number
  account_scope?: string
  enabled?: boolean
  dry_run?: boolean
  platform?: string
  /** Time-window shift in days. Null/undefined => backend uses data-source default. */
  lag_days?: number | null
  /** Test Run mode toggle. Sends through unchanged; backend re-arms (clears
   *  test_run_consumed_at) on a false→true transition automatically. */
  test_run?: boolean
  /** Per-entity per-day cap (migration 057). Null/undefined → backend uses
   *  its config default. Optional so callers that don't set it (legacy
   *  paths) still type-check. */
  max_entity_changes_per_day?: number | null
  /** Whole-rule record caps (migrations 007 / 015). Writable since feedback
   *  #48 — the runner's cap_hit message tells the operator to raise
   *  max_records_per_day, and nothing could until the rule editor grew these
   *  boxes. Undefined = don't touch (a PATCH preserves the stored value);
   *  explicit null = reset to the backend default (1000 / 1000 since
   *  migration 153). Must be >= 1: the runner reads 0 as "use the default",
   *  so the API rejects it. */
  max_records_per_fire?: number | null
  max_records_per_day?: number | null
  /** Migration 153 — "let this rule change more than the limit". Undefined =
   *  don't touch. Turns off both row caps and nothing else. */
  bypass_record_caps?: boolean | null
  /** Migration 156 — see Rule.keep_after_run. Undefined = don't touch
   *  (a PATCH preserves the stored value / column default of false). */
  keep_after_run?: boolean | null
}

// ── Dry-run preview (#63) ───────────────────────────────────────────────
// POST /api/rules/dry-run — runs an UNSAVED rule body through the same
// compile → fetch → execute(dry_run=True) path a real fire uses, writing
// nothing to bbpro_rule_runs/bbpro_rule_run_changes. Mirrors
// backend/engine/dry_run.py's DryRunChange/DryRunResult dataclasses
// (backend/api/rules_dry_run.py returns dataclasses.asdict(result)).
export type RuleDryRunInput = Pick<
  RuleInput,
  'data_source' | 'filter_json' | 'action_type' | 'action_params' | 'cooldown_tag' | 'cooldown_days' | 'lag_days'
> & {
  /** 'flat' (default) reads filter_json/action_type/action_params; 'tree'
   *  reads action_tree_json/scope_filter_json instead — same split as the
   *  saved-rule body (Rule.rule_kind). */
  rule_kind?: 'flat' | 'tree'
  action_tree_json?: TreeActionBody | null
  scope_filter_json?: Record<string, unknown> | null
  safety_rail?: boolean
  /** Rows to preview, clamped server-side 1..5000. Default 200. */
  limit?: number
}

export type RuleDryRunChange = {
  entity_type: string
  entity_id: string
  /** 'would_apply' | 'skipped' | 'error' — the executor's dry-run verdict
   *  for this row, same vocabulary SearchTermActionResult['status'] uses
   *  minus 'applied' (a dry-run never applies). */
  status: string
  before: Record<string, unknown>
  after: Record<string, unknown>
  error_message: string | null
  /** Tree rules only — which branch this row matched. Null for flat rules. */
  branch_id: string | null
  action_type: string
  /** The record's name and 7-day numbers (feedback #89); absent from an older
   *  server. acos_7d is a fraction; 99.99 means spend with no sales. */
  record?: PreviewRecord | null
}

export type RuleDryRunResult = {
  matched: number
  previewed: number
  would_apply: number
  skipped: number
  errors: number
  changes: RuleDryRunChange[]
  est_spend_delta: number
  /** Set on a tree preview with no scope_filter_json — mirrors
   *  tree_runner_dispatch.py's unscoped-population warning. */
  scope_warning: string | null
  lag_days_applied: number
  /** true when matched > limit — only the first `limit` rows were previewed. */
  truncated: boolean
  /** Tree rules: branch id -> the records that branch decided, over EVERY
   *  matched record (do-nothing branches and the else included), not just
   *  the previewed slice. {} for a flat rule; absent from an older server. */
  branch_counts?: Record<string, number>
  /** What the record limits count (PR 1c): for a tree, the records a branch
   *  with a real action decided (do-nothing branches and the else are not
   *  counted), over every matched record; for a flat rule, `matched`.
   *  Absent from an older server. */
  acted_on?: number
}

// ── Decision-tree rule body ───────────────────────────────────────────
// Mirrors backend engine/tree_schema.py TreeRule. `branches` is first-match-
// wins; `else` is the fall-through leaf (conventionally a noop). Each branch's
// `when` is an RQB-shape filter group; `then` is the leaf action.
export type TreeBranch = {
  id: string
  when: { combinator: string; rules: unknown[] }
  then: { action_type: string; params?: Record<string, unknown> }
}
export type TreeActionBody = {
  version: number
  entity_type: string
  data_source: string
  branches: TreeBranch[]
  /** Fall-through leaf (JSON key is `else`). */
  else?: TreeBranch
}
export type TreeBodyInput = {
  action_tree_json: TreeActionBody
  scope_filter_json?: Record<string, unknown> | null
  safety_rail?: boolean
  change_reason?: string | null
}
export type TreeFetch = {
  rule_id: number
  rule_kind: string | null
  action_tree_json: TreeActionBody | null
  scope_filter_json: Record<string, unknown> | null
  safety_rail: boolean
}

/** One planned inverse write (or skip) for one original change row — see
 *  backend/engine/undo.py:UndoOp. `from_value`/`to_value` are the ORIGINAL
 *  run's after/before values respectively (undo restores entity_id from
 *  from_value to to_value). */
export type UndoOp = {
  change_id: number
  entity_type: string
  entity_id: string
  inverse_action: 'update_bid' | 'update_state' | 'update_campaign_budget'
    | 'update_placement_modifier' | 'skip'
  profile_id: string | null
  ad_type: string | null
  from_value: Record<string, unknown>
  to_value: Record<string, unknown>
  will_skip: boolean
  skip_reason: string | null
}

/** Live-write gate truth as of the moment the plan was built (Phase 5 Build
 *  08 Part A2) — lets the preview modal say up front "this will be blocked"
 *  without dispatching anything. Rides the plan/execute response itself, no
 *  separate poll. */
export type UndoGateState = {
  live_writes: boolean
  /** Since 2026-09-16 an undo is an operator write: it is gated by
   *  live_writes OR manual_writes. rule_writes / rollout_stage /
   *  emergency_stop are informational only. */
  manual_writes?: boolean
  rule_writes: boolean
  rollout_stage: string | null
  emergency_stop: boolean
  permits_live_dispatch: boolean
  block_reason: string | null
}

export type RevertRunResult = {
  status: 'success' | 'errors' | 'blocked_by_gate' | 'success_no_records' | 'nothing_to_revert'
  original_run_id: number
  revert_run_id?: number
  /** Live writes that actually landed. */
  reverted: number
  /** Preview-only: ops that WOULD have dispatched (dry_run=true). */
  would_apply?: number
  errored?: number
  skipped?: number
  /** A live write was attempted but a gate (live writes / rule writes /
   *  rollout stage / emergency stop) was closed — recorded honestly instead
   *  of being silently counted as reverted. */
  blocked_by_gate?: number
  total_ops?: number
  dry_run?: boolean
  /** The full enumerated plan — always present except on 'nothing_to_revert'. */
  ops?: UndoOp[]
  gate_state?: UndoGateState
}

export type Run = {
  id: number
  rule_id: number
  rule_name?: string | null
  started_at: string
  finished_at: string | null
  status: string
  records_matched: number | null
  records_changed: number | null
  dry_run: boolean | null
  triggered_by: string | null
  error_message: string | null
  // Derived in backend SELECT (Runs page filtering/sorting).
  duration_ms?: number | null
  applied?: boolean | null
}

// Filter envelope for the new /api/runs endpoint. All fields optional.
export type RunsFilter = {
  rule_id?: number
  status?: string                       // 'success' | 'success_no_records' | 'errors,failed' | …
  from?: string                         // ISO datetime — lower bound (inclusive)
  to?: string                           // ISO datetime — upper bound (inclusive)
  dry_run?: 'true' | 'false' | 'all'    // backend tolerates yes/no too
  only_with_changes?: boolean
  sort_by?: 'started_at' | 'rule_name' | 'records_changed' | 'applied' | 'status' | 'duration_ms'
  sort_dir?: 'asc' | 'desc'
  page?: number
  page_size?: number
}

export type RunsPage = {
  rows: Run[]
  total: number
  page: number
  page_size: number
}

export type RuleRunHistoryPoint = {
  date: string                          // 'YYYY-MM-DD'
  fires_success: number
  fires_no_records: number
  fires_error: number
  fires_other: number
  /** Every fire's records_changed, test and live together. Kept for older
   *  callers; the chart reads the split below. */
  records_affected_total: number
  /** Fires that were test (dry) runs — NULL dry_run counts as test. */
  fires_test: number
  /** The 'success' fires among fires_test. */
  fires_success_test: number
  /** records_changed over test fires: records that WOULD change. */
  records_changed_test: number
  /** records_changed over live fires. */
  records_changed_live: number
}

export type Change = {
  id: number
  entity_type: string
  entity_id: string
  before_value: Record<string, unknown> | null
  after_value: Record<string, unknown> | null
  status: string | null
  error_message: string | null
  reason: string | null
  reason_source: string | null
}

/** Per-entity ad spend/sales/ACoS for the days since a run fired vs. an
 *  equal-length window right before it fired. See GET /api/runs/{id}/impact.
 *  acos_* is null when sales are 0 for that window (never a sentinel) — a
 *  present entity with spend_since > 0 and acos_since === null means "spend
 *  happened, no sales yet", not "no data". An entity_id absent from
 *  `entities` genuinely has no rows in its daily table for either window. */
export type RunImpactEntity = {
  spend_since: number
  sales_since: number
  acos_since: number | null
  spend_prior: number
  sales_prior: number
  acos_prior: number | null
}

export type RunImpact = {
  run_id: number
  finished_at: string
  days_since: number
  entities: Record<string, RunImpactEntity>
}

/** Follow-up action attached to a rule. See migration 046.
 *
 *  ``params_json`` and ``followup_filter_json`` are typed as ``unknown``
 *  because the backend round-trips them through JSON — they may come back
 *  as a parsed object, a raw JSON string (if parsing failed server-side),
 *  or null. Consumers narrow at use time. */
export type Followup = {
  id: number
  rule_id: number
  followup_action: string
  params_json: unknown
  trigger_mode: 'relative_delay' | 'cron' | 'conditional'
  trigger_value: string | null
  followup_filter_json: unknown
  enabled: boolean
  order_idx: number
  created_at: string
  updated_at: string
}

export type FollowupInput = {
  followup_action: string
  params_json?: unknown
  trigger_mode: 'relative_delay' | 'cron' | 'conditional'
  trigger_value?: string | null
  followup_filter_json?: unknown
  enabled?: boolean
  order_idx?: number
}

// ── ETL freshness observatory (migration 047) ────────────────────────
//
// FreshnessJob is the join of bbpro_etl_jobs and bbpro_etl_freshness for
// a single feed. status is the colour the dashboard renders.
export type FreshnessStatus = 'green' | 'yellow' | 'red' | 'unknown' | 'error'
export type FreshnessCategory = 'bq_source' | 'local_sync' | 'adbetter_internal'

export type FreshnessJob = {
  job_id: string
  category: FreshnessCategory
  display_name: string
  description: string | null
  runs_where: string | null
  check_kind: string
  check_target: string | null
  yellow_after_h: number
  red_after_h: number
  enabled: boolean
  sort_order: number
  checked_at: string | null
  last_success_at: string | null
  last_data_date: string | null
  row_count: number | null
  age_hours: number | null
  status: FreshnessStatus
  error_message: string | null
  detail: Record<string, unknown> | null
  // migration 117 — grading on the DATA date, not just the write date.
  // data_status/status_reason are null for jobs that haven't opted into
  // grade_on_data; status above is already worse-of(job, data) either way.
  data_age_hours: number | null
  data_status: FreshnessStatus | null
  status_reason: 'job' | 'data' | 'both' | null
  // migration 147 — feed registry lifecycle (C). 'active' is the only
  // state the badge/alert machinery counts; every other value means this
  // row is muted (a producer-side fact, not fixable from ClickPay) but
  // stays fully visible here. Optional: an old backend omits these five
  // fields entirely, which must degrade to 'active'/no-lifecycle-info,
  // never a false green.
  lifecycle?: 'active' | 'blocked' | 'planned' | 'retired'
  lifecycle_reason?: string | null
  lifecycle_review_by?: string | null
  cadence_h?: number | null
  eff_yellow_after_h?: number | null
  eff_red_after_h?: number | null
}

export type FreshnessOverall = {
  status: FreshnessStatus
  counts: Record<FreshnessStatus, number>
  // migration 147. Present on every backend that has it; an old backend
  // omits both — treat as {blocked:0,planned:0,retired:0} / 0, never as a
  // reason to change how `status`/`counts` are read.
  muted?: { blocked: number; planned: number; retired: number }
  review_due?: number
}

export type FreshnessOverviewResponse = {
  overall: FreshnessOverall
  jobs: FreshnessJob[]
}

export type FreshnessAlert = {
  id: number
  job_id: string
  display_name: string | null
  severity: 'yellow' | 'red'
  opened_at: string | null
  closed_at: string | null
  age_hours_at_open: number | null
  last_success_at_at_open: string | null
  message: string | null
  acknowledged_at: string | null
  acknowledged_by: string | null
}

// ── Saved table filter presets (migration 050) ─────────────────────────
//
// Backs the multi-column filter dropdown on every list page and the
// "Import filter from table" picker on the rule editor.
export type FilterOp =
  | 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte'
  | 'contains' | 'starts' | 'ends'
  | 'in' | 'not_in'
  | 'between' | 'is_null' | 'not_null'

export type FilterCondition = {
  column: string
  op: FilterOp
  value?: string | number | boolean | Array<string | number> | null
  // For 'between': [low, high].
  // ── F1 — execution-lane tagging ──────────────────────────────────────────
  // Which side evaluates the condition. Default (undefined) = 'client': the
  // condition is applied by `applyFilter` over already-fetched rows. 'server'
  // marks a "scope" condition that the page MUST push down to its list
  // endpoint at fetch time (and that `applyFilter` therefore SKIPS, so it isn't
  // double-applied against a lossy representative value).
  //
  // The Search-Terms Target chip is the canonical server case: list_search_terms
  // aggregates by query, so the row's `keyword_id` is an `any_value()`
  // representative — a client eq on it matches the wrong rows. The drill must
  // hit the RAW `keyword_id` inside the CTE server-side, so the chip carries
  // `exec:'server'`, `serverField:'keyword_id'`, and `_id` (the raw key the
  // endpoint filters on, distinct from the human `value`/label shown in the chip).
  exec?: 'client' | 'server'
  /** For exec:'server' — the raw backend column the scope pushes down to. */
  serverField?: string
  /** For exec:'server' — the opaque key passed to the endpoint (the raw id),
   *  kept distinct from `value` which is the human label shown in the chip. */
  _id?: string
}

export type FilterSpec = {
  conditions: FilterCondition[]
}

export type TableFilter = {
  id: number
  name: string
  table_key: string
  data_source: string | null
  filter_json: FilterSpec
  created_at: string
  updated_at: string
}

export type TableFilterInput = {
  name: string
  table_key: string
  data_source?: string | null
  filter_json: FilterSpec
}

export type FreshnessSummary = {
  checked: number
  green: number
  yellow: number
  red: number
  unknown: number
  error: number
  opened_alerts: number
  closed_alerts: number
}

// Aakaar Goal 1
export type Profile = {
  id: number
  name: string
  account_type: string
  region: string
  marketplace_id: string | null
  currency: string
  timezone: string
  ads_profile_id: string | null
  /** Amazon Ads CONSOLE entity id (migration 146). Null until the
   *  ads_entities sync has run — callers must hide any console link
   *  rather than emit a URL Amazon will not resolve. */
  ads_entity_id: string | null
  target_acos: number | null
  monthly_ad_budget: number | null
  status: string
  is_default: boolean
}

// Aakaar Goal 4
export type AgentGroup = {
  agent_group: string
  rule_count: number
  template_count: number
  binding_count: number
}

export type CampaignRow = {
  campaign_id: string
  campaign_name: string | null
  campaign_type: string | null
  portfolio_id: string | null
  portfolio_name: string | null
  state: string | null
  daily_budget: number | null
  asin: string | null
  impressions: number | null
  clicks: number | null
  spend: number | null
  sales: number | null
  orders: number | null
  acos: number | null
  cvr: number | null
  ctr: number | null
  cpc: number | null
  roas: number | null
  /** The rule binding's target when a rule sets one, else the ASIN's goal target (a fraction). */
  target_acos: number | null
  /** Where `target_acos` came from; null when neither exists. */
  target_acos_source?: 'rule' | 'goal' | null
  /** Spend ÷ (daily budget × days in the window), a fraction; null without a budget. */
  budget_use?: number | null
  /** Daily budget × days in the window (money); the denominator of `budget_use`. */
  budget_capacity?: number | null
  agent_count: number
  marketplace: string | null
  // Build 30 — stock cover + Buy Box for the campaign's PRIMARY ASIN
  // (campaign_meta carries one representative ASIN per campaign).
  days_of_supply: number | null
  current_units: number | null
  /** Fraction 0..1 over the last 30 days of offer-change events. */
  buy_box_win_rate_30d: number | null
  /** How many events that rate is computed from — under ~5 it means little. */
  bb_events_30d: number | null
}

/** v2 #6 — per-campaign placement spend shares (fractions 0..1). Off-Amazon
 *  (SD) share is carried too but the Campaigns table only renders TOS/PP/RoS. */
export type CampaignPlacementShareRow = {
  campaign_id: string
  tos_pct: number | null
  pp_pct: number | null
  ros_pct: number | null
  off_pct: number | null
}

/** SM W1.3 — GET /api/bulk/campaign-placements: a campaign's CURRENT
 *  placement adjustments, as stored decimal multipliers (0.5 = +50%; 0 is a
 *  real +0%). `has_placements` is false for SB / SD campaigns. */
export type CampaignPlacementModifiers = {
  campaign_id: string
  campaign_name: string | null
  ad_type: string | null
  state: string | null
  has_placements: boolean
  tos: number | null
  pp: number | null
  ros: number | null
}

export type Binding = {
  id: number
  rule_id: number
  rule_name: string | null
  agent_group: string | null
  binding_class: string | null
  campaign_id: string
  target_acos: number | null
  params_override: unknown
  last_tuned_at: string | null
  last_tuner_note: string | null
  enabled: boolean
  dry_run: boolean
  live_writes_approved_at: string | null
  activated_at: string | null
}

export type Tag = {
  entity_type: string
  entity_id: string
  tag: string
  applied_at: string
  expires_at: string | null
  applied_by_rule: number | null
}

export type TagCatalogRuleRef = {
  id: number
  name: string
  via?: 'cooldown' | 'action'
}

export type TagCatalogEntry = {
  tag: string
  /** Entity types this tag applies to (union of writer rules' data sources + active applications). */
  entity_types: string[]
  /** Rules that write the tag (cooldown_tag or tag_on_apply). */
  writers: TagCatalogRuleRef[]
  /** Rules that clear the tag (remove_tag_on_apply). */
  clearers: TagCatalogRuleRef[]
  /** Rules that read the tag in their filter (has_tag / not_has_tag). */
  readers: TagCatalogRuleRef[]
  /** Total active applications across all entity types. */
  active_count: number
  /** Active applications broken out per entity_type. */
  active_by_entity_type: Record<string, number>
}

export type ParamSpec = {
  name: string
  type: 'number' | 'string' | 'select' | 'boolean'
  required?: boolean
  default?: unknown
  options?: string[]
  help?: string
}

export type ActionType = {
  type: string
  description: string
  data_sources: string[]
  params_schema: ParamSpec[]
}

/** Per-field metadata as returned by /api/data-sources.
 *
 *  The rule builder no longer reads this endpoint — it moved to /api/fields,
 *  which is session-cached and carries a human `label` per field. The Reports
 *  page is the remaining consumer and treats the list as names only, via its
 *  own local `fieldNameOf`. */
export type DataSourceFieldMeta = {
  name: string
  type?: string                       // 'numeric' | 'string' | 'boolean'
  metric?: string                     // 'acos' (when windowed)
  window?: string                     // '30d' (when windowed)
  group?: 'metric' | 'attribute'
  /** 'percent' | 'usd' | 'integer' | 'number' | 'text' — drives the wizard's
   *  numeric editor: percent fields display value*100 with a % suffix and
   *  store value/100 (filter_json keeps fractions, e.g. 0.15 = 15%). */
  format?: string
}

// v5.1 — value picker catalog response
export type FieldValue = {
  value: string
  /** Human-readable label (e.g. campaign_name) for ID fields. Null on
   *  categoricals where value IS the label. */
  label: string | null
  /** For the `tag` field: how many rows currently carry this tag.
   *  0 = registered but never applied → picker shows a "not applied yet"
   *  warning. Null for non-tag fields. */
  count: number | null
}

export type FieldValuesResponse = {
  values: FieldValue[]
  /** True when value!=label semantics — picker shows label prominently,
   *  ID as small subtext. */
  is_id_field: boolean
  /** Column name that produced the label (audit / debugging aid). */
  label_field: string | null
  /** True when DB had more matching values than `limit` requested. */
  truncated: boolean
}

export type TagRegistry = {
  name: string
  description?: string | null
  color?: string | null
  /** v5.2 — 'tag' (operator-meaningful, surfaced in has_tag picker)
   *  vs 'cooldown' (system marker rule_NN/BB-PPC-NN, hidden from picker). */
  kind?: 'tag' | 'cooldown' | null
  created_at?: string | null
  created_by?: string | null
}

export type DataSource = {
  name: string
  view: string
  entity_type: string
  /** Field list. The backend returns DataSourceFieldMeta objects post-PR #28;
   *  pre-PR-28 deployments and tests may still return flat strings, so a
   *  consumer that only wants names must handle both — see Reports.tsx's
   *  local `fieldNameOf`. (lib/fieldName.ts no longer exports a normalizer:
   *  it indexes the server's classification rather than deriving one.) */
  fields: (string | DataSourceFieldMeta)[]
  /** Lag-group default (days) applied to time windows for this source.
   *  2 for PPC sources (Amazon report lag), 1 for orders, 0 for real-time
   *  signals (hourly notifications). The per-rule `lag_days` field is an
   *  additive buffer on top of this. See migration 048. */
  default_lag_days?: number
  /** Lag-group name this source belongs to: 'ppc' | 'orders' | 'inventory'
   *  | 'hourly'. Drives which `effective_date_<group>()` macro the view uses. */
  lag_group?: 'ppc' | 'orders' | 'inventory' | 'hourly'
  /** v5 — Entity-type grouping for the rule-target dropdown. Falls back to
   *  "Other" when the backend doesn't categorise the source. */
  category?: string
  /** v5 — Human-readable label (e.g. "Products (ASIN)"). Falls back to
   *  the technical name when absent. */
  display_name?: string
}

// ── Field-driven catalog (/api/fields, migration 051) ───────────────────
//
// Flat-shape replacement for DataSource[]. The editor's new entity-tab UI
// consumes this directly — every field carries its lag_group + entity_types
// so the picker filters itself without a separate data_source dropdown.

export type LagGroup = 'ppc' | 'orders' | 'inventory' | 'hourly'

export type FieldMeta = {
  name: string
  lag_group: LagGroup
  type: 'numeric' | 'text' | 'boolean'
  label: string
  metric: string | null         // 'acos' for windowed; null for attributes
  window: string | null         // '30d' for windowed; null otherwise
  views: string[]               // data_source names exposing this field
  entity_types: string[]        // grains where this field is selectable
  operators: string[]
  format: string                // 'percent' | 'money' | 'int' | 'text' | ...
  join_role: 'primary' | 'sidecar'
  is_pseudo: boolean            // True for the `tag` EXISTS-rewritten field
}

export type FieldsResponse = {
  fields: FieldMeta[]
  lag_groups: Record<LagGroup, number>
  entity_types: string[]
  windows: string[]
  operators: string[]
}

// ── PPC A3 — server-side deep-field tab filter (POST /api/entity-filter) ──
//
// The tab-facing twin of /api/filter/preview. A PPC tab posts its grain
// (entity_type) + a react-querybuilder filter tree (converted from the tab's
// FilterSpec via lib/filterConvert.specToRuleGroup); the backend compiles it
// through the SAME field-driven engine the rules use and returns the matching
// rows from the grain's `_plus` view. Used to evaluate "deep" registry fields
// that aren't present in the tab's client-loaded rows.
export type EntityFilterResponse = {
  rows: Array<Record<string, unknown>>
  match_count: number
  returned: number
  entity_type: string
  inferred_view: string
  sql_where: string
  lag_days_applied: number | null
}

export type RuleSourceWarning = {
  rule_id: number
  rule_name: string | null
  declared: string
  inferred: string
  inferred_group: LagGroup
  field_count: number
  detected_at: string
}

export type DashboardStats = {
  runs_today_by_status: Record<string, number>
  runs_24h: number
  records_changed_24h: number
  rules_enabled: number
  rules_live: number
  rules_dry_run: number
}

// ── Dashboard KPIs + charts (rebuilt dashboard) ─────────────────────────

export type KpiPctChange = {
  current: number
  prior: number
  change_pct: number | null
}

export type KpiPpChange = {
  current: number | null
  prior: number | null
  change_pp: number | null
}

export type DashboardKpis = {
  spend:  KpiPctChange
  acos:   KpiPpChange
  orders: KpiPctChange
  tacos:  KpiPpChange
  // F1: Sales/Net-profit 7d, on the SAME complete-UTC-days window as
  // spend/acos/orders/tacos above (backend/main.py dashboard_kpis) — not a
  // separate client-computed window (see `window` below).
  sales:      KpiPctChange
  net_profit: KpiPctChange
  // The window every '7d' tile on this screen actually covers, defined once
  // server-side — never re-derived from the browser clock.
  window: { from: string; to: string; basis: 'complete_utc_days' }
  window_days: number
  empty: boolean
}

export type SpendPoint = {
  date: string
  spend: number
  sales: number
  /** OPTIONAL stock cross-overlay (charts stock <-> PPC), default-off on the
   *  dashboard chart. units_available = total sellable FBA across ASINs that
   *  day; days_of_supply = units_available / trailing-30d avg daily units sold.
   *  Null when the ops inventory/orders tables are absent. */
  units_available?: number | null
  days_of_supply?: number | null
}
export type AcosPoint  = { date: string; acos: number | null }
export type DailySpend = { date: string; spend: number }

export type TopCampaign = {
  campaign_id: string
  name: string
  spend: number
  sales: number
  orders: number
  acos: number | null
  daily_spend: DailySpend[]
}

export type RecentFire = {
  run_id: number
  rule_id: number
  rule_name: string
  fired_at: string | null
  status: string
  records_matched: number
  records_affected: number
  applied: boolean
}

export type SchedulerJob = {
  id: string
  name: string
  next_run: string | null
  trigger: string
}

export type FilterPreview = {
  match_count: number
  sample_size: number
  sample: Record<string, unknown>[]
  sql_where: string
  /** Lag days the backend applied to the time windows in this preview. */
  lag_days_applied?: number
  /** View the field-driven compiler actually queried. May differ from
   *  the declared data_source when the field set spans / routes to a
   *  different view. Null when v2 compile fell back to legacy single-source
   *  (e.g. an unregistered field). */
  inferred_view?: string | null
  /** Distinct primary lag groups the field set touches. ['ppc'] for a
   *  typical PPC rule; ['inventory'] when only sidecars; multi-element
   *  values can't get here (cross-source guard would 400 first). */
  lag_groups?: string[] | null
  /** A2 — per-condition cumulative match counts, one step per top-level
   *  element of the root AND, in authored order. An element may be a leaf
   *  or itself a nested group — see `kind` on ConditionFunnelStep. Null
   *  when the top level isn't a plain AND (root OR, root NOT, or >12
   *  top-level elements) — those shapes don't have a single "step-by-step
   *  narrowing" story. */
  condition_funnel?: ConditionFunnelStep[] | null
  /** A3 — true stat-tile numbers computed over the FULL match set (not the
   *  capped `sample` above). Keys are present only when their source
   *  columns exist on the queried view. */
  aggregates?: FilterPreviewAggregates | null
}

export type ConditionFunnelStep = {
  field: string
  operator: string
  value: unknown
  /** This leaf compiled alone (no earlier conditions applied). */
  individual_count: number | null
  /** This leaf AND every earlier leaf in authored order. */
  cumulative_count: number | null
  /** Present only for acos_* leaves whose cumulative match set includes
   *  zero-sales (acos=99.99, "infinite ACoS") sentinel rows. */
  sentinel_note?: string
  /** 'leaf' (field/operator/value populated) or 'group' (a nested AND/OR
   *  under the root — field/operator/value are null, use `label` instead).
   *  Undefined on stale/back-compat data shapes — treat as 'leaf'. */
  kind?: 'leaf' | 'group'
  /** Group steps only — a server-generated caption, e.g.
   *  "(3 conditions, all of)". */
  label?: string
  /** Group steps only — the nested group's own combinator ('and'/'or'). */
  combinator?: string
  /** Group steps only — how many rules the nested group contains. */
  child_count?: number
}

export type FilterPreviewAggregates = {
  spend_30d_total?: number
  sales_30d_total?: number
  /** SUM(spend)/SUM(sales) over the full match set, excluding sentinel
   *  (infinite-ACoS) rows from both sides of the ratio. Null when there's
   *  no non-sentinel sales to divide by. */
  weighted_acos_30d?: number | null
  infinite_acos_count?: number
  by_expression_type?: Record<string, number>
  by_match_type?: Record<string, number>
  by_state?: Record<string, number>
}

/** One solved campaign from POST /api/campaign-tuner/preview. Deltas are
 *  whole-number percents (matching the deltaCell renderer); current modifiers
 *  are null until live Ads-API reads land (deltas measure vs an assumed 0%). */
export type CampaignTunerRow = {
  campaign_id: string
  campaign_name: string
  campaign_type: string | null
  state: string | null
  daily_budget: number | null
  spend_30d: number | null
  sales_30d: number | null
  acos_30d: number | null
  acos_is_sentinel: boolean
  goal_preset: string | null
  current_base_bid: number | null
  current_tos_modifier: number | null
  current_ros_modifier: number | null
  current_pp_modifier: number | null
  tos_acos_30d: number | null
  ros_acos_30d: number | null
  pp_acos_30d: number | null
  d_base: number
  d_tos: number
  d_ros: number
  d_pp: number
  new_base_bid: number | null
  new_tos_modifier: number | null
  new_ros_modifier: number | null
  new_pp_modifier: number | null
}

export type CampaignTunerPreview = {
  /** COUNT(*) of campaigns matching the filter (pre-LIMIT). */
  match_count: number
  /** Of the returned sample, how many would actually be tuned (enabled +
   *  goal-enabled) after the safety rails dropped paused / opted-out rows. */
  tunable_count: number
  excluded_paused: number
  excluded_goal_disabled: number
  sample_size: number
  target_acos: number
  rows: CampaignTunerRow[]
  sql_where: string
  lag_days_applied?: number
}

/** One dayparting schedule row as returned by POST /api/rules/{id}/dayparting
 *  (backend DaypartingScheduleOut). day_of_week is 0..6 (Mon=0..Sun=6) or null
 *  for "every day"; bid_multiplier is a float > 0 (1.0 = no-op). */
export type DaypartingScheduleRow = {
  id: number
  rule_id: number
  hour_of_day: number
  day_of_week: number | null
  bid_multiplier: number
  created_at: string
}

/** One bucket of the dayparting profile (GET /api/hourly/dayparting-profile).
 *  hour_of_day_utc is 0..23 UTC; weekday is Python weekday (0=Mon..6=Sun) when
 *  by_weekday was requested, else null. Ratios + shares are server-computed. */
export type DaypartingProfileRow = {
  hour_of_day_utc: number
  weekday: number | null
  days_observed: number | null
  impressions: number | null
  clicks: number | null
  cost: number | null
  sales_7d: number | null
  orders_7d: number | null
  acos_7d: number | null
  roas_7d: number | null
  share_of_spend: number | null
  share_of_sales: number | null
}

export type DaypartingProfile = {
  buckets: DaypartingProfileRow[]
  totals: { cost: number; sales_7d: number; acos_7d: number | null }
  hours_back: number
  /** PR 12b: set when the read took the header's from/to (Pacific days), clipped to the
   *  stream's newest 7 days; `window_clamped` says the clip happened. */
  window?: { from: string; to: string }
  window_clamped?: boolean
  /** True when nothing in the asked range is inside the stream's 7 days. */
  window_empty?: boolean
  by_weekday: boolean
  source: string
}

/** One recommended bucket (GET /api/hourly/dayparting-recommendation). multiplier
 *  is clamped to [0.5, 1.5]; 1.0 = hold / insufficient data. UTC hour, py-weekday. */
export type DaypartingRecommendationRow = {
  hour_of_day_utc: number
  weekday: number | null
  clicks: number | null
  cost: number | null
  sales_7d: number | null
  acos_7d: number | null
  multiplier: number
  reason: string
  /** SM W5.1 weekday-aware mode only: what the multiplier rests on — the
   *  cell's own weekday clicks, the hour pooled across all days, or nothing. */
  basis?: 'weekday' | 'hour' | 'none'
}

export type DaypartingRecommendation = {
  recommendations: DaypartingRecommendationRow[]
  blended_acos: number | null
  target_acos: number
  /** Where `target_acos` came from: the request, the account goal, or the built-in default. */
  target_acos_source?: 'request' | 'account_goal' | 'default'
  sensitivity: number
  min_clicks: number
  hours_back: number
  window?: { from: string; to: string }
  window_clamped?: boolean
  window_empty?: boolean
  by_weekday: boolean
  weekday_aware?: boolean
  source: string
}

export async function req<T>(path: string, init?: RequestInit): Promise<T> {
  // Merge caller headers ON TOP of the JSON default so opt-in headers
  // (e.g. X-Requested-With for /api/admin/*) don't accidentally clobber
  // Content-Type. Naive {...init} spread would replace the entire
  // headers map.
  //
  // F4.6 — stamp the Operator scope (X-Operator-Scope) on every request so the
  // whole app adjusts to the selected book of products. operatorScopeHeaders()
  // returns {} for the default 'all' scope, so an unscoped request is byte-
  // identical to before — the backend treats an absent header as whole-catalog.
  // Placed before caller headers so an explicit per-call override still wins.
  //
  // Build 47 (Phase 13): the ads half's single fetch choke point — the twin of
  // opsFetch in src/ops/lib/api.ts. Off unless the page was opened with
  // ?perf=1; perfRecord() returns immediately otherwise and never sends
  // anything anywhere.
  //
  // UI overhaul PR-0: an AbortSignal reaches fetch either explicitly
  // (`init.signal`) or ambiently (`withRequestSignal`, see lib/query/
  // requestSignal.ts) — the data layer cancels superseded requests without
  // every wrapper growing a parameter. Successful responses also feed the
  // server-clock estimate (lib/clock.ts) so relative times are measured
  // against the server's now, not this laptop's.
  const _perfT0 = performance.now()
  const res = await fetch(path, {
    ...init,
    signal: init?.signal ?? currentRequestSignal(),
    headers: {
      'Content-Type': 'application/json',
      ...operatorScopeHeaders(),
      ...(init?.headers ?? {}),
    },
  })
  perfRecord(path, performance.now() - _perfT0, res.status,
             Number(res.headers.get('content-length')) || null)
  if (!res.ok) {
    if (res.status === 401) emitSessionExpired()
    const text = await res.text()
    // Same message text as before (`"<status> <statusText>: <body>"`) so every
    // `String(e)` banner keeps reading the same; callers that want to branch
    // now have status / code / requestId on the error.
    throw new ApiError(path, res.status, res.statusText, text)
  }
  noteServerDate(res)
  // 204 No Content (e.g. DELETE /api/product-events/notes/{id}) has no body
  // to parse; res.json() would reject. Same rule as opsFetch.
  if (res.status === 204) return undefined as T
  return res.json()
}

/** GET through `req` for feature clients that live in their own file (e.g.
 *  lib/adsOverviewApi.ts), so new endpoints need not grow this one. */
export function apiGet<T>(path: string): Promise<T> {
  return req<T>(path)
}

/** Write twin of `apiGet` for feature clients in their own file (e.g.
 *  lib/userTagsApi.ts): JSON body, same `req` choke point. */
export function apiSend<T>(path: string, method: 'POST' | 'PATCH' | 'DELETE', body?: unknown): Promise<T> {
  return req<T>(path, { method, body: body === undefined ? undefined : JSON.stringify(body) })
}

/** The shared fetch choke point, for feature clients kept in their own file
 *  (e.g. lib/chatApi.ts, lib/harvestApi.ts) so this file does not grow per
 *  feature. */
export { req as apiRequest }

/** Multipart POST (a file upload) for feature clients in their own file (e.g.
 *  lib/imageStudioApi.ts's video upload). Same choke point duties as `req` —
 *  operator scope, ApiError, the session-expired signal, the server clock —
 *  but NO Content-Type header: the browser must write the multipart boundary
 *  itself, and `req` forces JSON. */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const _perfT0 = performance.now()
  const res = await fetch(path, {
    method: 'POST',
    body: form,
    credentials: 'same-origin',
    signal: currentRequestSignal(),
    headers: { ...operatorScopeHeaders() },
  })
  perfRecord(path, performance.now() - _perfT0, res.status,
             Number(res.headers.get('content-length')) || null)
  if (!res.ok) {
    if (res.status === 401) emitSessionExpired()
    throw new ApiError(path, res.status, res.statusText, await res.text())
  }
  noteServerDate(res)
  return res.json()
}

// ── Safety layer (migration 007) ──────────────────────────────────────────

export type PendingChange = {
  id: number
  rule_run_id: number
  rule_id: number | null
  rule_name: string | null
  entity_type: string
  entity_id: string
  before_value: Record<string, unknown> | null
  after_value: Record<string, unknown> | null
  created_at: string
  // 'preview_only' (engine E-H, D9): an approved change of an action ClickPay
  // can only preview. Closed for good: nothing was sent, and it is never retried.
  status: 'pending' | 'approved' | 'rejected' | 'applied' | 'expired' | 'preview_only'
  decided_by: string | null
  decided_at: string | null
  expires_at: string | null
}

export type CircuitState = {
  id: number
  tripped: boolean
  tripped_at: string | null
  tripped_reason: string | null
  cleared_at: string | null
  cleared_by: string | null
}

export type BulkApproveResult = {
  results: { id: number; status: string; error_message: string | null }[]
  total: number
  applied: number
  rejected: number
  errored: number
}

/**
 * Serialise a RunsFilter into a query string. Omits undefined / empty values
 * and 'all' (treated as "no filter") so the URL stays clean. Exported for tests
 * that want to assert URL shape, but the api methods use it internally.
 */
export function runsFilterQuery(filter: RunsFilter): string {
  const q = new URLSearchParams()
  if (filter.rule_id !== undefined) q.set('rule_id', String(filter.rule_id))
  if (filter.status) q.set('status', filter.status)
  if (filter.from) q.set('from', filter.from)
  if (filter.to) q.set('to', filter.to)
  if (filter.dry_run && filter.dry_run !== 'all') q.set('dry_run', filter.dry_run)
  if (filter.only_with_changes) q.set('only_with_changes', 'true')
  if (filter.sort_by) q.set('sort_by', filter.sort_by)
  if (filter.sort_dir) q.set('sort_dir', filter.sort_dir)
  if (filter.page !== undefined) q.set('page', String(filter.page))
  if (filter.page_size !== undefined) q.set('page_size', String(filter.page_size))
  return q.toString()
}

// ── AMC audiences (dark-launched behind CLICKPAY_AMC) ──────────────────────
// Read-only surface in Phase 1: status drives tab visibility; audiences/recipes
// power the (browse-only) Audiences page. Creation/refresh land in Phase 2.
/** Honest dark-launch state for the activation banner. Both flags off (the prod
 *  default) means nothing can move a real bid — the wizard renders 'Dry-run
 *  only' and labels its CTA 'Stage activation (dry-run)'. */
export type AmcActivationStatus = {
  activation_enabled: boolean
  live_writes_enabled: boolean
}

export type AmcStatus = {
  enabled: boolean
  configured: boolean
  instance_provisioned: boolean
  instance_id?: string | null
  last_refresh?: string | null
  reason?: string | null
  amc_activation_status?: AmcActivationStatus | null
}

/** Which managed surface an audience activates on. sp_sb = a bid adjustment on
 *  Sponsored Products/Brands (never suppression); sd = target/exclude on
 *  Sponsored Display. */
export type AmcActivationSurface = 'sp_sb' | 'sd'

export type AmcActivationPreview = {
  matched_campaigns: number
  est_daily_delta: number
  campaigns: { campaign_id: string; name: string | null; ad_type: string | null; spend_30d: number | null }[]
}

export type AmcActivation = {
  rule_id: number
  name: string
  surface: string
  modifier_pct: number | null
  mode: string | null
  enabled: boolean
  dry_run: boolean
  last_run_status: string | null
}

/** Body for previewActivation / activateAudience. */
export type AmcActivateBody = {
  surface: AmcActivationSurface
  campaign_ids: string[]
  modifier_pct?: number       // sp_sb
  mode?: 'target' | 'exclude' // sd
  bid?: number                // sd
  ad_types?: string[]         // sp_sb: subset of ['sp','sb']
  name?: string
  dry_run?: boolean
}

export type AmcAudience = {
  id: number
  name: string
  recipe_key: string | null
  status: string
  audience_type: string
  size: number | null
  used_by_rule_count: number
  last_refreshed_at: string | null
  created_at: string | null
  // A5 trend fields — cheap computed badge inputs (null = no badge).
  pct_change_7d: number | null
  prev_size: number | null
}

export type AmcSizePoint = { day: string; size: number }

// A5: batch size history (one series per audience), labeled by ASIN scope.
export type AmcAudienceSizeSeries = {
  audience_id: number
  asin: string | null
  points: AmcSizePoint[]
}

// A5: honest reach KPI. combined = naive sum (overlaps counted); deduped is the
// true cross-audience unique reach, available only when the owner enables it.
export type AmcReach = {
  combined: number
  deduped: number | null
  deduped_status: string
  reason?: string | null
}

// A3: a read-only "where to use this audience" suggestion.
export type CampaignRec = {
  campaign_id: string
  campaign_name: string
  ad_type: string
  asin: string | null
  spend_30d: number | null
  acos_30d: number | null
  clicks_30d: number | null
  suggested_action: string
  suggested_modifier_pct: number | null
  surface: string
  why: string
}
export type AmcAudienceJob = {
  id: number
  kind: string
  status: string
  execution_id: string | null
  row_count: number | null
  submitted_at: string | null
  finished_at: string | null
  error: string | null
}
export type AmcAudienceDetail = AmcAudience & {
  sql_text: string | null
  size_history: AmcSizePoint[]
  recent_jobs: AmcAudienceJob[]
}

export type AmcRecipeParam = {
  name: string
  label: string
  type: string
  default: unknown
  help: string
  options?: string[] | null
  min?: number | null
  max?: number | null
  suffix?: string | null
}

export type AmcRecipe = {
  key: string
  title: string
  blurb: string
  icon: string
  audience_type: string
  category: string
  activation_surface: string
  activation_note: string
  params: AmcRecipeParam[]
}

export type AmcInsightTemplate = {
  key: string
  title: string
  blurb: string
  icon: string
  category: string
  params: AmcRecipeParam[]
}

export type AmcInsight = {
  id: number
  template_key: string | null
  status: string
  row_count: number | null
  submitted_at: string | null
  finished_at: string | null
  error: string | null
}

export type AmcInsightResult = AmcInsight & {
  columns: string[]
  rows: Record<string, unknown>[]
  results_truncated: boolean
}

export type CreateAmcAudienceResult = { id: number; status: string; job_id: number | null }

// ── ClickPay Suggests (P4) ─────────────────────────────────────────────────
// Read-only recommendations over the *_performance_plus views. Each card STAGES
// (never auto-applies) into the existing approval queue via a dark @once rule.
export type RecommendationKind =
  | 'bid_down' | 'bid_up' | 'budget_up' | 'budget_down'
  | 'negate' | 'harvest' | 'placement' | 'dayparting' | 'audience'

/** Persisted dismiss/snooze/done state for one rec_key (migration 112 /
 *  bbpro_rec_state). `null` = active / not hidden. */
export type RecState = {
  state: 'dismissed' | 'snoozed' | 'done'
  snooze_until: string | null
  actor_slug: string
  created_at: string
} | null

export type Recommendation = {
  rec_key: string
  kind: RecommendationKind
  entity_type: string
  entity_id: string
  scope: Record<string, unknown>
  title: string
  plain_english_reason: string
  current_value: unknown
  proposed_value: unknown
  /** Modelled daily spend change. Null = NOT MODELLED for this action type
   *  (harvest, dayparting, placement modifier), which is a different fact
   *  from 0.00. Rendering the first as the second told the operator the
   *  change was free. */
  est_daily_spend_delta: number | null
  confidence: 'high' | 'medium' | 'low'
  evidence: Record<string, unknown>
  suggested_action_type: string | null
  suggested_filter_json: Record<string, unknown> | null
  suggested_action_params: Record<string, unknown> | null
  data_source: string | null
  source_freshness: string | null
  link_to: string | null
  activation_note: string | null
  already_staged?: boolean
  /** #33: the latest change ClickPay itself APPLIED to this entity AND this
   *  attribute, from bbpro_manual_edits_audit. NO time bound — round 1 read it
   *  through the same window that hides settled recs, which made the column
   *  that renders it an em-dash on 100% of visible rows. null when ClickPay
   *  has not touched this attribute — which does NOT mean nobody has; a change
   *  made directly in Seller Central leaves no row here (that is what the
   *  Change History page's Amazon tab is for).
   *
   *  `applied_age_seconds` is computed server-side (both sides of the
   *  subtraction on the DB's own clock). Prefer it over `applied_at`:
   *  applied_at is a naive local-time timestamp, and formatRelative() reads a
   *  TZ-less timestamp as UTC, which would age it by the server's offset. */
  last_applied?: RecentApplied | null
  /** #35: why this rec counts as already handled, or null when it is still
   *  actionable. 'staged' = a change to this same attribute is already
   *  waiting (the Suggests Queued tab or the Pending edits tray since PR-B,
   *  or Rules > Approvals for an automation proposal); 'applied' = ClickPay
   *  applied this same attribute inside `recs.recent_change_days`. The server
   *  drops these from the default response and counts them in
   *  `meta.settled_count`. #69/#99: 'applied' also covers a change the
   *  definitions sync saw (e.g. a Seller Central edit), and 'value_moved' = the
   *  live value is no longer the `current_value` this rec was computed from. */
  settled_reason?: 'staged' | 'applied' | 'value_moved' | null
  /** The (entity_type, entity_id) this rec's history is filed under in
   *  /api/audit — NOT always the rec's own pair (a placement rec is
   *  entity_type 'campaign' but audits under 'placement'). Use it to build the
   *  /history deep link; null means no link can be built that would find
   *  anything (placements — two id spellings coexist in the trail), in which
   *  case render no link rather than one that always shows an empty page. */
  audit_ref?: { entity_type: string; entity_id: string } | null
  /** P1: persisted dismiss/snooze/done state; null when active (not hidden). */
  state?: RecState
}

/** One row of ClickPay's own change trail, decorated onto a recommendation. */
export type RecentApplied = {
  applied_at: string | null
  applied_age_seconds: number | null
  operator_slug: string | null
  operator_display: string | null
  field: string | null
  old_value: string | null
  new_value: string | null
}

export type RecsEngineContext = {
  circuit_breaker: { tripped: boolean; tripped_reason: string | null }
  live_writes_env: boolean
  amc_enabled: boolean
}

/** P1: the resolved X-Operator-Scope this response was filtered against, plus
 *  how many recs couldn't be attributed to a person (campaign-level, e.g.
 *  audience) and so were excluded — never silently dropped without a count. */
export type RecsScopeInfo = { token: string; excluded_unattributable: number }

export type RecsResponse = {
  engine: RecsEngineContext
  total: number
  groups: Partial<Record<RecommendationKind, Recommendation[]>>
  recommendations: Recommendation[]
  scope?: RecsScopeInfo
  /** Count of recs currently hidden (dismissed/snoozed/done) across the whole
   *  engine — powers the Suggests page's "Hidden (n)" toggle. */
  hidden_count?: number
  /** Kinds where the generator's overfetch-by-one proved more rows exist beyond
   *  DEFAULT_LIMIT than are shown here — the tab count renders "N+" for these. */
  truncated_kinds?: RecommendationKind[]
}

/** One staged recommendation. `queue` says where it went (PR-B, feedback
 *  #100 / #102 — backend/recs/staging.queue_for_kind):
 *   - 'manual_edits' — a bid/budget/placement suggestion, now ONE pending
 *     manual edit in ClickPay Suggests' own Queued tab (`edit_id`). When that
 *     key already had a pending edit, that row comes back with
 *     `already_pending: true` and nothing new is queued (`queued_count: 0`);
 *     `in_suggests_queue` is false when the waiting row was typed into the
 *     Pending edits tray rather than queued from Suggests.
 *   - 'approvals' — an automation proposal (AMC audience): the dark one-shot
 *     rule that self-queued into Rules > Approvals (`rule_id`, `run_id`). */
export type StageResult = {
  rec_key: string
  queue: 'manual_edits' | 'approvals'
  queued_count: number
  edit_id?: number
  already_pending?: boolean
  in_suggests_queue?: boolean
  operator_slug?: string
  rule_id?: number
  run_id?: number | null
  pending_change_ids?: number[]
}

// ── Rules from repeated manual edits (Build 35) ───────────────────────────
// Mined from the permanent manual-edit trail: "you made this same change N
// times — want a rule for it?". Every proposed_rule is compile-checked
// server-side before it is returned, so Create draft can't 400.

/** How the proposed rule lines up with reality RIGHT NOW — an as-of-today
 *  overlap, explicitly NOT a historical coverage figure (Build 06's backtest
 *  returns per-day counts, not matched entity ids, so true coverage isn't
 *  computable and is never claimed). */
export type EditPatternMatchToday = {
  status: 'ok' | 'unavailable' | 'unsupported' | 'error'
  edited_entities?: number
  edited_still_matching?: number
  extra_entities?: number
  total_matching?: number
}

/** Opt-in historical replay through backend/engine/backtest.py. `not_run` is
 *  the default — the replay is one query per day per pattern, so the panel
 *  asks for it explicitly rather than paying for it on every page load. */
export type EditPatternBacktest = {
  status: 'not_run' | 'counts_only' | 'unavailable' | 'unsupported' | 'error'
  start_date?: string
  end_date?: string
  days_covered?: number
  days_insufficient_history?: number
  days_errored?: number
  /** SUM across the replayed days — the same entity counted once per day it
   *  would have fired, NOT a count of distinct entities. */
  would_fire_total?: number
  avg_entities_per_day?: number | null
  /** SUM of a per-day $/DAY rate (engine/blast_radius.py:62) across the
   *  replayed days — a window total, NOT a daily figure. Render
   *  `avg_spend_delta_per_day` when the sentence says "a day". */
  est_spend_delta_total?: number
  avg_spend_delta_per_day?: number | null
  reason?: string
  error?: string
}

export type EditPatternProposal = {
  data_source: string
  filter_json: Record<string, unknown>
  action_type: string
  action_params: Record<string, unknown>
  plain_english: string
  /** 'current_metrics' — thresholds come from the edited entities' CURRENT
   *  view metrics, a proxy for what they looked like at edit time. */
  inference_basis: string
  sample_entities: { entity_id: string; label: string | null }[]
}

export type EditPattern = {
  pattern_key: string
  entity_type: string
  field: string
  direction: string
  count: number
  distinct_entities: number
  window_days: number
  operators: Record<string, number>
  median_multiplier: number | null
  first_edit_at: string | null
  last_edit_at: string | null
  /** Edits counted but unusable for a direction (NULL old_value snapshots). */
  directionless_edits: number
  proposed_rule: EditPatternProposal | null
  no_proposal_reason: string | null
  match_today: EditPatternMatchToday | null
  backtest: EditPatternBacktest
  state?: RecState
  /** Set once create-draft has minted a rule from this pattern (the endpoint
   *  is idempotent and returns that same id). The card shows "open draft rule
   *  #N" instead of a second Create button — mining reads the audit trail, so
   *  the pattern itself looks unchanged after a draft is created. */
  draft_rule_id?: number | null
}

export type EditPatternsResponse = {
  patterns: EditPattern[]
  total: number
  hidden_count: number
  window_days: number
  min_count: number
  backtest_days: number
  inference_basis: string
}

export type StageBulkResult = {
  staged: StageResult[]
  errors: { rec_key: string; error: string }[]
  queued_total: number
  /** Keys that already had a pending edit — returned, not duplicated (PR-B). */
  already_pending_total?: number
}

/** submitRec applies a recommendation to Amazon NOW through the SAME audited
 *  manual-edit pipeline the Pending Tray uses (operators_api.apply_manual_edit)
 *  — no new write path. Only mappable kinds (bid_down/bid_up/budget_up/
 *  budget_down/placement) can submit; everything else 400s. `status` mirrors
 *  the manual-edit apply contract: 'applied' (a real write landed),
 *  'staged_no_writes' (both env gates off — recorded, nothing reached
 *  Amazon), or 'failed' (see error_msg). */
export type SubmitResult = {
  rec_key: string
  edit_id: number
  status: 'applied' | 'staged_no_writes' | 'failed'
  error_msg?: string
  target?: { profile_id: string; ad_type: string; field: string }
}

/** An operator's saved scope/context preference (TRACK D / F4.4,
 *  GET/PUT /api/operator-prefs). `selected_scope` is 'mine' | 'all' |
 *  '<operator-slug>'; `marketplace` is the remembered All/US/CA narrowing (null
 *  = All). `persisted` distinguishes a stored choice from the implicit default
 *  the backend returns when nothing was saved (or no operator resolved). */
export type OperatorPrefs = {
  viewer_operator_slug: string | null
  selected_scope: string
  marketplace: string | null
  updated_at: string | null
  persisted: boolean
}

// ── Daily Brief (P2 backend / P3 frontend) ──────────────────────────────────
// GET /api/brief?scope=<all|mine|slug> — one assembled, LIVE-computed payload
// (D:\Downloads\CLICKPAY_DAILY_BRIEF_PLAN.md PHASE 2/3, backend/api/brief_api.py).
// Locked decision: no snapshot, no scheduled generation — every field here is
// fresh as of the request that returned it.

export type BriefOperator = { slug: string; display_name: string; color: string | null }

export type BriefScope = {
  token: string
  operator: BriefOperator | null
  uids: number
  asins: number
  marketplaces: string[]
  /** True for an operator with zero assigned UIDs (e.g. a brand-new hire) —
   *  every other section is honestly all-zero in this case; the FE must
   *  render the explicit empty state, never a silent wall of zeros. */
  empty_book: boolean
}

export type BriefWorstAdBleed = {
  uid: string | null
  acos_pct: number | null
  spend_7d: number | null
  /** Risk-register mitigation: true when this bleed is a suggestion the
   *  operator already dismissed, but it re-surfaced here because it grew
   *  past the escalation threshold — the 2-day PPC-lag trap. */
  dismissed_but_escalating: boolean
}

export type BriefTldr = {
  stockouts: number
  worst_ad_bleed: BriefWorstAdBleed | null
  uncovered_cogs_units: number
}

export type BriefPeriodStat = { yest: number; d7: number; d30: number }
export type BriefPeriodPct = { d7: number | null; d30: number | null }

export type BriefPnl = {
  revenue: BriefPeriodStat
  units: BriefPeriodStat
  ad_spend: { d7: number; d30: number }
  /** SP+SD spend / SP+SD ad-attributed sales. Excludes Sponsored Brands from
   *  BOTH halves (SB has no per-ASIN daily report), so this numerator is NOT
   *  `ad_spend`. Null when the PPC mirrors are unreadable or attributed sales
   *  is zero — see acos_basis. */
  acos: BriefPeriodPct
  acos_basis: 'sp_sd_attributed' | 'unavailable'
  /** Total ad spend (Sponsored Brands included) / total sales. */
  tacos: BriefPeriodPct
  tacos_basis: 'ads_over_total_sales'
  ads_through_date: string | null
  /** Rendered inline wherever P&L numbers show — never hidden in a tooltip. */
  ads_caveat: string
}

export type BriefInventoryRow = {
  uid: string
  title: string | null
  asin: string | null
  fba_qty: number | null
  rate_per_day: number | null
  days: number | null
  inbound: number | null
}

export type BriefInventory = {
  critical: number
  low: number
  overstock: number
  rows: BriefInventoryRow[]
}

export type BriefPpcRow = {
  uid: string | null
  acos_pct: number | null
  spend_7d: number | null
  /** The generator's real action text, e.g. "Bid down $1.42 -> $1.05" —
   *  never a hardcoded "lower bids ~25%". */
  action: string
  rec_key: string
  kind: RecommendationKind
}

export type BriefPpc = {
  need_attention: number
  excluded_unattributable: number
  rows: BriefPpcRow[]
}

export type BriefPickDomain = 'restock' | 'overstock' | 'uncovered_cogs' | 'ppc'

export type BriefPickActions = {
  /** Non-PPC picks: a plain deep-link (Restock Hub / Products profitability). */
  link?: string
  /** PPC picks only: stages via the SAME approval queue as Suggests — never
   *  auto-applies. */
  accept?: { rec_key: string }
}

export type BriefPick = {
  /** Stable state key: for PPC picks this IS the rec_key (dismissing here
   *  hides it in Suggests too); for non-PPC picks it's "brief:<domain>:<uid>". */
  key: string
  domain: BriefPickDomain
  uid: string | null
  title: string | null
  /** Plain-English, one sentence, with the dollar figure. */
  why: string
  impact_usd_per_week: number
  actions: BriefPickActions
}

export type BriefDataHealthRow = {
  feed: string
  last_loaded: string | null
  status: string
  caveat: string | null
}

export type BriefUnassigned = { uids: number | null; no_asin: number | null }

// ── Follow-up queue (Phase 9 Build 32) ───────────────────────────────────
// "You changed this a week ago — did it work?" Live-computed from the stores
// that already record each change; nothing here is a snapshot. NOT related to
// a rule's post-fire follow-up chain (/api/rules/{id}/followups) — different
// concept, different endpoint.

/** Which store the change came from. `price` covers BOTH a person editing our
 *  listed price and Amazon's own price moving — `FollowupItem.detected`
 *  distinguishes them. */
export type FollowupLane = 'rule' | 'approval' | 'manual' | 'price'

/** A hint chip, not a claim — the numbers beside it are the payload.
 *  - `worked`       the change did what it was trying to do
 *  - `check`        something moved the wrong way; worth a look
 *  - `no_effect`    measurable, but nothing moved much
 *  - `unclear`      the maths is undefined (e.g. nothing to compare against)
 *  - `pending_data` Amazon hasn't delivered the days after the change yet
 *  - `no_metrics`   this kind of thing has no daily numbers anywhere */
export type FollowupVerdict =
  | 'worked' | 'check' | 'no_effect' | 'unclear' | 'pending_data' | 'no_metrics'

export type FollowupChange = {
  entity_type: string
  entity_id: string
  field: string
  before: string | number | null
  after: string | number | null
}

export type FollowupPpcWindow = {
  impressions: number
  clicks: number
  spend: number
  sales: number
  purchases: number
  /** null when there were no sales to divide by — never rendered as 0%. */
  acos: number | null
}

export type FollowupPpcOutcome = {
  kind: 'ppc'
  pre: FollowupPpcWindow
  post: FollowupPpcWindow
  /** null when the before-window was zero (a percent change off zero is
   *  undefined, and 0 would read as "nothing moved") OR when
   *  `days_covered_post` is 0 — no days after the change have arrived, so the
   *  post sums are zeros-because-absent and subtracting them would print a
   *  -100% collapse on a row that means "we don't know yet". */
  spend_delta_pct: number | null
  sales_delta_pct: number | null
  /** null for the same reason as the percentages when there is no
   *  after-window. Never a full-size fabricated dollar swing. */
  spend_delta_usd: number | null
  sales_delta_usd: number | null
  days_covered_pre: number
  /** < eval_days means Amazon's feed hasn't caught up — say so on the row.
   *  0 means nothing after the change has arrived at all. */
  days_covered_post: number
  data_through: string | null
  measured_entities: number
  /** Changed things with no daily table anywhere in ClickPay (search terms,
   *  ad groups, product ads). Genuinely unmeasurable. */
  no_metrics_entities: number
  /** Changed things that DO have daily numbers but sit at a different grain
   *  from the measured one — e.g. the campaign containing the keywords above.
   *  Left out because their spend is already inside the totals. */
  excluded_for_double_count: number
}

export type FollowupPriceWindow = { units: number; sales: number; net_profit: number }

export type FollowupPriceOutcome = {
  kind: 'price'
  pre: FollowupPriceWindow
  post: FollowupPriceWindow
  units_delta_pct: number | null
  sales_delta_pct: number | null
  profit_delta_pct: number | null
  /** null when there is no after-window yet — see FollowupPpcOutcome. */
  profit_delta_usd: number | null
  days_covered_pre: number
  days_covered_post: number
  data_through: string | null
  /** The ONE marketplace these numbers come from ('Amazon US', 'Amazon CA',
   *  or 'All' when the region maps to no P&L split). A price change is made on
   *  one channel and is measured against that channel. */
  marketplace: string
}

export type FollowupOutcome = FollowupPpcOutcome | FollowupPriceOutcome

export type FollowupItem = {
  /** bbpro_rec_state key — pass it straight to setRecState/clearRecState. */
  key: string
  lane: FollowupLane
  t0: string | null
  days_since: number | null
  who: string
  /** Plain English, already assembled server-side. */
  what: string
  entity_count: number
  changes: FollowupChange[]
  /** `mixed` = a rule run that moved some things up and others down. It gets
   *  no directional verdict, because there is no single thing it was trying
   *  to do. */
  intent: 'cut' | 'grow' | 'mixed' | 'other'
  verdict: FollowupVerdict
  /** null when the verdict is `no_metrics` — there is nothing to show. */
  outcome: FollowupOutcome | null
  measured_entity_type?: string | null
  /** Set only when the item resolves to exactly ONE product — see
   *  `uid_count`. A run spanning several products names none of them. */
  uid?: string | null
  /** Every product this item touches. The person tabs filter on this, so an
   *  item lands on each owner's tab rather than only the first one's. */
  uids?: string[]
  uid_count?: number
  asin?: string | null
  /** Price lane only: which marketplace the change was made on. */
  marketplace?: string | null
  /** true = Amazon's price moved on its own; nobody at Better Basics did it. */
  detected?: boolean
  link?: string | null
  state: RecState
}

/** GET /api/followups */
export type FollowupsResponse = {
  items: FollowupItem[]
  due_count: number
  resolved_count: number
  /** Items that couldn't be tied to a product, so a person tab can't show
   *  them. Counted, never silently dropped. */
  excluded_unattributable: number
  /** Items beyond the server's per-lane cap, dropped from `items`. 0
   *  normally. */
  truncated: number
  /** Which lanes were cut, so "we dropped some" can say which some. */
  truncated_by_lane: Record<FollowupLane, number>
  eval_days: number
  max_age_days: number
  /** What Snooze / Dismiss actually do, read from the same recs.* settings
   *  recs_api applies. The buttons describe these, never hardcoded numbers. */
  snooze_days: number
  dismiss_days: number
  ads_data_through: string | null
  scope: { token: string }
}

/** The Daily Brief's slice of the queue. */
export type FollowupsBlock = {
  items: FollowupItem[]
  /** null only when `unavailable` is true — never guessed at 0. */
  due_count: number | null
  excluded_unattributable?: number
  truncated?: number
  eval_days?: number
  max_age_days?: number
  snooze_days?: number
  dismiss_days?: number
  ads_data_through?: string | null
  /** true = the lane failed to load. The section says so instead of
   *  rendering an empty list that would read as "nothing to review". */
  unavailable: boolean
}

export type BriefResponse = {
  scope: BriefScope
  tldr: BriefTldr
  pnl: BriefPnl
  inventory: BriefInventory
  ppc: BriefPpc
  picks: BriefPick[]
  /** Optional so a frontend deployed ahead of the backend still renders the
   *  rest of the brief instead of crashing on an absent key. */
  followups?: FollowupsBlock
  /** Non-null only under the All-tab scope. */
  unassigned: BriefUnassigned | null
  data_health: BriefDataHealthRow[]
}

export type BriefTabOperator = BriefOperator & { assigned_uids: number }

/** GET /api/brief/tabs — the person-tab strip AND the header's operator
 *  switcher (PR 8, D2): every ACTIVE operator who is a person, with 0
 *  assigned_uids when they have none (Nuchem); service and test accounts never
 *  appear. `viewer` is the logged-in operator's own slug (null when
 *  unidentified — FE falls back to 'all'). */
export type BriefTabsResponse = {
  operators: BriefTabOperator[]
  viewer: string | null
}

export const api = {
  // Search: every ASIN's UIDs and SKUs, so a browser-side search box finds a
  // row by the UID the operator thinks in (backend/search_query.py).
  searchIdentifiers: () => req<{ by_asin: Record<string, string[]> }>('/api/search/identifiers'),
  // Ctrl+K palette: records matching q, grouped by kind (backend/api/command_search.py).
  commandSearch: (q: string, limit = 6, signal?: AbortSignal) =>
    req<CommandSearchResult>(`/api/search?q=${encodeURIComponent(q)}&limit=${limit}`, { signal }),
  // Rules
  listRules: () => req<Rule[]>('/api/rules'),
  getRule: (id: number) => req<RuleDetail>(`/api/rules/${id}`),
  createRule: (data: RuleInput) => req<RuleDetail>('/api/rules', { method: 'POST', body: JSON.stringify(data) }),
  updateRule: (id: number, data: Partial<RuleInput>) =>
    req<RuleDetail>(`/api/rules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteRule: (id: number) => req<{ status: string }>(`/api/rules/${id}`, { method: 'DELETE' }),
  cloneRule: (id: number) => req<RuleDetail>(`/api/rules/${id}/clone`, { method: 'POST' }),
  // v5 (migration 060): archive / unarchive endpoints.
  archiveRule:   (id: number) => req<RuleDetail>(`/api/rules/${id}/archive`,   { method: 'POST' }),
  unarchiveRule: (id: number) => req<RuleDetail>(`/api/rules/${id}/unarchive`, { method: 'POST' }),
  archiveRulesBulk: (rule_ids: number[]) =>
    req<{ archived: number; requested: number; already_archived: number }>(
      `/api/rules/archive-bulk`,
      { method: 'POST', body: JSON.stringify({ rule_ids }), headers: { 'Content-Type': 'application/json' } },
    ),
  runRule: (id: number) => req<Run>(`/api/rules/${id}/run`, { method: 'POST' }),
  listRunsForRule: (id: number, limit = 50) => req<Run[]>(`/api/rules/${id}/runs?limit=${limit}`),

  // ── AMC audiences (dark-launched behind CLICKPAY_AMC) ────────────────
  // All read endpoints answer flag-off (enabled:false / empty []). No write
  // methods here in Phase 1 — creation returns 501 until Phase 2 provisioning.
  getAmcStatus: () => req<AmcStatus>('/api/amc/status'),
  listAmcAudiences: () => req<AmcAudience[]>('/api/amc/audiences'),
  getAmcAudience: (id: number) => req<AmcAudienceDetail>(`/api/amc/audiences/${id}`),
  deleteAmcAudience: (id: number) =>
    req<{ deleted: number }>(`/api/amc/audiences/${id}`, { method: 'DELETE' }),
  listAmcRecipes: () => req<AmcRecipe[]>('/api/amc/recipes'),
  createAmcAudience: (data: { recipe_key: string; name: string; audience_type?: string; params?: Record<string, unknown> }) =>
    req<CreateAmcAudienceResult>('/api/amc/audiences', { method: 'POST', body: JSON.stringify(data) }),
  refreshAmcAudience: (id: number) =>
    req<{ job_id: number; status: string }>(`/api/amc/audiences/${id}/refresh`, { method: 'POST' }),
  setAmcAudienceStatus: (id: number, status: string) =>
    req<{ id: number; status: string }>(`/api/amc/audiences/${id}/status`, { method: 'POST', body: JSON.stringify({ status }) }),
  listAmcInsightTemplates: () => req<AmcInsightTemplate[]>('/api/amc/insights/templates'),
  runAmcInsight: (data: { template_key: string; params?: Record<string, unknown> }) =>
    req<{ job_id: number; status: string }>('/api/amc/insights/run', { method: 'POST', body: JSON.stringify(data) }),
  listAmcInsights: () => req<AmcInsight[]>('/api/amc/insights'),
  getAmcInsight: (id: number) => req<AmcInsightResult>(`/api/amc/insights/${id}`),
  pollAmc: () => req<{ advanced?: number; succeeded?: number; failed?: number; still_running?: number }>(
    '/api/amc/poll', { method: 'POST' }),

  // ── AMC activation (PR-1, dark) ──────────────────────────────────────
  // Activation mints a NORMAL ClickPay rule that flows through the rules
  // engine but can't move a real bid until the owner enables activation
  // (CLICKPAY_AMC_ACTIVATION + ADBETTER_LIVE_WRITES). previewActivation never
  // creates a rule; activateAudience defaults dry_run=true.
  previewActivation: (id: number, body: AmcActivateBody) =>
    req<AmcActivationPreview>(`/api/amc/audiences/${id}/activation-preview`,
      { method: 'POST', body: JSON.stringify(body) }),
  activateAudience: (id: number, body: AmcActivateBody) =>
    req<{ rule_id: number; dry_run: boolean; status: string }>(
      `/api/amc/audiences/${id}/activate`,
      { method: 'POST', body: JSON.stringify(body) }),
  listActivations: (id: number) =>
    req<AmcActivation[]>(`/api/amc/audiences/${id}/activations`),

  // ── AMC trend + recommendations (PR-3, read-only) ────────────────────
  // All three are read-only DuckDB reads, gated by CLICKPAY_AMC (empty/combined-
  // only when off). None submit an AMC workflow or touch a write path.
  listAmcAudienceSizes: (days = 90) =>
    req<AmcAudienceSizeSeries[]>(`/api/amc/audiences/sizes?days=${days}`),
  getAmcReach: () => req<AmcReach>('/api/amc/audiences/reach'),
  getAmcAudienceRecommendations: (id: number) =>
    req<CampaignRec[]>(`/api/amc/audiences/${id}/recommendations`),

  // ── ClickPay Suggests (P4) ───────────────────────────────────────────
  // GET is read-only (runs the generators). stageRec / stageRecsBulk queue a
  // bid/budget/placement suggestion as a pending manual edit in the Suggests
  // Queued tab, or (AMC audience only) mint the dark @once rule that
  // self-queues into Rules > Approvals — STAGING ONLY, never auto-applies
  // (see StageResult.queue). Auth gating mirrors the AMC write methods.
  listRecs: (opts?: { kind?: string; includeHidden?: boolean }) => {
    const params = new URLSearchParams()
    if (opts?.kind) params.set('kind', opts.kind)
    if (opts?.includeHidden) params.set('include_hidden', '1')
    const qs = params.toString()
    return req<RecsResponse>(`/api/recs${qs ? `?${qs}` : ''}`)
  },
  // Every write below that changes the Pending changes queue announces it
  // (lib/pendingEditsEvents.ts), so the hub's workspace and chips follow.
  stageRec: (recKey: string) =>
    afterPendingWrite(req<StageResult>(`/api/recs/${encodeURIComponent(recKey)}/stage`, { method: 'POST' })),
  stageRecsBulk: (recKeys: string[]) =>
    afterPendingWrite(req<StageBulkResult>('/api/recs/stage-bulk', {
      method: 'POST', body: JSON.stringify({ rec_keys: recKeys }),
    })),
  // PR 4 (#70) — read-only: the open suggestions for ONE page of hub rows
  // (GET /api/recs/for-rows). `id` repeats; the server takes at most 200.
  getRecsForRows: (level: RowSuggestionLevel, ids: readonly string[]) => {
    const params = new URLSearchParams({ level })
    for (const id of ids) params.append('id', id)
    return req<RowSuggestions>(`/api/recs/for-rows?${params.toString()}`)
  },
  // submitRec applies NOW — same two-gate auth, mapped onto the audited
  // manual-edit apply path (SAFETY IS THE HEADLINE: no new write path).
  submitRec: (recKey: string) =>
    afterPendingWrite(req<SubmitResult>(`/api/recs/${encodeURIComponent(recKey)}/submit`, { method: 'POST' })),
  // P1 (Suggests foundations) — persisted dismiss/snooze/done. Lighter auth
  // than stage/submit (an identified operator, not can_write_ads): hiding a
  // suggestion isn't an ads write.
  setRecState: (recKey: string, body: { state: 'dismissed' | 'snoozed' | 'done'; snooze_days?: number; note?: string }) =>
    req<{ rec_key: string; state: string; snooze_until: string | null; expires_at: string | null }>(
      `/api/recs/${encodeURIComponent(recKey)}/state`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  clearRecState: (recKey: string) =>
    req<{ rec_key: string; cleared: true }>(
      `/api/recs/${encodeURIComponent(recKey)}/state`, { method: 'DELETE' },
    ),

  // ── Rules from repeated manual edits (Build 35) ──────────────────────
  // GET is read-only (mines the audit trail). createRuleFromEditPattern mints
  // a rule enabled=FALSE, dry_run=TRUE — it cannot fire; the operator lands in
  // the editor and enables it by hand. Auth mirrors Suggests: dismiss takes
  // the lighter identified-operator gate, create-draft the full two-gate.
  getEditPatterns: (opts?: { days?: number; minCount?: number; backtestDays?: number; includeHidden?: boolean }) => {
    const params = new URLSearchParams()
    if (opts?.days) params.set('days', String(opts.days))
    if (opts?.minCount) params.set('min_count', String(opts.minCount))
    if (opts?.backtestDays) params.set('backtest_days', String(opts.backtestDays))
    if (opts?.includeHidden) params.set('include_hidden', '1')
    const qs = params.toString()
    return req<EditPatternsResponse>(`/api/edit-patterns${qs ? `?${qs}` : ''}`)
  },
  dismissEditPattern: (patternKey: string) =>
    req<{ pattern_key: string; state: string; expires_at: string | null }>(
      `/api/edit-patterns/${encodeURIComponent(patternKey)}/dismiss`, { method: 'POST' },
    ),
  // The way back out of "Not useful" — without it a mis-click buries the
  // pattern for recs.dismiss_expiry_days (30) with no employee-reachable
  // recourse. Mirrors clearRecState above.
  undismissEditPattern: (patternKey: string) =>
    req<{ pattern_key: string; state: null; cleared: true }>(
      `/api/edit-patterns/${encodeURIComponent(patternKey)}/dismiss`, { method: 'DELETE' },
    ),
  createRuleFromEditPattern: (patternKey: string, opts?: { days?: number }) => {
    const qs = opts?.days ? `?days=${opts.days}` : ''
    // `existing: true` means an earlier call already minted this pattern's
    // draft and this call returned that same rule instead of a duplicate.
    return req<{ pattern_key: string; rule_id: number; enabled: false;
                 dry_run: true; existing: boolean }>(
      `/api/edit-patterns/${encodeURIComponent(patternKey)}/create-draft${qs}`,
      { method: 'POST' },
    )
  },

  // ── Daily Brief (P2 backend / P3 frontend) ───────────────────────────
  // scope as an explicit query param (not just the X-Operator-Scope header):
  // the person-tab strip fetches all/mine/<slug> without flipping the
  // operator's own global switcher selection (plan §PHASE 2, work item 2).
  getBrief: (scope: string) => req<BriefResponse>(`/api/brief?scope=${encodeURIComponent(scope)}`),
  getBriefTabs: () => req<BriefTabsResponse>('/api/brief/tabs'),

  // ── Follow-up queue (Build 32) ───────────────────────────────────────
  // Read-only. Got it / Snooze / Dismiss go through the EXISTING
  // setRecState/clearRecState above using the item's own `key` — this
  // endpoint has no write side at all.
  getFollowups: (opts?: { scope?: string; status?: 'due' | 'resolved' | 'all' }) => {
    const params = new URLSearchParams()
    if (opts?.scope) params.set('scope', opts.scope)
    if (opts?.status) params.set('status', opts.status)
    const qs = params.toString()
    return req<FollowupsResponse>(`/api/followups${qs ? `?${qs}` : ''}`)
  },

  // ── Decision-tree rule body (rules_tree_api.py) ──────────────────────
  // GET is public; PUT validates the body via TreeRule + semantic checks
  // server-side and flips the row to rule_kind='tree'. The V3 wizard's tree
  // mode does a two-phase save: createRule() to mint the row, then putRuleTree()
  // to attach the validated tree body.
  getRuleTree: (id: number) => req<TreeFetch>(`/api/rules/${id}/tree`),
  putRuleTree: (id: number, data: TreeBodyInput) =>
    req<{ rule_id: number; rule_kind: string; updated_by: string }>(
      `/api/rules/${id}/tree`,
      { method: 'PUT', body: JSON.stringify(data) },
    ),

  // ── ETL freshness observatory (migration 047) ────────────────────
  getFreshness: () => req<FreshnessOverviewResponse>('/api/freshness'),
  getFreshnessAlerts: () => req<{ alerts: FreshnessAlert[] }>('/api/freshness/alerts'),
  ackFreshnessAlert: (alert_id: number, by?: string) =>
    req<{ ack: boolean; alert_id: number }>(
      `/api/freshness/alerts/${alert_id}/ack`,
      { method: 'POST', body: JSON.stringify({ by: by ?? null }) }
    ),
  checkFreshnessNow: () =>
    req<{ summary: FreshnessSummary }>('/api/freshness/check-now',
      { method: 'POST' }),
  refreshLocalSyncNow: () =>
    req<{ started: boolean; started_at: string }>(
      '/api/freshness/refresh-now',
      { method: 'POST' }),
  getPpcFreshness: () =>
    req<{
      /** YYYY-MM-DD of the last COMPLETE attributed report day (the data
       *  horizon every PPC page clamps to — UI overhaul PR-3b). */
      last_complete_date: string | null
      last_synced_at: string | null
      is_refreshing: boolean
      last_refresh_error: string | null
      current_table: string | null
      tables_total: number
      tables_synced: number
      last_refresh_result: { table_count?: number; message?: string } | null
    }>('/api/ppc-freshness'),
  getStaleLocalTables: () =>
    req<{
      count: number
      stale: Array<{
        local_table: string
        bq_table: string
        reason: string
        bq_modified_at: string
        local_synced_at: string | null
        lag_hours: number | null
      }>
    }>('/api/freshness/stale-local-tables'),
  getAdminRefreshes: () =>
    req<{
      refreshes: Array<{
        id: string
        label: string
        running: boolean
        external?: boolean
        started_at: string | null
        finished_at: string | null
        completed: number
        total: number
        current_label: string | null
        log_path?: string
        error: string | null
      }>
    }>('/api/admin/refreshes'),
  getAdminTriggers: () =>
    req<{
      triggers: Record<string, {
        label: string
        kind: 'scheduled_task' | 'script' | 'internal'
        target: string
        notes: string | null
        available: boolean
      }>
    }>('/api/admin/triggers'),
  triggerJob: (runs_where: string) =>
    req<{
      started: boolean
      kind: string
      target: string
      note: string | null
    }>('/api/admin/trigger-job',
      {
        method: 'POST',
        body: JSON.stringify({ runs_where }),
        // The CSRF guard on /api/admin/* requires this header.
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      }),

  // ── Saved table filter presets (migration 050) ───────────────────
  listTableFilters: (params: { table_key?: string; data_source?: string } = {}) => {
    const q = new URLSearchParams()
    if (params.table_key)   q.set('table_key', params.table_key)
    if (params.data_source) q.set('data_source', params.data_source)
    const qs = q.toString()
    return req<TableFilter[]>(`/api/table-filters${qs ? `?${qs}` : ''}`)
  },
  /** v5.2 — fetch one saved filter by id. Used by RuleEdit's URL-param
   *  load (?filter_id=N) to pre-populate the rule with a carry-over from
   *  an Analytics tab. */
  getTableFilter: (id: number) =>
    req<TableFilter>(`/api/table-filters/${id}`),
  createTableFilter: (data: TableFilterInput) =>
    req<TableFilter>('/api/table-filters',
      { method: 'POST', body: JSON.stringify(data) }),
  updateTableFilter: (id: number, data: TableFilterInput) =>
    req<TableFilter>(`/api/table-filters/${id}`,
      { method: 'PUT', body: JSON.stringify(data) }),
  deleteTableFilter: (id: number) =>
    req<{ deleted: number }>(`/api/table-filters/${id}`,
      { method: 'DELETE' }),

  // ── Follow-up actions (migration 046, rule-consolidation PR3) ──────
  listFollowups: (rule_id: number) =>
    req<Followup[]>(`/api/rules/${rule_id}/followups`),
  createFollowup: (rule_id: number, data: FollowupInput) =>
    req<Followup>(`/api/rules/${rule_id}/followups`,
      { method: 'POST', body: JSON.stringify(data) }),
  updateFollowup: (followup_id: number, data: Partial<FollowupInput>) =>
    req<Followup>(`/api/rules/followups/${followup_id}`,
      { method: 'PUT', body: JSON.stringify(data) }),
  deleteFollowup: (followup_id: number) =>
    req<{ status: string; id: number }>(`/api/rules/followups/${followup_id}`,
      { method: 'DELETE' }),

  // ── Aakaar Goal 1 — Profiles ─────────────────────────────────────
  listProfiles: () => req<Profile[]>('/api/profiles'),
  patchProfile: (id: number, patch: Partial<Pick<Profile, 'target_acos' | 'monthly_ad_budget' | 'marketplace_id' | 'ads_profile_id' | 'status'>>) =>
    req<Profile>(`/api/profiles/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  // ── Aakaar Goal 4 — Agent Library + Campaigns + Bindings ────────
  listAgentGroups: () => req<AgentGroup[]>('/api/agent-groups'),
  syncCampaignProfileMap: () =>
    req<{ started: true; started_at: string }>(
      '/api/admin/sync-campaign-profile-map',
      { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' } }
    ),

  campaignProfileMapStatus: () =>
    req<{
      by_market: { marketplace: string | null; ad_type: string; count: number }[]
      last_synced_at: string | null
      last_run_finished_at: string | null
      last_run_error: string | null
      last_run_summary: unknown | null
    }>('/api/admin/campaign-profile-map-status'),

  listCampaigns: (opts?: {
    limit?: number; ad_type?: string; portfolio_id?: string;
    /** E+ — incoming Portfolio drill: narrow to one portfolio's campaigns by
     *  human name (a Campaigns "Portfolio" cell click). */
    portfolio_name?: string;
    /** E+ — incoming Campaign drill: narrow to a single campaign (a /targets,
     *  /ad-groups, /keywords, or /placements Campaign cell click). */
    campaign_id?: string;
    state?: string; marketplace?: string;
    from?: string; to?: string;
    /** When true, include SP campaigns that exist in the catalog but had
     *  no activity in the date window (paused single-keyword campaigns
     *  etc.). Defaults to false on the backend. */
    include_inactive?: boolean;
  }) => {
    const q = new URLSearchParams()
    if (opts?.limit) q.set('limit', String(opts.limit))
    if (opts?.ad_type) q.set('ad_type', opts.ad_type)
    if (opts?.portfolio_id) q.set('portfolio_id', opts.portfolio_id)
    if (opts?.portfolio_name) q.set('portfolio_name', opts.portfolio_name)
    if (opts?.campaign_id) q.set('campaign_id', opts.campaign_id)
    if (opts?.state) q.set('state', opts.state)
    if (opts?.marketplace) q.set('marketplace', opts.marketplace)
    if (opts?.from) q.set('from', opts.from)
    if (opts?.to) q.set('to', opts.to)
    if (opts?.include_inactive) q.set('include_inactive', 'true')
    return req<CampaignRow[]>(`/api/campaigns${q.toString() ? '?' + q : ''}`)
  },
  /** Campaigns with the paged ENVELOPE (items + true `total` + whole-set
   *  `totals`). SM W1.1: the Campaigns grid pages this on the server
   *  (limit/offset/sort/dir/search/filter). Its live columns (Pacing, Today)
   *  are merged in the browser and do not exist server-side, so sorting or
   *  filtering by one switches the page to a whole-set load (limit 10000)
   *  instead of sending the server a column it would reject. */
  listCampaignsPaged: (opts?: CampaignListOpts) => {
    const q = new URLSearchParams()
    q.set('envelope', '1')
    campaignPagingParams(q, opts)
    if (opts?.limit) q.set('limit', String(opts.limit))
    if (opts?.ad_type) q.set('ad_type', opts.ad_type)
    if (opts?.portfolio_id) q.set('portfolio_id', opts.portfolio_id)
    if (opts?.portfolio_name) q.set('portfolio_name', opts.portfolio_name)
    if (opts?.campaign_id) q.set('campaign_id', opts.campaign_id)
    if (opts?.state) q.set('state', opts.state)
    if (opts?.marketplace) q.set('marketplace', opts.marketplace)
    if (opts?.from) q.set('from', opts.from)
    if (opts?.to) q.set('to', opts.to)
    if (opts?.include_inactive) q.set('include_inactive', 'true')
    return req<Paged<CampaignRow>>(`/api/campaigns?${q}`)
  },
  /** SM W1.1 — every campaign id the same query matches (no limit), for the
   *  bulk grid's "Select all N matching". Same params as listCampaignsPaged;
   *  limit/offset are ignored server-side. */
  listCampaignIds: (opts?: CampaignListOpts) => {
    const q = new URLSearchParams()
    q.set('ids_only', '1')
    campaignPagingParams(q, opts)
    for (const k of ['ad_type', 'portfolio_id', 'portfolio_name', 'campaign_id', 'state', 'marketplace', 'from', 'to'] as const) {
      const v = opts?.[k]
      if (v) q.set(k, v)
    }
    if (opts?.include_inactive) q.set('include_inactive', 'true')
    return req<{ ids: string[]; total: number }>(`/api/campaigns?${q}`)
  },
  listBindings: (campaign_id: string) =>
    req<Binding[]>(`/api/campaigns/${encodeURIComponent(campaign_id)}/bindings`),
  patchBinding: (id: number, patch: Partial<Pick<Binding, 'enabled' | 'target_acos' | 'dry_run'>>) =>
    req<{ id: number; patched: string[] }>(`/api/bindings/${id}`, {
      method: 'PATCH', body: JSON.stringify(patch),
    }),
  createBinding: (data: { rule_id: number; campaign_id: string; target_acos?: number; dry_run?: boolean }) =>
    req<{ id: number }>('/api/bindings', { method: 'POST', body: JSON.stringify(data) }),
  deleteBinding: (id: number) =>
    req<{ id: number; deleted: boolean }>(`/api/bindings/${id}`, { method: 'DELETE' }),

  // ── Aakaar Goal 6 — AI Assistant chat ───────────────────────────
  // PR 12b (C10): `opts.date_range` is the page's range (the panel's range chip); the
  // server then adds PAGE RANGE sections to what the assistant sees. Omitted -> not sent.
  chatAnalytics: (
    question: string,
    history?: Array<{role: string; content: string}>,
    opts?: { date_range?: { from: string; to: string }; page?: string },
  ) =>
    req<{ answer: string; context_size: number }>('/api/chat/analytics', {
      method: 'POST',
      body: JSON.stringify({ question, history, date_range: opts?.date_range, page: opts?.page }),
    }),
  compileNlRule: (text: string, data_source = 'keyword_performance') =>
    req<{ rule: Record<string, unknown> | null; errors: string[]; warnings: string[] }>(
      '/api/chat/compile-rule',
      { method: 'POST', body: JSON.stringify({ text, data_source }) },
    ),

  // ── Aakaar Goal 8 — Profitability Report ───────────────────────
  profitabilityReport: (window_days = 30) =>
    req<{
      window: { start: string; end: string; days: number };
      totals: Record<string, number | null | boolean>;
      by_asin: Array<Record<string, number | string | null>>;
    }>(`/api/analytics/profitability?window_days=${window_days}`),

  // ── Aakaar Goal 9 — Strategies ─────────────────────────────────
  ramp2027: (asin: string, portfolio_id = '74834645701962', stagger_days = 2) =>
    req<{ asin: string; found: number; plan: Array<{day_offset: number; tier: number; campaign_ids: string[]; action: string}>; errors: string[] }>(
      '/api/strategies/ppc2027-ramp',
      { method: 'POST', body: JSON.stringify({ asin, portfolio_id, stagger_days }) },
    ),
  investmentFunnel: (asin: string, daily_budget_total = 50, target_acos = 0.25) =>
    req<{ asin: string; campaign_count: number; plan: Array<Record<string, unknown>> }>(
      '/api/strategies/investment-funnel',
      { method: 'POST', body: JSON.stringify({ asin, daily_budget_total, target_acos }) },
    ),

  // ── Aakaar Goal 10/11 — Listing Agent ──────────────────────────
  scoreListing: (asin: string, listing: Record<string, unknown>, marketplace = 'amazon', required_terms?: string[]) =>
    req<{
      overall: number; length_score: number; coverage_score: number;
      compliance_score: number; readability_score: number;
      issues: string[]; suggestions: string[];
    }>('/api/listings/score', {
      method: 'POST',
      body: JSON.stringify({ asin, listing, marketplace, required_terms }),
    }),
  rewriteListing: (asin: string, listing: Record<string, unknown>, marketplace = 'amazon', required_terms?: string[], issues?: string[]) =>
    req<{
      rewrites: { title?: string; bullets?: string[]; description?: string; backend_keywords?: string };
      compliance_check: string[];
      preserved_terms: string[];
    }>('/api/listings/rewrite', {
      method: 'POST',
      body: JSON.stringify({ asin, listing, marketplace, required_terms, issues }),
    }),

  // Runs
  listRuns: (limit = 100, status?: string) => {
    // Legacy signature — preserved so existing callers (Approvals etc.) keep working.
    // For new code prefer listRunsPaged() which surfaces total + page metadata.
    const q = new URLSearchParams({ limit: String(limit) })
    if (status) q.set('status', status)
    return req<Run[]>(`/api/runs?${q}`)
  },
  /** Paged + filterable runs list. Returns {rows, total, page, page_size}. */
  listRunsPaged: (filter: RunsFilter = {}) =>
    req<RunsPage>(`/api/runs?${runsFilterQuery(filter)}`),
  /** CSV export honouring the same filter envelope as listRunsPaged. */
  exportRunsCsv: async (filter: RunsFilter = {}): Promise<{ blob: Blob; filename: string }> => {
    const res = await fetch(`/api/runs/export.csv?${runsFilterQuery(filter)}`)
    if (!res.ok) {
      const text = await res.text()
      throw new Error(`${res.status} ${res.statusText}: ${text}`)
    }
    const cd = res.headers.get('Content-Disposition') ?? ''
    const m = cd.match(/filename="?([^"]+)"?/i)
    const filename = m?.[1] ?? `runs-${new Date().toISOString().slice(0, 10)}.csv`
    const blob = await res.blob()
    return { blob, filename }
  },
  /** SellerMate W1.11 — one Change-history read. lib/changeHistoryApi.ts builds
   *  the /api/audit/... path and owns the response types. */
  getChangeHistory: <T>(path: string) => req<T>(path),
  /** SellerMate W1.11 — the Change-history CSV download (same shape as
   *  exportRunsCsv, plus the server's full match count). */
  exportChangeHistoryCsv: async (path: string): Promise<{ blob: Blob; filename: string; total: number }> => {
    const res = await fetch(path, { headers: { ...operatorScopeHeaders() } })
    if (!res.ok) throw new ApiError(path, res.status, res.statusText, await res.text())
    const cd = res.headers.get('Content-Disposition') ?? ''
    const m = cd.match(/filename="?([^"]+)"?/i)
    const filename = m?.[1] ?? 'change-history.csv'
    const total = Number(res.headers.get('X-Total-Count') ?? '0') || 0
    return { blob: await res.blob(), filename, total }
  },
  /** 30-day-by-default per-rule run history for the run-history chart. */
  getRuleRunHistory: (ruleId: number, windowDays = 30) =>
    req<RuleRunHistoryPoint[]>(`/api/rules/${ruleId}/run-history?window_days=${windowDays}`),
  /** Same shape, but summed across EVERY rule — the Runs tab's default
   *  (no rule selected) view. */
  getAllRulesRunHistory: (windowDays = 30) =>
    req<RuleRunHistoryPoint[]>(`/api/rule-run-history?window_days=${windowDays}`),
  getRun: (id: number) => req<Run>(`/api/runs/${id}`),
  getRunChanges: (runId: number, limit = 500) => req<Change[]>(`/api/runs/${runId}/changes?limit=${limit}`),
  /** ACoS-since impact for a run's live changes. 400s for a run that hasn't
   *  finished yet (no window to measure) — callers should treat that as "not
   *  available" rather than a hard error. */
  getRunImpact: (runId: number) => req<RunImpact>(`/api/runs/${runId}/impact`),
  /** Preview (dryRun=true) or confirm (dryRun=false) an undo of a run's live
   *  changes. Admin-only + CSRF-guarded server-side (Phase 5 Build 08) — the
   *  frontend also hides the Undo button for non-admins, same
   *  belt-and-suspenders pattern as rolloutAdminApi. `expectedOps` is the op
   *  count shown during preview; passing it on confirm lets the server 409
   *  if the eligible set changed since preview (TOCTOU guard) instead of
   *  silently reverting a different set than what was shown. */
  revertRun: (runId: number, dryRun: boolean, expectedOps?: number) =>
    req<RevertRunResult>(`/api/runs/${runId}/revert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ dry_run: dryRun, expected_ops: expectedOps ?? null }),
    }),

  // Tags
  listTagCatalog: () => req<TagCatalogEntry[]>('/api/tags/catalog'),
  listTags: (limit = 500, tag?: string) => {
    const q = new URLSearchParams({ limit: String(limit) })
    if (tag) q.set('tag', tag)
    return req<Tag[]>(`/api/tags?${q}`)
  },
  clearTag: (entity_type: string, entity_id: string, tag: string) =>
    req<{ status: string }>(`/api/tags/${entity_type}/${entity_id}/${encodeURIComponent(tag)}`,
      { method: 'DELETE' }),
  clearTagsBulk: (params: { tag?: string; expired?: boolean }) => {
    const q = new URLSearchParams()
    if (params.tag) q.set('tag', params.tag)
    if (params.expired) q.set('expired', 'true')
    return req<{ status: string }>(`/api/tags?${q}`, { method: 'DELETE' })
  },

  // Metadata
  listActions: () => req<ActionType[]>('/api/actions'),
  // Settings catalog — existing read-only route (settings_api.py). Used by the
  // rule builder's ValueInput to populate the $setting picker.
  listSettings: () => req<{
    categories: Record<string, Array<{
      setting_key: string; value_type: string; description: string | null
    }>>
    count: number
  }>('/api/settings'),
  getDataSources: () => req<{
    data_sources: DataSource[]
    operators: string[]
    windows?: string[]
    /** Map of lag-group → default days. Mirrors LAG_GROUP_DEFAULTS in
     *  backend/engine/runner.py; UI uses it to render the freshness chip's
     *  per-source breakdown. */
    lag_groups?: Record<string, number>
  }>('/api/data-sources'),

  // v5.1 — value-picker catalog. Returns DB-backed suggestions for a field
  // (state values, portfolio names, ASIN/title pairs, etc.). The picker
  // hits this on every keystroke; backend has a 60s LRU cache.
  getFieldValues: (
    data_source: string, field: string,
    opts: { q?: string; limit?: number } = {},
  ) => {
    const q = new URLSearchParams({ data_source, field })
    if (opts.q) q.set('q', opts.q)
    if (opts.limit !== undefined) q.set('limit', String(opts.limit))
    return req<FieldValuesResponse>(`/api/field-values?${q}`)
  },

  // v5.1 — register a new tag name in bbpro_tag_registry. Idempotent.
  // Used by ValuePicker's inline "+ Create tag" workflow when the operator
  // wants to filter on a tag that doesn't exist yet (e.g. authoring the
  // filter before building the rule that applies it).
  createTagRegistry: (
    name: string,
    description?: string,
    color?: string,
    kind?: 'tag' | 'cooldown',
  ) =>
    req<TagRegistry>('/api/tags/registry', {
      method: 'POST',
      body: JSON.stringify({ name, description, color, kind }),
    }),
  listTagRegistry: (opts: { q?: string; limit?: number; kind?: 'tag' | 'cooldown' } = {}) => {
    const q = new URLSearchParams()
    if (opts.q) q.set('q', opts.q)
    if (opts.limit !== undefined) q.set('limit', String(opts.limit))
    if (opts.kind) q.set('kind', opts.kind)
    const qs = q.toString()
    return req<TagRegistry[]>(`/api/tags/registry${qs ? '?' + qs : ''}`)
  },
  schedulerJobs: () => req<SchedulerJob[]>('/api/scheduler/jobs'),
  dashboardStats: () => req<DashboardStats>('/api/dashboard/stats'),

  // Dashboard charts + KPIs (rebuilt dashboard)
  dashboardKpis: (window_days = 7) =>
    req<DashboardKpis>(`/api/dashboard/kpis?window_days=${window_days}`),
  spendTrend: (window_days = 30) =>
    req<SpendPoint[]>(`/api/dashboard/spend-trend?window_days=${window_days}`),
  acosTrend: (window_days = 30) =>
    req<AcosPoint[]>(`/api/dashboard/acos-trend?window_days=${window_days}`),
  tacosLive: () =>
    req<TacosLiveResponse>('/api/dashboard/tacos-live'),
  topCampaigns: (window_days = 7, limit = 10) =>
    req<TopCampaign[]>(`/api/dashboard/top-campaigns?window_days=${window_days}&limit=${limit}`),
  recentFires: (hours = 24) =>
    req<RecentFire[]>(`/api/dashboard/recent-fires?hours=${hours}`),
  previewFilter: (
    data_source: string,
    filter_json: unknown,
    limit = 25,
    lag_days?: number | null,
    entity_type?: string | null,
  ) =>
    req<FilterPreview>('/api/filter/preview', {
      method: 'POST',
      body: JSON.stringify({ data_source, filter_json, limit, lag_days, entity_type }),
    }),

  /** Campaign Tuner solver (C4a). data_source is fixed server-side to
   *  campaign_performance; target_acos is a whole-number percent (e.g. 25).
   *  Do NOT route this through previewFilter — the tuner requires target_acos
   *  and returns its solved campaign list under `rows` (not `sample`). */
  previewCampaignTuner: (
    filter_json: unknown,
    target_acos: number,
    opts: { limit?: number; lag_days?: number | null; cooldown_days?: number } = {},
  ) =>
    req<CampaignTunerPreview>('/api/campaign-tuner/preview', {
      method: 'POST',
      body: JSON.stringify({ filter_json, target_acos, ...opts }),
    }),

  /** #63 — dry-run an unsaved rule body (flat or tree). Zero writes to
   *  bbpro_rule_runs / bbpro_rule_run_changes; see RuleDryRunInput. Same
   *  auth tier as previewCampaignTuner/previewFilter (can_write_ads), not
   *  under /api/admin — no X-Requested-With header needed. */
  dryRunRule: (body: RuleDryRunInput) =>
    req<RuleDryRunResult>('/api/rules/dry-run', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Attach one dayparting schedule cell to a rule (migration 010). Body
   *  matches the backend DaypartingScheduleIn model exactly:
   *    hour_of_day    — 0..23 (required)
   *    day_of_week    — 0..6 (Mon=0..Sun=6) or null for "every day"
   *    bid_multiplier — float > 0 (1.0 is the no-op default, so callers
   *                     should skip posting unchanged cells)
   *  The runner folds the active hour's multiplier into the rule's update_bid
   *  base multiplier when it fires. */
  createDaypartingCell: (
    ruleId: number,
    cell: { hour_of_day: number; day_of_week: number | null; bid_multiplier: number },
  ) =>
    req<DaypartingScheduleRow>(`/api/rules/${ruleId}/dayparting`, {
      method: 'POST',
      body: JSON.stringify(cell),
    }),

  /** List every dayparting schedule cell on a rule (GET /api/rules/{id}/dayparting,
   *  backend list_dayparting). Used by the wizard's edit mode to (a) detect that
   *  a plain update_bid rule is REALLY a dayparting rule and (b) rebuild its
   *  hour-of-week grid. Empty list ⇒ not a dayparting rule. */
  getRuleDayparting: (ruleId: number) =>
    req<DaypartingScheduleRow[]>(`/api/rules/${ruleId}/dayparting`),

  /** Delete one dayparting schedule cell (DELETE /api/dayparting/{schedule_id}).
   *  The edit path clears the old grid before re-writing it so the operator's
   *  changes don't leave orphaned cells behind. */
  deleteDaypartingCell: (scheduleId: number) =>
    req<void>(`/api/dayparting/${scheduleId}`, { method: 'DELETE' }),

  /** PR-6 (P3): read-only SP-keyword hour-of-week spend/efficiency profile that
   *  powers the standalone Dayparting tab's Recommendations pane. Buckets the
   *  window by UTC hour (and optionally Python weekday). BQ-backed, 60s cache,
   *  degrades to empty buckets on any BQ failure (never throws server-side). */
  getDaypartingProfile: (opts?: {
    hours_back?: number; campaign_id?: string; by_weekday?: boolean
    from?: string; to?: string
  }) => {
    const p = new URLSearchParams()
    if (opts?.hours_back != null) p.set('hours_back', String(opts.hours_back))
    if (opts?.from) p.set('from', opts.from)
    if (opts?.to) p.set('to', opts.to)
    if (opts?.campaign_id) p.set('campaign_id', opts.campaign_id)
    if (opts?.by_weekday) p.set('by_weekday', 'true')
    const q = p.toString()
    return req<DaypartingProfile>(`/api/hourly/dayparting-profile${q ? `?${q}` : ''}`)
  },

  /** PR-6 (P3): read-only daypart recommendation — a proposed bid multiplier
   *  per UTC hour (optionally × Python weekday) with a human rationale. Pure
   *  advice; applying it stages cells through the existing dayparting CRUD. */
  getDaypartingRecommendation: (opts?: {
    hours_back?: number; campaign_id?: string; by_weekday?: boolean
    target_acos?: number; sensitivity?: number; min_clicks?: number
    weekday_aware?: boolean; from?: string; to?: string
  }) => {
    const p = new URLSearchParams()
    if (opts?.hours_back != null) p.set('hours_back', String(opts.hours_back))
    if (opts?.from) p.set('from', opts.from)
    if (opts?.to) p.set('to', opts.to)
    if (opts?.campaign_id) p.set('campaign_id', opts.campaign_id)
    if (opts?.by_weekday) p.set('by_weekday', 'true')
    if (opts?.weekday_aware) p.set('weekday_aware', 'true')
    if (opts?.target_acos != null) p.set('target_acos', String(opts.target_acos))
    if (opts?.sensitivity != null) p.set('sensitivity', String(opts.sensitivity))
    if (opts?.min_clicks != null) p.set('min_clicks', String(opts.min_clicks))
    const q = p.toString()
    return req<DaypartingRecommendation>(`/api/hourly/dayparting-recommendation${q ? `?${q}` : ''}`)
  },
  // SM W5.1 — hour x weekday heatmap (Pacific dates, inclusive; null = NA).
  getHourlyHeatmap: (opts: { metric: HeatmapMetric; from?: string; to?: string }, signal?: AbortSignal) => {
    const p = new URLSearchParams({ metric: opts.metric })
    if (opts.from) p.set('from', opts.from)
    if (opts.to) p.set('to', opts.to)
    return req<HourlyHeatmapResponse>(`/api/hourly/heatmap?${p.toString()}`, { signal })
  },

  // Field-driven catalog (migration 051). The new rule editor consumes this
  // instead of getDataSources() so the user picks fields without first
  // picking a data_source.
  getFields: () => req<FieldsResponse>('/api/fields'),
  // PPC A3 — push a tab's deep-field filter down to the rules engine. body
  // carries the page's grain + a react-querybuilder filter tree (RuleGroupType).
  entityFilter: (body: { entity_type: string; filter_json: unknown; limit?: number; lag_days?: number }) =>
    req<EntityFilterResponse>('/api/entity-filter', {
      method: 'POST', body: JSON.stringify(body),
    }),
  listSourceWarnings: () =>
    req<RuleSourceWarning[]>('/api/admin/rule-source-warnings'),

  // Safety layer — approvals
  listApprovals: (opts: { rule_id?: number; status?: string; limit?: number } = {}) => {
    const q = new URLSearchParams()
    if (opts.rule_id !== undefined) q.set('rule_id', String(opts.rule_id))
    if (opts.status) q.set('status', opts.status)
    if (opts.limit !== undefined) q.set('limit', String(opts.limit))
    const qs = q.toString()
    return req<PendingChange[]>(`/api/approvals${qs ? `?${qs}` : ''}`)
  },
  approveChange: (id: number, decided_by = 'ui') =>
    req<PendingChange>(`/api/approvals/${id}/approve`, {
      method: 'POST', body: JSON.stringify({ decided_by }),
    }),
  rejectChange: (id: number, decided_by = 'ui') =>
    req<PendingChange>(`/api/approvals/${id}/reject`, {
      method: 'POST', body: JSON.stringify({ decided_by }),
    }),
  bulkApprove: (ids: number[], decided_by = 'ui') =>
    req<BulkApproveResult>('/api/approvals/bulk-approve', {
      method: 'POST', body: JSON.stringify({ ids, decided_by }),
    }),

  // Safety layer — circuit breaker
  getCircuitBreaker: () => req<CircuitState>('/api/circuit-breaker'),
  resetCircuitBreaker: (cleared_by = 'ui') =>
    req<CircuitState>('/api/circuit-breaker/reset', {
      method: 'POST', body: JSON.stringify({ cleared_by }),
    }),

  // Reports (migration 009) — saved-report CRUD + run + CSV export
  listReports: () => req<Report[]>('/api/reports'),
  getReport: (id: number) => req<Report>(`/api/reports/${id}`),
  createReport: (data: ReportInput) =>
    req<Report>('/api/reports', { method: 'POST', body: JSON.stringify(data) }),
  updateReport: (id: number, data: Partial<ReportInput>) =>
    req<Report>(`/api/reports/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteReport: (id: number) =>
    req<{ status: string }>(`/api/reports/${id}`, { method: 'DELETE' }),
  runReport: (id: number, limit?: number) => {
    const q = limit !== undefined ? `?limit=${limit}` : ''
    return req<ReportRunResult>(`/api/reports/${id}/run${q}`, { method: 'POST' })
  },
  exportReportCsvUrl: (id: number) => `/api/reports/${id}/export`,
  exportReportCsv: async (id: number): Promise<{ blob: Blob; filename: string }> => {
    const res = await fetch(`/api/reports/${id}/export`, { method: 'POST' })
    if (!res.ok) {
      const text = await res.text()
      throw new Error(`${res.status} ${res.statusText}: ${text}`)
    }
    // Extract filename from Content-Disposition header if present.
    const cd = res.headers.get('Content-Disposition') ?? ''
    const m = cd.match(/filename="?([^"]+)"?/i)
    const filename = m?.[1] ?? `report-${id}.csv`
    const blob = await res.blob()
    return { blob, filename }
  },

  // ── Operator scope/context preferences (TRACK D / F4.4) ──────────────────
  // Each operator's OWN remembered switcher selection, persisted server-side
  // (per employee, not per browser) so it follows them across devices. Identity
  // is the request operator (F4.1) — you always read/write your OWN row.
  // NULL-tolerant: no resolvable operator / no saved row → the neutral default
  // (scope:'all', persisted:false), so an unidentified or first-time caller just
  // gets whole-catalog rather than an error.
  getOperatorPrefs: () => req<OperatorPrefs>('/api/operator-prefs'),
  putOperatorPrefs: (body: { selected_scope?: string; marketplace?: string | null }) =>
    req<OperatorPrefs>('/api/operator-prefs', { method: 'PUT', body: JSON.stringify(body) }),
}

// ── Reports (migration 009) ─────────────────────────────────────────────

export type ReportSort = { field: string; dir: 'asc' | 'desc' }

export type Report = {
  id: number
  name: string
  description: string | null
  data_source: string
  filter_json: unknown
  columns_json: string[] | null
  sort_json: ReportSort[] | null
  limit_rows: number | null
  account_scope: string | null
  created_by: string | null
  created_at: string
  updated_at: string | null
}

export type ReportInput = {
  name: string
  description?: string | null
  data_source: string
  filter_json: unknown
  columns_json?: string[] | null
  sort_json?: ReportSort[] | null
  limit_rows?: number
  account_scope?: string
  created_by?: string | null
}

export type ReportRunResult = {
  rows: Record<string, unknown>[]
  total: number
}

// ── Approvals bulk operations (Approvals page v2) ───────────────────────
//
// Extends the safety layer with filter-driven bulk endpoints + paginated
// listing. The single-row approve/reject paths in `api` above are kept
// for backward compat with anything that still hits them.

export type PendingChangeWithContext = PendingChange & {
  /** Full snapshot of the matched record at the time the rule fired, or
   *  null for changes queued before migration 010 populated this field. */
  matched_record_json: Record<string, unknown> | null
  /** True when the underlying record's bid/state has drifted since the rule
   *  fired — the server detects this by comparing to the live data source
   *  view and exposes it so the UI can warn before the operator approves. */
  stale: boolean
  /** #69/#99: set by the stale sweep when the value this change was computed
   *  from has moved. Such rows are hidden unless `include_stale` is sent. */
  stale_at?: string | null
  stale_reason?: string | null
}

export type ApprovalsPage = {
  changes: PendingChangeWithContext[]
  total: number
  page: number
  page_size: number
}

export type BulkRejectResult = {
  rejected: number
  failed: { change_id: number; error: string }[]
}

export type BulkApproveDetailed = {
  approved: number
  failed: { change_id: number; error: string }[]
  /**
   * Rows the server refused to even ATTEMPT because a write gate is shut
   * (see `_bulk_approve_ids`). Distinct from `failed`: nothing was dispatched,
   * nothing was consumed, and every one of these rows is still pending and
   * retryable once the blocker clears.
   *
   * It must never be rendered as success. A fully-blocked batch comes back as
   * `{approved: 0, failed: [], blocked: [...]}`, so any caller branching only
   * on `failed.length` reports a green "0 changes approved" while nothing
   * happened — which is precisely the silent-success defect the whole approval
   * change set exists to remove, one layer up.
   *
   * Optional so an older backend (which cannot send it) still type-checks.
   */
  blocked?: { change_id: number; error: string }[]
}

export type ApprovalFilters = {
  rule_id?: number
  entity_type?: string
  from?: string  // ISO date
  to?: string    // ISO date
  status?: string
  /** #69/#99: include rows whose value already moved (hidden by default). */
  include_stale?: boolean
}

const buildApprovalsQuery = (
  f: ApprovalFilters & { page?: number; page_size?: number } = {},
): string => {
  const q = new URLSearchParams()
  if (f.rule_id !== undefined) q.set('rule_id', String(f.rule_id))
  if (f.entity_type) q.set('entity_type', f.entity_type)
  if (f.status) q.set('status', f.status)
  if (f.from) q.set('from', f.from)
  if (f.to) q.set('to', f.to)
  if (f.include_stale) q.set('include_stale', '1')
  if (f.page !== undefined) q.set('page', String(f.page))
  if (f.page_size !== undefined) q.set('page_size', String(f.page_size))
  return q.toString()
}

// Bulk methods. We attach them to a separate namespace to avoid touching
// the existing `api` const above (which other agents may also be appending
// to in parallel branches).
export const approvalsApi = {
  /** Paginated, filterable list with stale-detection metadata.
   *
   * Routes to GET /api/approvals — pagination is opted into by passing
   * `page` or `page_size`. Without them, the endpoint returns a flat list
   * (the legacy contract); with them, it returns this paginated wrapper.
   * The wrapper signal is `page` being present, so we always send it. */
  list: (filters: ApprovalFilters & { page?: number; page_size?: number } = {}) => {
    const withPage = { page: 1, page_size: 50, ...filters }
    const qs = buildApprovalsQuery(withPage)
    return req<ApprovalsPage>(`/api/approvals${qs ? `?${qs}` : ''}`)
  },

  /** Approve each change_id in series. Errors are collected per-id, not raised. */
  bulkApprove: (change_ids: number[], decided_by = 'manual_bulk') =>
    req<BulkApproveDetailed>('/api/approvals/bulk-approve', {
      method: 'POST',
      body: JSON.stringify({ change_ids, decided_by }),
    }),

  /** Reject each change_id with a shared reason. */
  bulkReject: (change_ids: number[], reason: string, decided_by = 'manual_bulk') =>
    req<BulkRejectResult>('/api/approvals/bulk-reject', {
      method: 'POST',
      body: JSON.stringify({ change_ids, reason, decided_by }),
    }),

  /** Server-side bulk approve: applies up to `max_count` records matching the
   *  same filters as the list endpoint. Hard-capped at 500 server-side. */
  approveFilter: (
    filters: ApprovalFilters,
    max_count = 500,
    decided_by = 'manual_bulk',
  ) =>
    req<BulkApproveDetailed & { matched: number }>('/api/approvals/approve-filter', {
      method: 'POST',
      body: JSON.stringify({ filters, max_count, decided_by }),
    }),
}

// ── Observability ─────────────────────────────────────────────────────────

export type HealthOut = {
  status: string
  service: string
  version: string
  uptime_s: number
  db_path: string
}

/** One row of bbpro_sync_state — per-mirrored-table BQ→local sync results. */
export type SyncStateRow = {
  local_table: string
  last_sync_at: string | null
  last_data_date: string | null
  age_hours: number | null
  row_count: number | null
  sync_status: string | null
  note: string | null
  // migration 148 (main-chain twin of ops/093). Optional: see SyncSkipReason.
  skip_reason?: SyncSkipReason | null
  // backend/sync/sync_state_sources.py. Optional: absent (an old backend) =
  // unknown, never rendered as historical on that basis alone.
  active?: boolean
}

/**
 * Mirrors backend/main.py:readiness() exactly. The endpoint has ALWAYS
 * returned this full shape (scheduler_running_flag, scheduler_heartbeat_age_s,
 * migrations_ok, enabled_rules, sync_state[]); the old type omitted them so
 * consumers silently discarded the richest signals. The /ops hub consumes the
 * whole body. getReadiness() already parses the 503 (degraded) body.
 */
export type ReadinessOut = {
  status: 'ok' | 'degraded'
  checks: {
    db: boolean
    scheduler: boolean
    scheduler_running_flag: boolean
    scheduler_heartbeat_age_s: number | null
    rules_loaded: boolean
    migrations_ok: boolean
    enabled_rules: number
    last_definitions_sync: string | null
    sync_state: SyncStateRow[]
  }
  errors: string[]
}

/** classification: 'error' (a real failure — runner._FAILURE_STATUSES) |
 *  'parked_safety' (cap_hit/needs_confirmation — the guardrail working as
 *  designed, never an Issue) | 'ok'. */
export type EngineRunClassification = 'error' | 'parked_safety' | 'ok'

export type EngineTopRecordError = {
  message: string
  count: number
}

export type EngineLastRun = {
  rule_name: string | null
  started_at: string | null
  status: string | null
  classification: EngineRunClassification
  records_matched: number | null
  records_changed: number | null
  top_record_error: EngineTopRecordError | null
}

export type EngineFailingRule = {
  rule_id: number
  rule_name: string | null
  status: string | null
  classification: EngineRunClassification
  started_at: string | null
}

export type EngineState = {
  rules_enabled: number
  rules_circuit_open: number
  last_rule_fire_at: string | null
  last_rule_fire_age_s: number | null
  last_error: {
    message: string
    at: string | null
    rule_name: string | null
  } | null
  // D-signal-taxonomy. Optional: an old backend omits both — every
  // consumer must degrade to reading last_error alone, never a false green.
  last_run?: EngineLastRun | null
  failing_rules?: EngineFailingRule[]
}

export type AlertRow = {
  id: number
  created_at: string
  severity: string | null
  source: string | null
  message: string | null
  context_json: unknown
  acknowledged_at: string | null
}

// GET /api/alerts/summary — exact unacked counts via one GROUP BY over the
// WHOLE bbpro_alerts table (backend/observability/alerts.py:summarize_unacknowledged),
// never through a LIMITed list. This is what the header bell badge and the
// SystemHealth Alerts subsystem read so neither undercounts once the table
// has more unacked rows than a list `limit` (447 unacked observed in prod).
export type AlertSummarySourceOut = {
  source: string
  count: number
  worst_severity: string
  latest_at: string | null
}

export type AlertSummaryOut = {
  total_unacked: number
  by_severity: Record<string, number>
  by_source: AlertSummarySourceOut[]
  latest_at: string | null
  /** Alerts v2 (W3.2): open BUSINESS alerts for the bell — separate from the
   *  system-health counts above. Absent/null on an older backend or before
   *  migration 231. Client: lib/alertsV2Api.ts. */
  business?: AlertsV2SummaryOut | null
  /** PR 8: open INCIDENTS — one per fingerprint, not one per row — by worst
   *  severity, sources whose producer no longer alerts left out. System
   *  health's Alerts subsystem reads this when present (lib/opsStatus.ts);
   *  absent/null on an older backend or before migration 288. */
  open_incidents?: {
    total: number
    by_severity: Record<string, number>
    /** What is open, one entry per source, worst first: `title` is the
     *  plain-English name (never the raw source code), `count` the number of
     *  open incidents of that source. */
    by_source?: OpenIncidentSource[]
  } | null
}

/** One source's open incidents in /api/alerts/summary `open_incidents`. */
export type OpenIncidentSource = {
  source: string
  title: string
  count: number
  worst_severity: string
}

export type AlertsV2SummaryOut = {
  open: number
  snoozed: number
  by_severity: Partial<Record<'info' | 'warning' | 'critical', number>>
  worst_severity: 'info' | 'warning' | 'critical' | null
  latest_at: string | null
}

/** Alerts v2 (W3.2): lib/alertsV2Api.ts sends through the one fetch choke
 *  point (ApiError, cancellation, server clock, operator scope) via this. */
export function alertsV2Req<T>(path: string, init?: RequestInit): Promise<T> {
  return req<T>(path, init)
}

// Add the observability methods onto the `api` object below. They sit
// outside the original object literal to keep the merge with sibling
// branches (B1/B2/B3) clean — they touch a single place per agent.
Object.assign(api, {
  // GET /api/health — liveness, always 200
  getHealth: () => req<HealthOut>('/api/health'),
  // GET /api/readiness — 200 if all deep checks pass, else 503
  getReadiness: async (): Promise<ReadinessOut> => {
    const res = await fetch('/api/readiness')
    // 503 is the *normal* "not ready" response; we still want the body.
    if (res.status !== 200 && res.status !== 503) {
      throw new Error(`readiness ${res.status}`)
    }
    return res.json() as Promise<ReadinessOut>
  },
  // GET /api/prometheus — Prometheus text format (moved off /api/metrics, which
  // is the Custom metrics list since the Automations redesign, engine E7-1).
  // Returns the raw body string.
  getMetricsText: async (): Promise<string> => {
    const res = await fetch('/api/prometheus')
    if (!res.ok) throw new Error(`metrics ${res.status}`)
    return res.text()
  },
  // GET /api/engine/state — internal numbers for the Health page Engine panel.
  getEngineState: () => req<EngineState>('/api/engine/state'),
  // Alerts
  listAlerts: (opts: { ack?: boolean; limit?: number; source?: string } = {}) => {
    const q = new URLSearchParams()
    if (opts.ack !== undefined) q.set('ack', String(opts.ack))
    if (opts.limit !== undefined) q.set('limit', String(opts.limit))
    if (opts.source !== undefined) q.set('source', opts.source)
    const qs = q.toString()
    return req<AlertRow[]>(`/api/alerts${qs ? `?${qs}` : ''}`)
  },
  ackAlert: (id: number) =>
    req<AlertRow>(`/api/alerts/${id}/ack`, { method: 'POST' }),
  // GET /api/alerts/summary — see AlertSummaryOut above for why the bell and
  // the SystemHealth Alerts subsystem read this instead of listAlerts.
  getAlertSummary: () => req<AlertSummaryOut>('/api/alerts/summary'),
})

// Re-export the augmented `api` type so callers see the new methods.
// The body is empty on purpose — see the note below; `Object.assign` above
// already widens the type structurally, so there is nothing to declare.
// (The directive that used to sit here named `@typescript-eslint/no-empty-
// interface`, a rule that no longer exists under that name in v8, so it
// suppressed nothing and was itself reported as an unused directive.)
declare module './api' {}

// Module-augmentation alternative: declare on the object so TS knows the keys.
// TypeScript's structural typing means `Object.assign` widening above already
// gives us the right runtime shape; for the typed surface we attach a
// supplementary interface here.
declare global {
  // intentionally empty — no global pollution
}

export interface AdBetterObservabilityApi {
  getHealth: () => Promise<HealthOut>
  getReadiness: () => Promise<ReadinessOut>
  getMetricsText: () => Promise<string>
  getEngineState: () => Promise<EngineState>
  listAlerts: (opts?: { ack?: boolean; limit?: number; source?: string }) => Promise<AlertRow[]>
  ackAlert: (id: number) => Promise<AlertRow>
  getAlertSummary: () => Promise<AlertSummaryOut>
}

export const apiObs = api as unknown as typeof api & AdBetterObservabilityApi

// ── Per-user table layout / saved views (Round-4 Area D — POWER) ────────────
//
// Server-side persistence of each user's per-table column layout + saved views,
// keyed by the table's `filterTableKey`. Mirrors the sidebar/hub-tab stores
// (cp_table_layouts, migration 085): GET hydrates, PUT upserts, and a bulk
// GET /all hydrates every table on login in one request. The frontend keeps a
// localStorage copy as the dev/offline fallback (auth off → these no-op).
//
// The `layout` blob is opaque to the backend: { views?, visibility?,
// columnOrder?, columnWidths? } — see frontend/src/lib/tableLayout.tsx.
export interface TableLayoutBlob {
  views?: unknown[]
  visibility?: Record<string, boolean>
  columnOrder?: string[]
  columnWidths?: Record<string, number>
  /** PR 9a: colKey -> the column's `widthRev` when its width was saved. */
  columnWidthRevs?: Record<string, number>
  /** PR 9b: colKey -> the column's `visibilityRev` when its show/hide was saved. */
  columnVisibilityRevs?: Record<string, number>
}
export const apiTableLayout = {
  /** All of the caller's saved table layouts, keyed by table_id. {} when
   *  unauthenticated (dev mode) — caller then uses its localStorage copy. */
  getAll: () =>
    req<{ layouts: Record<string, TableLayoutBlob> }>('/api/table-layout/all'),
  /** One table's saved layout; {} when none/unauthenticated. */
  get: (tableId: string) =>
    req<{ layout?: TableLayoutBlob; updated_at?: string | null }>(
      `/api/table-layout?table_id=${encodeURIComponent(tableId)}`),
  /** Upsert the caller's OWN layout for a table. No-op (persisted:false) in
   *  dev mode (auth off) — the localStorage copy is the source of truth then. */
  put: (tableId: string, layout: TableLayoutBlob) =>
    req<{ ok: boolean; persisted: boolean }>('/api/table-layout', {
      method: 'PUT',
      body: JSON.stringify({ table_id: tableId, layout }),
    }),
}

// ── Ops hub (product review #2) ────────────────────────────────────────────
//
// Read-only rollup sources + remediation actions for the /ops System Health
// page (frontend/src/pages/SystemHealth.tsx, model in lib/opsStatus.ts).

/** /api/ops/health/deep — the MasterData-half deep health probe.
 *  `coverage` values are fractions (0..1) for *_pct keys, counts otherwise. */
/** One typed, KIND-tagged health warning (D-signal-taxonomy). See
 *  backend/ops/main.py:build_warnings_v2's module docstring for the full
 *  counted-vs-visible contract per kind. */
export type OpsWarningV2 = {
  key: string
  kind: 'completeness' | 'work_queue' | 'awaiting_decision' | 'tripwire'
      | 'infra' | 'needs_calibration' | 'observability'
  label: string
  value: number | string | null
  unit: string | null
  action_url?: string | null
  action_label?: string | null
  since?: string
}

export type OpsDeepHealth = {
  status: 'ok' | 'warn'
  version: string
  uptime_seconds: number
  auth_enabled: boolean
  row_counts: Record<string, number | null>
  coverage: Record<string, number | Record<string, number | null> | null>
  coverage_warnings?: string[]
  // D-signal-taxonomy. Optional: an old backend omits it — every consumer
  // must degrade to coverage_warnings alone, never a false green.
  warnings_v2?: OpsWarningV2[]
  db_path?: string
  audit_chain_valid?: boolean
  /** ISO timestamp of the last nightly verify, or 'not_yet_verified'. */
  audit_chain_last_verified_at?: string | null
  scheduler_last_tick_age_s?: number | null
  bq_reachable?: boolean | null
  disk_free_gb?: number | null
  migrations_manifest_ok?: boolean
  reorder_parity_last_run?: { run_id: string; ran_at: string; row_count: number; breach_count: number } | null
  reorder_parity_breach_count?: number
}

/** 'other_half_owns' | 'upstream_missing_optional' | 'upstream_missing_required'
 *  | 'precondition_missing'. Additive (migration ops/093 / 148) — a row
 *  from before this shipped, or one this specific call site hasn't been
 *  classified yet, carries null and degrades to today's plain rendering. */
export type SyncSkipReason =
  | 'other_half_owns' | 'upstream_missing_optional'
  | 'upstream_missing_required' | 'precondition_missing'
  /** PR 19: a job the owner switched off on purpose (CLICKPAY_LINK_MATCHER=0). */
  | 'switched_off'

/** One row of md_sync_state — per-source results from the ops sync jobs. */
export type OpsSyncStateRow = {
  source: string
  last_run_at: string | null
  last_status: string | null
  rows_synced: number | null
  last_error: string | null
  // migration ops/093. Optional: see SyncSkipReason.
  skip_reason?: SyncSkipReason | null
  // backend/ops/sync/sources.py:ACTIVE_SOURCE_KEYS. Optional: an old
  // backend omits it — every consumer must treat absence as "unknown, not
  // known-inactive" (never render as historical/dimmed on that basis alone).
  active?: boolean
}

/** /api/admin/sync-jobs — Stage-4 scheduled-job ownership map merged with
 *  live scheduler state. The cutover runbook asserts
 *  live_jobs_not_in_registry is empty. */
export type SyncJobsRegistryEntry = {
  id: string
  name: string
  owner: string
  kind: string
  source: string | null
  target_tables: string[] | null
  cadence: string | null
  trigger_type: string | null
  trigger_args: Record<string, unknown> | null
  kill_switches: string[] | null
  enabled_when: string | null
  notes: string | null
  registered: boolean
  /** ClickPay V3 P0-4 — registry default overlaid by any enabled+valid DB
   *  schedule override (backend/sync_registry.py effective_schedule). */
  effective_trigger_type?: string | null
  /** Best-effort md_sync_state join (backend/main.py:7750
   *  _job_last_run_outcome) — null when job_id has no entry in
   *  _JOB_SYNC_STATE_SOURCES, or none of its mapped sources have run yet.
   *  null is "unknown", never render it as "never ran". */
  last_run: string | null
  last_outcome: string | null
  /** From the live scheduler (same source as live_jobs[].next_run); null if
   *  this job isn't currently registered on it (kill-switched / gated off,
   *  or the scheduler itself is down). */
  next_run_time: string | null
}

/** POST /api/admin/sync-jobs/{job_id}/run-now (backend/main.py:8006-8070).
 *  `started: true` is the ONLY thing this response can promise — the job
 *  runs in a background thread (mode 'direct_call') or is nudged onto the
 *  live scheduler's next tick (mode 'next_tick_nudge'); its own success or
 *  failure is not known yet at response time. Read the job's last_run /
 *  last_outcome (SyncJobsRegistryEntry above) for that — never treat a 200
 *  here as "the job worked". See frontend/src/lib/runNow.ts. */
export type SyncJobRunNowOut = {
  job_id: string
  mode: 'direct_call' | 'next_tick_nudge'
  started: boolean
  note?: string
}

export type SyncJobsOut = {
  jobs: SyncJobsRegistryEntry[]
  dynamic_jobs: unknown[]
  overlap_notes: unknown[]
  scheduler_running: boolean
  live_jobs: {
    id: string
    name: string
    next_run: string | null
    trigger: string
    owner: string | null
    in_registry: boolean
  }[]
  live_jobs_not_in_registry: string[]
  db_mode: 'unified' | 'split'
}

/** /api/admin/freshness-unified — ONE answer to "is everything fresh?"
 *  across both halves. Sections degrade independently via *_error. */
export type FreshnessUnifiedOut = {
  server_time: string
  db_mode: 'unified' | 'split'
  adbetter: {
    etl_jobs: FreshnessJob[]
    etl_jobs_error?: string
    sync_state: Omit<SyncStateRow, 'age_hours'>[]
    sync_state_error?: string
  }
  ops: {
    sync_state: OpsSyncStateRow[]
    sync_state_error?: string
  }
}

/** /api/ops/triage-summary — tiny read-only counts for the Command Center
 *  "Needs Attention" chips. Integer counts only; cheap enough for the
 *  dashboard's 30s refresh. */
export type OpsTriageSummary = {
  urgent_restocks: number
  uncovered_cogs_units: number
  urgent_threshold_days: number
}

/** POST /api/ops/sync/refresh — one synchronous per-source result. Costs are
 *  irrelevant here; `ok`/`error` reflect whether the in-process sync succeeded. */
export type SyncRefreshResult = {
  source: string
  ok: boolean
  duration_seconds: number
  error: string | null
}
export type SyncRefreshResponse = {
  results: SyncRefreshResult[]
  total_duration_seconds: number
}

/** GET /api/ops/sync/status — per-source last-synced timestamp + row count,
 *  for the "last updated X ago" labels. Source 'warehouse_stock' backs the
 *  System Health "Warehouse stock (live)" row: its age reflects
 *  md_mpl_inventory.wh_qty_as_of (when GoFlow was actually asked for the
 *  number), which is a DIFFERENT clock than 'mpl_inventory' — that source's
 *  age is when ClickPay last pulled BigQuery, not how old the warehouse
 *  figure inside it is. */
export type SyncStatusEntry = {
  source: string
  last_synced_at: string | null
  row_count: number
  age_seconds: number | null
}
export type SyncStatusResponse = {
  entries: SyncStatusEntry[]
  server_time: string
}

/** GET /api/ops/reorder-parity/defects — one row per (uid, marketplace,
 *  metric, external_source) currently breaching the parity threshold.
 *  Backs the reorder-parity drill-down under the "report-only, no baseline
 *  recorded yet" observability line on System Health. */
export type ReorderParityDefect = {
  uid: string
  marketplace: string
  metric: string
  external_source: string
  first_seen_run_id: number | null
  first_seen_at: string | null
  last_seen_run_id: number | null
  last_seen_at: string | null
  clickpay_value: number | null
  external_value: number | null
  variance_pct: number | null
}

/** GET /api/ops/qbo/status — QuickBooks Online connection state for the
 *  Settings → Integrations panel. `connected_at`/`last_sync` are null until
 *  a connection (resp. a sync) has happened at least once. */
export type QboStatus = {
  connected: boolean
  realm_id: string | null
  connected_at: string | null
  last_sync: string | null
}

/** One QuickBooks Desktop Web Connector run, as recorded by
 *  backend/ops/sync/from_qbd.py. `skipped_payees` are the payees whose
 *  transactions were NOT imported because they match no supplier or PO. */
export type QbdRunSummary = {
  complete: boolean
  seen: number
  written_total: number
  deleted: number
  skipped_total: number
  skipped_payees: { payee: string; count: number; amount: number }[]
  errors: string[]
  error_count: number
  mode: 'first_run' | 'incremental'
}

/** GET /api/ops/qbwc/status — QuickBooks Desktop (Web Connector) state. */
export type QbdStatus = {
  configured: boolean
  last_used_at: string | null
  user_name: string
  app_url: string
  company_file: string | null
  cursor: string | null
  last_session_at: string | null
  last_summary: QbdRunSummary | null
}

/** POST /api/ops/qbwc/setup — the new Web Connector password, shown once. */
export type QbdSetup = {
  user_name: string
  password: string
  qwc: string
  app_url: string
}

/** Mutating ops-hub actions carry the X-Requested-With CSRF header.
 *  Destructive ones (reset/restart) also require an operator + reason,
 *  recorded as a bbpro_alerts audit row server-side. */
export function reqMut<T>(path: string, body?: unknown): Promise<T> {
  return req<T>(path, {
    method: 'POST',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
}

export const apiOps = {
  // Read-only rollup sources.
  getDeepHealth: () => req<OpsDeepHealth>('/api/ops/health/deep'),
  getOpsSyncState: () => req<OpsSyncStateRow[]>('/api/ops/sync-state'),
  getSyncJobs: () => req<SyncJobsOut>('/api/admin/sync-jobs'),
  /** Run one registered job now, outside its schedule (System Health
   *  "Run now" button — admin-only, backend enforces the session gate).
   *  `encodeURIComponent` matches every other id-in-path call in this file
   *  (see the sweep in fix/payments-po-url-encoding). Sends the CSRF header
   *  via reqMut; see SyncJobRunNowOut for why a 200 here never means the
   *  job itself succeeded. */
  runSyncJobNow: (jobId: string) =>
    reqMut<SyncJobRunNowOut>(`/api/admin/sync-jobs/${encodeURIComponent(jobId)}/run-now`),
  getFreshnessUnified: () => req<FreshnessUnifiedOut>('/api/admin/freshness-unified'),
  /** Command Center triage counts (urgent restocks + uncovered-COGS units). */
  getTriageSummary: () => req<OpsTriageSummary>('/api/ops/triage-summary'),
  // getPnlSummaryAll REMOVED (F1) — Command Center's Sales/Net-profit 7d cards
  // now read kpis.sales / kpis.net_profit from GET /api/dashboard/kpis
  // (DashboardKpis above), the SAME complete-UTC-days window as every other
  // '7d' tile on the page, instead of building their own browser-clock window
  // via a separate ops pnl-summary call. Removing the helper (not just its
  // only call site) makes that regression impossible to reintroduce.
  // Tier 0/1 — safe, no live-money risk.
  checkpoint: () => reqMut<{ status: string }>('/api/admin/checkpoint'),
  refreshStale: () => reqMut<{ started: boolean; started_at?: string }>('/api/freshness/refresh-now'),
  recheckFreshness: () => reqMut<{ summary: unknown }>('/api/freshness/check-now'),
  refreshPpc: () => reqMut<Record<string, unknown>>('/api/ppc-freshness/refresh'),
  ackAlert: (id: number) => reqMut<unknown>(`/api/alerts/${id}/ack`),
  /** Bulk-ack every unacked alert for one source in ONE request (POST
   *  /api/alerts/ack-source) — replaces the old per-row ack loop, which at
   *  hundreds of unacked rows is the same request-storm failure class as
   *  clickpay-polling-request-storm. 200 with acknowledged:0 is normal
   *  (idempotent — acking an already-clean source is not an error). */
  ackAlertSource: (source: string) =>
    reqMut<{ source: string; acknowledged: number }>('/api/alerts/ack-source', { source }),
  /** Bulk-ack every unacked alert at/above min_severity in ONE request
   *  (POST /api/alerts/ack-bulk) — System Health's "Mark N alerts as seen"
   *  button. Deliberately never called a fix: it clears the noise, it does
   *  not resolve whatever raised the alert (see lib/fixPlan.ts). */
  ackBulk: (minSeverity: 'critical' | 'error' | 'warning' | 'info' = 'error') =>
    reqMut<{ min_severity: string; acknowledged: number }>(
      '/api/alerts/ack-bulk', { min_severity: minSeverity }),
  /** Open reorder-parity defects (worst variance first) — the drill-down
   *  behind the "report-only, no baseline yet" line on System Health. */
  getReorderParityDefects: (limit = 200) =>
    req<{ count: number; items: ReorderParityDefect[] }>(
      `/api/ops/reorder-parity/defects?limit=${limit}`),
  /** Set one feed's registry lifecycle (migration 147's mute/unmute
   *  control) — the "Fix-it / Mark as waiting / Retire" editor under a
   *  muted feed on System Health. 'active' clears reason/review_by;
   *  anything else REQUIRES both (backend 422s otherwise). */
  setFreshnessJobLifecycle: (
    jobId: string,
    body: { lifecycle: 'active' | 'blocked' | 'planned' | 'retired'; reason?: string; review_by?: string; by?: string },
  ) =>
    reqMut<{ job_id: string; lifecycle: string; reason: string | null; review_by: string | null; set_at: string; set_by: string | null }>(
      `/api/freshness/jobs/${encodeURIComponent(jobId)}/lifecycle`, body),
  ackFreshnessAlert: (id: number, by: string) =>
    reqMut<{ ack: boolean }>(`/api/freshness/alerts/${id}/ack`, { by }),
  /** Fire one ops sync job now (md_sync_state records the result). */
  syncNow: (job: string) =>
    reqMut<{ ok: boolean; job: string; queued_at: number }>(`/api/ops/sync-now/${encodeURIComponent(job)}`),
  /** Trigger a SYNCHRONOUS per-source refresh and get the real result back
   *  (ok / duration / error per source) — unlike the fire-and-forget syncNow.
   *  Backed by POST /api/ops/sync/refresh (backend/ops/routes/sync.py). This
   *  is what the System Health page's per-source Refresh buttons call.
   *  Source 'goflow_wh_live' walks every product against GoFlow's per-product
   *  inventory endpoint (no bulk endpoint exists) and takes a few minutes —
   *  use it right after receiving a PO, when the ~12h-lagged BigQuery number
   *  in 'mpl_inventory' hasn't caught up yet. */
  syncRefresh: (source: string) =>
    req<SyncRefreshResponse>('/api/ops/sync/refresh', {
      method: 'POST', body: JSON.stringify({ source }),
    }),
  /** Per-source last-synced timestamps + row counts (GET /api/ops/sync/status).
   *  Drives the "last updated X ago" labels next to the Refresh buttons. */
  getSyncStatus: (sources?: string) =>
    req<SyncStatusResponse>(
      `/api/ops/sync/status${sources ? `?sources=${encodeURIComponent(sources)}` : ''}`),
  // Tier 2 — destructive, require operator + reason (audited server-side).
  // The reset endpoint predates the hub and only persists cleared_by, so the
  // reason is folded into that string rather than dropped.
  resetBreaker: (operator: string, reason: string) =>
    reqMut<CircuitState>('/api/circuit-breaker/reset', {
      cleared_by: reason ? `${operator} — ${reason}` : operator,
    }),
  restartScheduler: (operator: string, reason: string) =>
    reqMut<{ status: string; note?: string }>('/api/admin/scheduler/restart', { operator, reason }),
  restartBackend: (operator: string, reason: string) =>
    reqMut<{ status: string; note?: string }>('/api/admin/restart', { operator, reason }),
  // QuickBooks Online — Settings → Integrations panel. Connect is a full-page
  // redirect (window.location.href) straight to /api/ops/qbo/connect, not a
  // fetch call — it hands off to Intuit's consent screen — so only status +
  // disconnect need client methods here.
  getQboStatus: () => req<QboStatus>('/api/ops/qbo/status'),
  disconnectQbo: () => reqMut<{ ok: boolean }>('/api/ops/qbo/disconnect'),
  // QuickBooks Desktop — the Web Connector polls /api/qbwc itself; the UI
  // only reads status and issues the password (admin). The .qwc file is a
  // plain same-origin download link to /api/ops/qbwc/qwc.
  getQbdStatus: () => req<QbdStatus>('/api/ops/qbwc/status'),
  setupQbd: () => reqMut<QbdSetup>('/api/ops/qbwc/setup'),
}


// ────────────────────────────────────────────────────────────────────────
// AdPro-parity entity types (Operate section: Performance subgroup)
// ────────────────────────────────────────────────────────────────────────

// All entity perf types share these aggregate metric columns.
// Values reflect whatever date range the request used (default = last 30d).
export type PerfMetrics = {
  impressions: number | null
  clicks: number | null
  spend: number | null
  sales: number | null
  orders: number | null
  acos: number | null
  ctr: number | null
  cvr: number | null
}

export type AdGroupRow = PerfMetrics & {
  /** #113 — from the shared portfolio / ad-group-name joins (backend/entities.py). */
  portfolio_id?: string | null
  portfolio_name?: string | null
  ad_group_id: string
  /** The ad group's English name from Amazon (migration 151 surfaced it on
   *  ad_group_performance). Null only for an ad group that has performance
   *  rows but no definition row yet. */
  ad_group_name: string | null
  campaign_id: string | null
  campaign_name: string | null
  campaign_asin: string | null
  avg_cpc: number | null
  inventory_signal: string | null
  /** The ad group's own status from Amazon (enabled / paused / archived,
   *  lower-case); null when it has no definitions row yet. PR 22, G5d. */
  state?: string | null
}

export type TargetRow = PerfMetrics & {
  /** #113 — from the shared portfolio / ad-group-name joins (backend/entities.py). */
  ad_group_name?: string | null
  portfolio_name?: string | null
  target_id: string
  expression: string | null
  expression_type: string | null
  campaign_id: string | null
  campaign_name: string | null
  ad_group_id: string | null
  portfolio_id: string | null
  asin: string | null
  current_bid: number | null
  state: string | null
  inventory_signal: string | null
  /** feedback #121: what this ad's clicks sold when it was a DIFFERENT one of our
   *  items (Amazon 7-day attribution). Null until the purchased-product report lands. */
  other_item_sales?: number | null
  other_item_orders?: number | null
  other_item_units?: number | null
  other_item_asins?: number | null
  // PR-7 (P5): Amazon's `getTargetBidRecommendations` output. Null until the
  // bid-rec ETL fills bbpro_bid_recommendations — the UI shows a current-vs-
  // suggested chip when suggested_bid is non-null, '—' otherwise.
  suggested_bid: number | null
  suggested_bid_low: number | null
  suggested_bid_high: number | null
  // PR-7 (P5): Keepa context columns for the bid decision (read-only).
  // buy_box_price is currently all-NULL in prod (Keepa sync gap) → renders '—'.
  review_count: number | null
  star_rating: number | null
  buy_box_price: number | null
  sales_rank: number | null
  // Bid truthfulness: 'exact' = a real bid synced from Amazon (td.current_bid);
  // 'estimated' = no synced bid, so the backend falls back to the window's avg
  // CPC for the Bid column — the FE badges this "est" and excludes it from
  // bulk-bid math (it's not a real bid to compute a % change off).
  bid_source: 'exact' | 'estimated' | null
  // Build 30 — stock cover + Buy Box for the campaign's PRIMARY ASIN. Both are
  // ASIN-level facts reached through campaign_meta, so a multi-product
  // campaign's rows show that one representative ASIN.
  days_of_supply: number | null
  current_units: number | null
  /** Fraction 0..1 over the last 30 days of offer-change events. */
  buy_box_win_rate_30d: number | null
  /** How many events that rate is computed from — under ~5 it means little. */
  bb_events_30d: number | null
}

export type KeywordRow = PerfMetrics & {
  /** #113 — from the shared portfolio / ad-group-name joins (backend/entities.py). */
  ad_group_name?: string | null
  portfolio_name?: string | null
  keyword_id: string
  keyword_text: string | null
  match_type: string | null
  campaign_id: string | null
  campaign_name: string | null
  ad_group_id: string | null
  portfolio_id: string | null
  asin: string | null
  current_bid: number | null
  state: string | null
  inventory_signal: string | null
  /** feedback #121: what this ad's clicks sold when it was a DIFFERENT one of our
   *  items (Amazon 7-day attribution). Null until the purchased-product report lands. */
  other_item_sales?: number | null
  other_item_orders?: number | null
  other_item_units?: number | null
  other_item_asins?: number | null
  // Amazon Ads `getKeywordBidRecommendations` output. Null until the SP-API
  // ETL pulls these — UI surfaces a "(low–high)" hint next to the bid cell
  // and an Apply button when both are non-null.
  suggested_bid: number | null
  suggested_bid_low: number | null
  suggested_bid_high: number | null
  // PR-7 (P5): Keepa context columns for the bid decision (read-only).
  // buy_box_price is currently all-NULL in prod (Keepa sync gap) → renders '—'.
  review_count: number | null
  star_rating: number | null
  buy_box_price: number | null
  sales_rank: number | null
  // Bid truthfulness — see the identical field on TargetRow.
  bid_source: 'exact' | 'estimated' | null
  // Build 30 — see the identical block on TargetRow.
  days_of_supply: number | null
  current_units: number | null
  buy_box_win_rate_30d: number | null
  bb_events_30d: number | null
}

export type SearchTermRow = Omit<PerfMetrics, 'ctr'> & {
  /** #113 — from the shared portfolio / ad-group-name joins (backend/entities.py). */
  ad_group_name?: string | null
  portfolio_id?: string | null
  portfolio_name?: string | null
  search_term: string
  keyword_text: string | null
  match_type: string | null
  // Unified TRIGGER id (E3): the representative ppc_search_terms.keyword_id for
  // this query (any_value — the query rolls up by `query` only). Used to deep-
  // link the Target cell to /search-terms?keyword_id=<id> (filters on the RAW
  // column server-side, so the representative-id display lossiness is harmless).
  keyword_id: string | null
  campaign_id: string | null
  campaign_name: string | null
  ad_group_id: string | null
  asin: string | null
  // PPC A1 — dedup signal. 'harvested' = already in AdBetter's harvest log;
  // 'is_keyword' = already a keyword def in this campaign; null = fresh.
  harvest_status: 'harvested' | 'is_keyword' | null
  // SM-W1.6 — targeting / negation flags (backend/entities.py
  // _search_term_flag_parts). null = the source table is not synced, so the
  // backend could not tell (never a confident "no").
  targeted_in_account?: boolean | null
  targeted_in_campaign?: boolean | null
  negated_campaign?: boolean | null
  negated_adgroup?: boolean | null
  // Amazon API context (row 11). Not displayed — forwarded verbatim on the
  // negate / harvest records so the handlers can resolve which endpoint to
  // call. Without them every on-demand apply errored after a green preview.
  profile_id: string | null
  ad_type: string | null
}

// ── PPC A1 — on-demand search-term actions (negate / harvest) ──────────────
export type SearchTermActionType = 'negate_search_term' | 'harvest_search_term'

export type SearchTermActionRecord = {
  search_term: string
  campaign_id?: string | null
  ad_group_id?: string | null
  keyword_id?: string | null
  profile_id?: string | null
  ad_type?: string | null
  /** The product the term converts for (harvest: a new campaign advertises it).
   *  The Negate modal strips it before the POST; the harvest dialog sends it. */
  asin?: string | null
  /** DISPLAY ONLY — SearchTermActionModal shows which campaign / ad group each
   *  write lands in, and strips both before the POST (the server's record model
   *  has no such fields). */
  campaign_name?: string | null
  ad_group_name?: string | null
  /** DISPLAY ONLY — the source campaign's portfolio (null = none); the harvest
   *  dialog compares it with the destination's. */
  portfolio_name?: string | null
  /** DISPLAY ONLY — the keyword that found the term ("found by:" in the harvest
   *  preview); never sent. */
  source_match_type?: string | null
  source_keyword_text?: string | null
}

export type SearchTermActionRequest = {
  action_type: SearchTermActionType
  params?: Record<string, unknown>
  records: SearchTermActionRecord[]
  dry_run: boolean
}

export type SearchTermActionResult = {
  entity_type: string
  entity_id: string
  status: 'would_apply' | 'applied' | 'skipped' | 'requires_manual_approval' | 'error'
  error_message: string | null
  before: Record<string, unknown>
  after: Record<string, unknown>
}

export type SearchTermActionResponse = {
  action_type: SearchTermActionType
  dry_run: boolean
  // Whether a live (dry_run=false) apply would actually reach Amazon right now.
  live_writes_enabled: boolean
  count: number
  summary: Partial<Record<SearchTermActionResult['status'], number>>
  results: SearchTermActionResult[]
}

export type ProductAdRow = PerfMetrics & {
  ad_id: string
  asin: string | null
  sku: string | null
  campaign_id: string | null
  ad_group_id: string | null
  campaign_name: string | null
  inventory_signal: string | null
  // 2026-06-01: scraped main-image URL, populated by bbpro_scraped_listings.
  // Null until the scraper has run for the ASIN; UI falls back to a Package
  // placeholder.
  image_url: string | null
}

// ── /api/product-ads-rollup nested response shapes ───────────────────────
// Two-level expand on /product-ads: ASIN parent → campaigns → ad-groups.
// `state` on ad-groups uses campaign-state as proxy (no per-ad-group state
// mirror in DuckDB today). `state_mix` aggregates that proxy at parent level.
export type ProductAdRollupAdGroup = PerfMetrics & {
  ad_group_id: string | null
  campaign_id: string | null
  marketplace: string | null
  state: string | null
}

export type ProductAdRollupCampaign = PerfMetrics & {
  campaign_id: string | null
  campaign_name: string | null
  campaign_state: string | null
  marketplace_set: string[]
  ad_groups: ProductAdRollupAdGroup[]
}

export type ProductAdRollupRow = PerfMetrics & {
  asin: string
  sku: string | null
  /** Product title — joined from the catalog (products.title), first
   *  non-empty per ASIN across the window's leaf rows. */
  title: string | null
  image_url: string | null
  inventory_signal: string | null
  marketplace_set: string[]
  state_mix: { enabled: number; paused: number; total: number }
  campaigns: ProductAdRollupCampaign[]
}

export type AsinRow = PerfMetrics & {
  asin: string
  sku: string | null
  uid: string | null
  title: string | null
  brand: string | null
  category: string | null
  price: number | null
  cogs: number | null
  be_acos: number | null
  active: boolean | null
  avg_cpc: number | null
  current_units: number | null
  days_of_supply: number | null
  inventory_signal: string | null
}

export type PlacementRow = PerfMetrics & {
  /** #113 — from the shared portfolio / ad-group-name joins (backend/entities.py). */
  portfolio_id?: string | null
  portfolio_name?: string | null
  placement: string
  campaign_id: string | null
  campaign_name: string | null
  campaign_asin: string | null
  /** Current placement bid adjustment for this row's bucket, as a fraction
   *  (0.50 = +50%, range 0..9). 0 is a real synced value — Amazon's default,
   *  no boost. null only when modifier_state is 'na' or 'unknown'. */
  current_modifier: number | null
  /** How to read current_modifier:
   *  'set' = synced value for this bucket (0% included);
   *  'na' = a placement adjustment does not exist here (the Off Amazon
   *         bucket, or an SB/SD campaign);
   *  'unknown' = this campaign hasn't been synced yet. */
  modifier_state: 'set' | 'na' | 'unknown' | null
  /** "<campaign>:<tos|ros|pp>" — the ONE key this bucket is matched on across
   *  the app, whichever spelling the row that wrote an edit used. Computed
   *  server-side by the same normalizer the apply path runs, so the browser
   *  never re-derives the placement→bucket ladder. */
  canonical_entity_id?: string | null
  /** Row-native marketplace (already returned by the backend — a.marketplace). */
  marketplace: string | null
  /** Campaign-grain context, repeated across every placement row for that
   *  campaign — default-hidden in the table (see item 6). */
  campaign_state: string | null
  daily_budget: number | null
}

export type HourlyRow = {
  hour: string             // ISO timestamp
  asin: string | null
  marketplace: string | null
  units: number | null
  revenue: number | null
  page_views: number | null
  sessions: number | null
  conv_rate: number | null
  aov: number | null
  cogs: number | null
  gross_profit: number | null
  margin: number | null
  // Phase 3: filterable split-out time dims + intraday reconciliation marker.
  hour_of_day: number | null    // 0-23
  day_of_week: number | null    // 0=Mon .. 6=Sun
  is_preliminary: boolean | null
}

// ── AMS-backed live BQ row types (2026-05-26) ────────────────────────────
// Returned by /api/budget-pacing + /api/hourly/sp-keyword. AMS data lags
// 30-90 min upstream; the runner+API server-cache adds 60s. These rows
// surface ``staleness_minutes`` (budget pacing) so the UI can show a
// yellow badge if the AMS feed is stale.

export type BudgetPacingRow = {
  campaign_id: string
  campaign_name: string | null
  state: string | null
  advertising_product_type: 'sp' | 'sb' | 'sd' | null
  daily_budget: number | null
  used_pct: number | null
  spent_today: number | null
  remaining_today: number | null
  observed_at: string | null  // ISO timestamp
  local_date: string | null
  staleness_minutes: number | null
}

// #68: GET /api/budget-pacing now returns an envelope, not a bare array — a
// used_pct-sorted LIMIT 500 used to render ~93% of campaigns' Pacing cells as
// a blank '—', indistinguishable from a genuine BigQuery outage. `degraded`
// (non-null only on a BQ failure) lets the frontend tell "no AMS row for this
// campaign" apart from "the pacing feed itself is down".
export type BudgetPacingResponse = {
  items: BudgetPacingRow[]
  count: number
  degraded: string | null
  truncated: boolean
}

/** One campaign's same-day Marketing Stream figures (GET
 *  /api/campaigns/today-live, 2026-09-18). Native currency, like the
 *  report-driven Spend column. `acos` is null until the campaign has BOTH
 *  spend and attributed sales today — never a fake 0%. */
export type CampaignTodayLiveRow = {
  spend: number
  sales: number
  orders: number
  acos: number | null
}

/** GET /api/campaigns/today-live — the Campaigns table's amber "Today"
 *  overlay, keyed by campaign_id so the page merges it like the pacing map.
 *  A campaign with no stream row today (every CA campaign — the stream is
 *  subscribed for the US advertiser only) is simply ABSENT, not $0.
 *  `degraded` is 'bq_error' when either AMS leg failed this refresh — `rows`
 *  is then empty and the page must render 'n/a', not $0. */
export type CampaignsTodayLiveResponse = {
  date: string
  day_basis: string
  window_start_utc: string
  window_end_utc: string
  as_of: string
  spend_basis: string
  spend_through_utc: string | null
  sales_through_utc: string | null
  degraded: string | null
  note: string | null
  count: number
  rows: Record<string, CampaignTodayLiveRow>
}

export type SpKeywordHourlyRow = {
  event_hour_utc: string | null  // ISO timestamp
  hour_of_day_utc: number | null
  local_date: string | null
  campaign_id: string | null
  ad_group_id: string | null
  keyword_id: string | null
  keyword_text: string | null
  match_type: string | null
  impressions: number | null
  clicks: number | null
  cost: number | null
  orders_7d: number | null
  sales_7d: number | null
  units_7d: number | null
  acos_7d: number | null
  roas_7d: number | null
}

/** Per-hour AD rollup across keywords (Round-4 Area B PPC subtab). Sourced
 *  from v_sp_keyword_hourly (AMS/BQ), GROUP BY hour. `*_7d` are 7-day
 *  ATTRIBUTION-window values, not same-hour ad sales. */
export type HourlyPpcRow = {
  hour: string | null          // ISO timestamp (from event_hour_utc)
  impressions: number | null
  clicks: number | null
  cost: number | null
  sales_7d: number | null
  orders_7d: number | null
  acos_7d: number | null       // cost / sales_7d (server-derived)
  ctr: number | null           // clicks / impressions
  roas_7d: number | null       // sales_7d / cost
  // PR-7 (P5): present on the unified PPC channel rows — a derived 'still
  // settling' marker (today/yesterday UTC, the 2-day rule mirroring
  // hourly_perf). Absent on the /api/hourly/ppc rollup, so it's nullable.
  is_preliminary?: boolean | null
}

// ── Unified hourly (F3 — Hourly view consolidation) ──────────────────────
// One hour-grained dataset sliced 3 ways (All / PPC Hourly / Organic Hourly)
// by /api/hourly/unified. The total side comes from hourly_perf (DuckDB); the
// ppc_* overlay from AMS (BQ). `ppc_available` is false when the AMS feed is
// down/empty — the UI then shows total-only and a "no ad data yet" note (the
// rest of the row still renders). All ad/organic figures are nullable so a
// partial/empty AMS window renders cleanly and lights up as data lands.

/** A row of the All / Organic channels — total perf + ad overlay. */
export type HourlyUnifiedRow = {
  hour: string | null
  asin: string | null
  marketplace: string | null
  units: number | null
  revenue: number | null
  page_views: number | null
  sessions: number | null
  conv_rate: number | null
  aov: number | null
  cogs: number | null
  gross_profit: number | null
  margin: number | null
  hour_of_day: number | null
  day_of_week: number | null
  is_preliminary: boolean | null
  // Ad overlay (null when AMS has no row for this hour).
  ppc_cost: number | null
  ppc_sales: number | null
  ppc_units: number | null
  acos: number | null            // ppc_cost / ppc_sales
  tacos: number | null           // ppc_cost / total revenue
  // Organic channel only (total − 7d-attributed ads, clamped ≥ 0; approx).
  organic_revenue?: number | null
  organic_units?: number | null
  organic_aov?: number | null
  organic_approx?: boolean
}

/** A row of the PPC channel — per-hour ad rollup with keyword drill-down. */
export type HourlyUnifiedPpcRow = HourlyPpcRow & {
  keywords: SpKeywordHourlyRow[]
}

export type HourlyUnifiedResponse = {
  channel: 'all' | 'organic'
  ppc_available: boolean
  /** F3: true when the raw per-keyword-hour feed backing the PPC overlay hit
   *  its 5,000-row cap for this window — the per-hour totals above (`ppc_cost`
   *  etc.) are unaffected (GROUP-BY-hour, uncapped); only a keyword-level
   *  drill-down could be partial. Present on every channel for a consistent
   *  shape, though only the 'ppc' channel's own keyword drill-down can
   *  actually truncate. */
  ppc_truncated?: boolean
  rows: HourlyUnifiedRow[]
}

export type HourlyUnifiedPpcResponse = {
  channel: 'ppc'
  ppc_available: boolean
  /** F3: true when the raw keyword-hour feed backing each hour's `keywords[]`
   *  drill-down hit its 5,000-row cap — the per-hour totals themselves come
   *  from an uncapped GROUP BY and are always complete. */
  ppc_truncated?: boolean
  /** PR 12b: the Pacific days actually covered when a from/to was sent, and whether the
   *  stream's 7-day retention clipped it. */
  window?: { from: string; to: string }
  window_clamped?: boolean
  window_empty?: boolean
  rows: HourlyUnifiedPpcRow[]
}

/** Aggregated hourly bucket (across ASINs unless asin filter set). */
export type HourlyTimeseriesPoint = {
  hour: string
  units: number | null
  revenue: number | null
  page_views: number | null
  sessions: number | null
  conv_rate: number | null
  aov: number | null
  gross_profit: number | null
  margin: number | null
}

/** SKU row returned by GET /api/skus. Source view ``sku_performance``
 *  already carries 7d and 30d windowed PPC + inventory signals; no
 *  from/to window param needed at the caller. */
export type SkuRow = {
  sku: string
  asin: string | null
  uid: string | null
  title: string | null
  brand: string | null
  category: string | null
  marketplace: string | null
  price: number | null
  cogs: number | null
  active: boolean | null
  impressions_30d: number | null
  clicks_30d: number | null
  spend_30d: number | null
  sales_30d: number | null
  orders_30d: number | null
  acos_30d: number | null
  ctr_30d: number | null
  cvr_30d: number | null
  impressions_7d: number | null
  clicks_7d: number | null
  spend_7d: number | null
  sales_7d: number | null
  orders_7d: number | null
  acos_7d: number | null
  current_units: number | null
  fba_units: number | null
  fba_inbound: number | null
  daily_burn: number | null
  days_of_supply: number | null
  inventory_signal: string | null
  acos_7d_vs_30d_pct_change: number | null
  review_count: number | null
  star_rating: number | null
  sales_rank: number | null
  list_price: number | null
}

/** Per-marketplace signal chip surfaced on parent Products rows.
 *  Emitted by the backend when an ASIN's SKUs have active inventory signals;
 *  the UI renders one chip per (marketplace, worst-signal) pair with a
 *  "+N" badge when sku_count_with_signal > 1. */
export type SignalStackEntry = {
  marketplace: string
  worst_signal: string
  sku_count_with_signal: number
}

/** Inline-expandable child row under a Products parent.
 *  `kind: 'sku'` rows are real (SKU × marketplace) tuples with full perf;
 *  `kind: 'unattributed'` rows aggregate PPC spend that lacks a SKU
 *  attribution (SP rows with null SKU + all SD spend), keeping the
 *  invariant `parent.spend === SUM(children.spend)`. */
export type ProductChildRow = {
  kind: 'sku' | 'unattributed'
  asin: string
  sku: string | null
  marketplace: string | null
  active: boolean | null
  current_units: number | null
  days_of_supply: number | null
  inventory_signal: string | null
  impressions: number | null
  clicks: number | null
  spend: number | null
  sales: number | null
  orders: number | null
  acos: number | null
  cvr: number | null
  ctr: number | null
  spend_7d: number | null
  sales_7d: number | null
  orders_7d: number | null
  acos_7d: number | null
  spend_30d: number | null
  sales_30d: number | null
  orders_30d: number | null
  acos_30d: number | null
}

/** Catalog row returned by GET /api/products. When the caller passes `from`/`to`,
 *  the response carries PPC aggregates over the window, fixed 7d/30d windows,
 *  the per-MP signal stack, the highest-spend SKU + "+N" badge count, and a
 *  nested `children` array of (SKU, marketplace) rows. Catalog-only callers
 *  (no from/to) get null perf fields and empty children. */
export type ProductRow = {
  asin: string
  sku: string | null
  uid: string | null
  title: string | null
  brand: string | null
  category: string | null
  price: number | null
  // PR 2 (one COGS) — cost, break-even and fees come from the product facts
  // (backend/product_facts.py): the landed cost the P&L, Restock and every
  // automation use, each with the tier it came from. The basis/estimate
  // fields are null on a split-mode (no facts) backend; lib/productFacts.ts
  // turns them into the cell copy.
  cogs: number | null
  /** 'po' | 'fifo' | 'fifo_seed' | 'catalog' | 'estimate'. */
  cogs_basis?: string | null
  cogs_is_estimate?: boolean | null
  /** Plain-English source, e.g. "Received PO 1668". */
  cogs_label?: string | null
  /** Break-even ACoS with fees: (price − cost − referral − FBA) ÷ price. */
  be_acos: number | null
  be_is_estimate?: boolean | null
  /** FBA fee per unit, and its basis ('sp_api' | 'manual' | 'catalog' | 'default'). */
  fba_fee?: number | null
  fba_fee_basis?: string | null
  /** Referral fee as a fraction of the price, and its basis. */
  referral_pct?: number | null
  referral_basis?: string | null
  active: boolean | null
  marketplace: string | null
  /** FBA units — the Amazon US inventory row (Amazon CA when the page is
   *  scoped to CA), no longer the US + CA snapshot sum. */
  fba_qty: number | null
  /** PR 24: FBA stock = available + reserved + in transfer (inbound separate).
   *  Server-computed (product facts); absent on an older server. */
  fba_stock?: number | null
  /** Units on their way into Amazon warehouses (Amazon US row; null on CA). */
  fba_inbound?: number | null
  current_units: number | null
  days_of_supply: number | null
  inventory_signal: string | null
  impressions: number | null
  clicks: number | null
  spend: number | null
  sales: number | null
  orders: number | null
  acos: number | null
  ctr: number | null
  cvr: number | null
  avg_cpc: number | null
  // Fixed-window perf (read by the "Show 7d/30d" header toggle).
  spend_7d: number | null
  sales_7d: number | null
  orders_7d: number | null
  acos_7d: number | null
  spend_30d: number | null
  sales_30d: number | null
  orders_30d: number | null
  acos_30d: number | null
  // Highest-spend SKU within picked window + count of SKUs.
  highest_spend_sku: string | null
  sku_count: number
  // Per-MP worst-signal chips. Null when no SKUs under this ASIN have
  // active signals.
  signal_stack: SignalStackEntry[] | null
  // v6 goal binding (2026-06-01). `goal_preset` falls back to 'Normal' for
  // unassigned ASINs via v_asin_effective_goal. Rendered as a chip on the
  // parent row; clicking drills to /products/:asin Settings card.
  goal_preset: 'Growth' | 'Normal' | 'Preserve'
  goal_rules_enabled: boolean
  goal_boost_active: boolean | null
  // Goals-in-Products (task #13): the derived target ACoS + how it was sourced
  // ('override' | 'boost' | 'derived' | 'preset') + lock state, for the row
  // chip + a locked badge.
  goal_target_acos: number | null
  goal_target_acos_source: string | null
  goal_locked_until: string | null
  // Price basis for the Price column: 'marketplace' | 'catalog' | 'derived'
  // (the 90-day average selling price — an estimate). Null in split mode.
  price_source: string | null
  // Product image — md_products.main_image_url first (PR 2).
  image_url: string | null
  // PR-7 (P5): Keepa attributes on the ASIN row (parity with the SKU page).
  // review_count/star_rating/sales_rank are real for ~327/424 ASINs;
  // buy_box_price is currently all-NULL in prod → renders '—'.
  review_count: number | null
  star_rating: number | null
  buy_box_price: number | null
  sales_rank: number | null
  // Inline-expandable detail rows. Always present (empty array when no
  // window or no PPC data).
  children: ProductChildRow[]
}

export type ProductMaster = {
  asin: string
  sku: string | null
  /** PR 2 — the catalog product the page's facts come from (the identity
   *  bridge's primary UID for this ASIN). */
  uid: string | null
  /** The listing's own UID (products.uid) — what the Settings card's UID field
   *  edits. Differs from `uid` on an ASIN two catalog products share. Absent on
   *  older cached responses. */
  listing_uid?: string | null
  title: string | null
  brand: string | null
  /** PR 2 — the product manager who owns this product. */
  manager?: string | null
  product_type: string | null
  category: string | null
  active: boolean | null
  marketplace: string | null
  image_url: string | null
  // PR 2 (RC9) — real feeds from the catalog record. The fields that had NO
  // feed (parent ASIN, manufacturer, colour, …) and the two fabricated ones
  // (selling status, seller company) are no longer sent.
  /** "L × W × H cm, W kg". */
  item_dimensions: string | null
  /** The shipping carton, "L × W × H cm, W kg". */
  carton_dimensions?: string | null
  package_quantity: number | null
  /** True when the product sells through FBA (inventory feed). */
  is_fba: boolean | null
  current_state: string | null
  hidden: boolean | null
}

/** The product page's money block (PR 2): every value from the product facts
 *  with its basis, label and estimate flag. Split mode (no facts) sends the
 *  legacy price and cost with null bases and false flags. */
export type ProductFinancial = {
  uid: string | null
  /** md_products.version — the If-Match for a catalog landed cost edit. */
  md_version: number | null
  price: number | null
  price_basis: string | null
  price_label: string | null
  price_is_estimate: boolean
  cost: number | null
  cost_basis: string | null
  cost_label: string | null
  cost_is_estimate: boolean
  cost_po_number: string | null
  /** True when the cost is edited on the catalog record (not on a PO). */
  cost_editable: boolean
  referral_usd: number | null
  referral_pct: number | null
  referral_basis: string | null
  fba_fee: number | null
  fba_basis: string | null
  fees_label: string | null
  fees_are_estimate: boolean
  break_even_acos: number | null
  break_even_is_estimate: boolean
  /** Amazon's list ("was") price — only sent when it is at or above the price. */
  list_price: number | null
}

export type ProductInventory = {
  fba_qty: number
  /** PR 24: the one FBA stock definition and its parts (product facts). */
  fba_stock?: number | null
  fba_inbound?: number | null
  fba_transfer?: number | null
  fba_reserved?: number | null
  fba_available?: number | null
  /** The sentence that defines FBA stock, from the server. */
  definition?: string | null
  fbm_qty: number
  combined_qty: number
  local_qty: number
  days_of_supply: number | null
  first_stock_date: string | null
}

export type ProductReviews = {
  rating: number
  count: number
  previous_count: number | null
  breakdown: { stars: number; pct: number }[]
  /** 2026-07-08: which table the rating/count came from — 'keepa' (the live,
   *  populated bbpro_asin_product_attributes source) or 'scraped' (the
   *  legacy bbpro_scraped_listings fallback, effectively dead in prod today).
   *  Absent on older cached responses; render '—'/omit the badge then. */
  source?: 'keepa' | 'scraped'
} | null

export type ProductCategoryRank = {
  category: string
  rank: number
}[] | null

/** 2026-07-08: SP-API listing copy (bullets/description/backend keywords),
 *  100% populated in prod but never surfaced on the product detail page
 *  before now. */
export type ProductListingContent = {
  bullets: string[]
  description: string | null
  backend_keywords: string | null
}

export type ProductEconomics = {
  /** PR 2 — the facts break-even (the one the goal ladder derives the target
   *  from and the Bid Optimizer executes on). */
  break_even_acos?: number | null
  /** Deprecated alias of break_even_acos (kept one release). */
  gross_margin_pct: number | null
  referral_pct: number | null
  fba_fee: number | null
  target_acos: number | null
  target_acos_source: string | null
  target_acos_override: number | null
  profit_buffer: number | null
  /** The per-ASIN profit-buffer override, or null when the preset's applies. */
  profit_buffer_override?: number | null
  goal_preset: string | null
  /** PR 2 (RC8) — Sponsored-Products-attributed net profit over 30 days; not
   *  the P&L's net profit (that one is on Marketplaces › P&L). */
  ad_attributed_net_profit_30d?: number | null
  /** Deprecated alias of ad_attributed_net_profit_30d (kept one release). */
  net_profit_30d: number | null
  net_margin_pct_after_ads: number | null
}

export type ProductDetail = {
  asin: string
  master: ProductMaster
  financial: ProductFinancial
  economics?: ProductEconomics
  inventory: ProductInventory
  reviews: ProductReviews
  category_rank: ProductCategoryRank
  /** Optional — absent on older cached responses; treat as empty. */
  listing?: ProductListingContent
  tags: string[]
  /** `source`/`rank` land with migration 136 and are optional so a response
   *  from a pre-136 backend still type-checks. Soft-deleted competitors are
   *  filtered server-side and never appear here. */
  competitor_asins: {
    asin: string; note: string | null
    source?: string; rank?: number | null
  }[]
  custom_attrs: { key: string; value: string | null }[]
  parent_product: { id: number; asin: string } | null
}

// ── Product → Keywords tab (2026-07-08) ─────────────────────────────────
// Owner ask: see every PPC keyword that drove sales for THIS product, its own
// CPC (spend/clicks) over the window, then drill into one keyword's DAILY
// performance + a CPC-band summary. Amazon exposes keyword performance at
// DAILY grain only — no order-level keyword attribution — so the drill-in is
// deliberately "Daily performance", never "Orders".
export type ProductKeywordRow = {
  keyword_id: string
  keyword_text: string | null
  match_type: string | null
  campaign_id: string | null
  campaign_name: string | null
  ad_group_id: string | null
  asin: string | null
  current_bid: number | null
  state: string | null
  impressions: number | null
  clicks: number | null
  spend: number | null
  sales: number | null
  orders: number | null
  acos: number | null
  ctr: number | null
  cvr: number | null
  /** spend / clicks over the window — the "separate CPC for this keyword" ask. */
  cpc: number | null
}

export type ProductKeywordDailyRow = {
  date: string
  impressions: number | null
  clicks: number | null
  spend: number | null
  sales: number | null
  orders: number | null
  /** The bid that was on record for this keyword on this date — the last
   *  snapshot at or before it (ppc_definitions_history has gaps, and a bid is
   *  a step function). An INFERENCE, not a daily reading. null before the
   *  keyword's first snapshot, and null when that snapshot carried no bid. */
  bid: number | null
  cpc: number | null
  cvr: number | null
  acos: number | null
}

/** One CPC band (roughly a quartile of the priced days, ranked by CPC). The
 *  band's cvr/avg_cpc/acos are recomputed from the SUMMED clicks/spend/sales/
 *  orders in the band (weighted), not averaged per-day, so a few low-volume
 *  days can't skew the headline number. */
export type ProductKeywordCpcBand = {
  band: number
  cpc_min: number
  cpc_max: number
  days: number
  clicks: number
  spend: number
  sales: number
  orders: number
  cvr: number | null
  avg_cpc: number | null
  acos: number | null
}

/** The drill-in's bands: the shared shape PLUS the bid that bought each
 *  band's CPC (#40). Separate from `ProductKeywordCpcBand` because ONLY
 *  /keywords/{id}/daily can fill these — the batch bands endpoint never joins
 *  ppc_definitions_history (~20.6M rows, once per keyword on a product page),
 *  so it returns the shared shape and these keys are absent, not null. Keeping
 *  them off the shared type is what stops a table column being bound to a
 *  field that would render an em-dash forever.
 *
 *  A band is a SET of days and they can sit on different bids, so this is the
 *  RANGE across the band's days that had a bid on record (`bid_min ===
 *  bid_max` is the common case; the UI collapses it to one figure) and
 *  `bid_days` is how many of `days` those were. */
export type ProductKeywordCpcBandWithBid = ProductKeywordCpcBand & {
  bid_min: number | null
  bid_max: number | null
  bid_days: number
}

export type ProductKeywordDailyResponse = {
  keyword_id: string
  keyword_text: string | null
  match_type: string | null
  campaign_name: string | null
  asin: string
  daily: ProductKeywordDailyRow[]
  /** First date this keyword has a bid ON RECORD — the honest coverage floor
   *  for the daily `bid` column. A snapshot row can exist carrying a NULL bid,
   *  and those do NOT count: ~1% of keywords have snapshots and not one bid
   *  among them, and for those this is null rather than a date on which the
   *  Bid column still renders an em-dash. null therefore means "no bid history
   *  at all". Server-derived rather than hard-coded: the snapshot table is
   *  pruned on a 400d retention, so a baked-in date would quietly go wrong. */
  bid_history_from: string | null
  cpc_bands: ProductKeywordCpcBandWithBid[]
}

/** Batch CPC bands for EVERY keyword advertising an ASIN (owner feedback
 *  #13) — feeds the table-wide band column on the product page's Keywords
 *  tab in one request instead of one drill-in call per row. `bands` is
 *  keyed by keyword_id; a keyword with no clicked day in the window is
 *  simply absent. Same `_cpc_bands` math as the drill-in endpoint. */
export type ProductKeywordCpcBandsResponse = {
  asin: string
  bands: Record<string, ProductKeywordCpcBand[]>
}

export type ListQuery = {
  search?: string
  sort?: string
  dir?: 'asc' | 'desc'
  limit?: number
  offset?: number
  /** Inclusive lower bound of the perf window, ISO YYYY-MM-DD. Default: 30d ago. */
  from?: string
  /** Inclusive upper bound of the perf window, ISO YYYY-MM-DD. Default: today. */
  to?: string
}

/** One row of GET /api/targets-all (PR 3, G8): a keyword or a product/auto
 *  target, in one shape (backend list_paging.COLUMNS["target_union"]).
 *  `row_key` (`keyword:<id>` / `target:<id>`) is unique across both grains —
 *  keyword and target ids can collide — and is the table key, the tag key and
 *  the server's sort tie-break. */
export type TargetUnionRow = PerfMetrics & {
  row_key: string
  entity_type: 'keyword' | 'target'
  /** keyword_id or target_id. */
  id: string
  /** Broad / Phrase / Exact (a raw match type verbatim, 'Keyword' when none)
   *  for keywords; ASIN / Category / Auto / Target for targets. */
  type_label: string
  /** The keyword text or the target expression (the id when neither). */
  target: string
  match_type: string | null
  expression_type: string | null
  campaign_id: string | null
  campaign_name: string | null
  ad_group_id: string | null
  ad_group_name: string | null
  portfolio_id: string | null
  portfolio_name: string | null
  /** The campaign's ASIN (campaign_meta). */
  asin: string | null
  current_bid: number | null
  bid_source: 'exact' | 'estimated' | null
  state: string | null
  inventory_signal: string | null
  /** feedback #121: what this ad's clicks sold when it was a DIFFERENT one of our
   *  items (Amazon 7-day attribution). Null until the purchased-product report lands. */
  other_item_sales?: number | null
  other_item_orders?: number | null
  other_item_units?: number | null
  other_item_asins?: number | null
  /** spend ÷ clicks (null with no clicks) — a server column, so the tab can
   *  sort by it across every row. */
  cpc?: number | null
  days_of_supply: number | null
  current_units: number | null
  buy_box_win_rate_30d: number | null
  bb_events_30d: number | null
  suggested_bid: number | null
  suggested_bid_low: number | null
  suggested_bid_high: number | null
  review_count: number | null
  star_rating: number | null
  buy_box_price: number | null
  sales_rank: number | null
}

/** GET /api/targets-all query: the window, the drill scope, the Tag select
 *  (`''` every row, a tag id, or `__untagged__`) and the paging params. */
export type TargetsAllQuery = {
  from?: string; to?: string
  campaign_id?: string; ad_group_id?: string; asin?: string; target_id?: string
  portfolio_id?: string; portfolio_name?: string
  tag?: string
  search?: string; sort?: string; dir?: 'asc' | 'desc'
  limit?: number; offset?: number
  filter?: string
}

/** Query for a server-paged list endpoint (`envelope=1`). `filter` is a
 *  FilterSpec serialized to JSON — the server rejects any column or op it
 *  cannot express EXACTLY, so a filter chip can never appear applied while
 *  the rows behind it are unfiltered. */
export type PagedQuery = ListQuery & {
  envelope: true
  filter?: string
}

/** Envelope returned by a list endpoint called with `envelope=1`.
 *  Mirrors PagedResponse in backend/api/list_paging.py.
 *
 *  Same vocabulary as the ops list endpoints (Build 49): `count` is this page,
 *  `total` is what the filters match in the database ignoring limit/offset.
 *  `totals` carries sum + non-null count per numeric column, over the WHOLE
 *  filtered set — the totals footer must describe every matching row, not the
 *  100 currently on screen. */
export type Paged<Row> = {
  count: number
  total: number
  offset: number
  items: Row[]
  totals: Record<string, { sum: number | null; n: number }>
  /** Search terms only (B12): what the keyword / target reports say the same
   *  scope spent. Absent from other lists and from an older server. */
  coverage?: SearchTermCoverage | null
}

/** GET /api/campaigns query (SM W1.1: the Campaigns grid is server-paged). */
export type CampaignListOpts = {
  limit?: number; offset?: number; sort?: string; dir?: 'asc' | 'desc';
  search?: string; filter?: string;
  ad_type?: string; portfolio_id?: string; portfolio_name?: string;
  campaign_id?: string; state?: string; marketplace?: string;
  from?: string; to?: string; include_inactive?: boolean;
}

/** The paging half of a campaigns query: offset / sort / dir / search / filter. */
function campaignPagingParams(q: URLSearchParams, opts?: CampaignListOpts): void {
  if (opts?.offset) q.set('offset', String(opts.offset))
  if (opts?.sort) q.set('sort', opts.sort)
  if (opts?.dir) q.set('dir', opts.dir)
  if (opts?.search) q.set('search', opts.search)
  if (opts?.filter) q.set('filter', opts.filter)
}

// PR 5c — GET /api/products-coverage: which ad types / match types cover each
// product (the Coverage view on PPC Products and the product page strip).
export type CoverageCell = { live: number; paused: number; spend: number; sales: number; acos: number | null }
export type CoverageColumnKey = 'sp_auto' | 'sp_exact' | 'sp_phrase' | 'sp_broad' | 'sp_pat' | 'sb' | 'sd'
export type CoverageColumn = {
  key: CoverageColumnKey; label: string
  status: 'ok' | 'partial' | 'sync_off' | 'no_data'; note: string | null
}
export type CoverageItem = {
  sku: string | null; shells: number; mixed: number; cells: Record<CoverageColumnKey, CoverageCell>
}
export type CoverageResponse = {
  marketplace: 'US' | 'CA'; profile_id: string | null; from: string; to: string
  as_of: { definitions: string | null; reports: string | null }
  columns: CoverageColumn[]; count: number; items: Record<string, CoverageItem>
}

function _entityQS(opts?: ListQuery & Record<string, string | number | boolean | undefined>): string {
  if (!opts) return ''
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(opts)) {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v))
  }
  const s = q.toString()
  return s ? `?${s}` : ''
}

export type TimeseriesBucketMetrics = {
  impressions: number | null
  clicks: number | null
  spend: number | null
  sales: number | null
  orders: number | null
  acos: number | null
  ctr: number | null
  cvr: number | null
}

export type TimeseriesPoint = {
  date: string
  impressions: number | null
  clicks: number | null
  spend: number | null
  sales: number | null
  orders: number | null
  acos: number | null
  ctr: number | null
  cvr: number | null
  /** True when sales/orders for this date were backfilled from hourly
   *  notification totals instead of PPC-attributed sales (last 1-2 days,
   *  attribution still settling). Charts render these points as dashed
   *  lines to make the data-source distinction visible. */
  live?: boolean
  /** v5 — Per-placement-bucket sub-metrics, populated only when the
   *  /api/timeseries call passes ``breakout=placement`` on the
   *  ``source=placements`` endpoint. Keys are 'tos' | 'ros' | 'pp'. */
  breakouts?: {
    tos?: TimeseriesBucketMetrics
    ros?: TimeseriesBucketMetrics
    pp?:  TimeseriesBucketMetrics
  }
}

export type TacosLiveEntry = {
  date: string
  ad_spend: number
  total_revenue: number
  total_orders: number
  tacos: number | null
  revenue_complete: boolean
  is_live: boolean
  /** B5-fe: how `ad_spend` was sourced. 'ams+pacing' | 'ams' | 'pacing' (the
   *  owner's AMS traffic/pacing blend, live) vs 'ppc_daily_stale' (the blend
   *  was unavailable and today fell back to the stale ppc_campaign_daily sum)
   *  vs 'ppc_daily' (yesterday, expected/not degraded). Optional — older
   *  backends may not send it. */
  spend_basis?: string
  revenue_basis?: string
}

export type TacosLiveResponse = {
  today: TacosLiveEntry
  yesterday: TacosLiveEntry
}

export type TimeseriesSource =
  | 'campaigns' | 'ad_groups' | 'product_ads' | 'asin'
  | 'keywords'  | 'search_terms' | 'targets' | 'placements'
  /** PR 3 (G8): keywords UNION ALL product/auto targets — Targets › All. */
  | 'targets_all'

export type TimeseriesOpts = {
  source: TimeseriesSource
  from?: string
  to?: string
  match_type?: string
  expression_type?: string
  campaign_id?: string
  /** CSV list of campaign IDs — the chart/KPI strip uses this to mirror
   *  the table's MultiColumnFilter narrow-set. Backend caps at 500 IDs;
   *  callers should fall back to undefined (no filter) above that. */
  campaign_id_in?: string
  asin?: string
  /** CSV list of ASINs — analog of campaign_id_in for asin/product_ads
   *  /ad_groups sources. */
  asin_in?: string
  placement?: string
  ad_group_id?: string
  /** CSV list of ad-group IDs — honored when source='ad_groups'. */
  ad_group_id_in?: string
  /** CSV list of keyword IDs — honored when source='keywords'. */
  keyword_id_in?: string
  /** PR 3 (B1) — the rest of the drill scope (lib/adsScope.ts). A portfolio
   *  by id or by name, and one target (a keyword id or a product/auto target
   *  id). Like every filter here, honoured or refused with a 400 — never
   *  dropped — by both /api/timeseries and the AMS tail. */
  portfolio_id?: string
  portfolio_name?: string
  target_id?: string
  /** PR 12d (C9) - the Campaigns page's Ad type (SP / SB / SD) and Marketplace
   *  (US / CA) selects. `source=campaigns` counts all three ad types like the
   *  table and answers both; every other source is SP-only and refuses SB / SD
   *  with a 400. The live tail applies ad_type, and answers a non-US marketplace
   *  with an empty list (the stream is subscribed for the US advertiser only). */
  ad_type?: string
  marketplace?: string
  /** v5 — When ``source='placements'`` and ``breakout='placement'``, each
   *  point in the response carries a ``breakouts`` dict keyed
   *  ``tos``/``ros``/``pp`` with per-bucket metrics. */
  breakout?: 'placement'
}

/** The narrowing half of a timeseries request: everything but the source,
 *  the window and the placement breakout. What a page hands PerfSummary /
 *  PpcPeriodTiles as `filters` — usually `withScope(scope, chartFilter)`. */
export type TimeseriesFilters = Omit<TimeseriesOpts, 'source' | 'from' | 'to' | 'breakout'>

Object.assign(api, {
  // ── Timeseries (chart at top of perf pages) ─────────────────────────
  getTimeseries: (opts: TimeseriesOpts) => {
    const q = new URLSearchParams()
    Object.entries(opts).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v))
    })
    return req<TimeseriesPoint[]>(`/api/timeseries?${q.toString()}`)
  },

  /** Trailing today+yesterday AMS-derived rows. Same shape as
   *  getTimeseries; each row carries ``live: true`` so the chart can
   *  style preliminary points distinctly. Returns [] on AMS-side failure
   *  (the report-driven series is still authoritative). */
  getTimeseriesAmsTail: (opts: TimeseriesOpts) => {
    const q = new URLSearchParams()
    Object.entries(opts).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v))
    })
    return req<TimeseriesPoint[]>(`/api/timeseries/ams-tail?${q.toString()}`)
  },

  /** Preliminary per-ASIN 'today' AMS spend, keyed by ASIN. Powers the
   *  Products table's amber today-spend column. Same campaign-day pro-rata
   *  allocation as the asin ams-tail. Returns {} on AMS-side failure. */
  getAmsTodaySpendByAsin: (asin_in?: string) => {
    const q = asin_in ? `?asin_in=${encodeURIComponent(asin_in)}` : ''
    return req<{ by_asin: Record<string, number>; as_of: string }>(
      `/api/timeseries/ams-today-by-asin${q}`)
  },

  /** PR 5c — per-product coverage by ad type / match type for one marketplace
   *  (the Coverage view on PPC Products; `asins` narrows it to one product). */
  getProductsCoverage: (opts: { marketplace: 'US' | 'CA'; from: string; to: string; asins?: string[] }) =>
    req<CoverageResponse>(`/api/products-coverage${_entityQS({ ...opts, asins: opts.asins?.join(',') })}`),

  /** Same-day per-campaign spend / attributed sales / ACoS from the Amazon
   *  Marketing Stream (Pacific day, ~1-2 h behind Amazon), keyed by
   *  campaign_id — the Campaigns table's amber "Today" overlay. Degrades to
   *  `degraded: 'bq_error'` + empty rows (HTTP 200) on a BigQuery failure. */
  getCampaignsTodayLive: () => req<CampaignsTodayLiveResponse>('/api/campaigns/today-live'),

  // ── Ad Groups ───────────────────────────────────────────────────────
  // campaign_id: cross-tab incoming filter (v2 #5) — Campaigns → Ad Groups.
  // UI overhaul PR-6: always the paged envelope (`total` + `totals` over the
  // whole filtered set) — the app never asks a list for a bare array again.
  // `include_inactive` lifts the "can serve" restriction (ad groups inside
  // paused campaigns), the same switch /api/campaigns has.
  listAdGroups: (opts?: ListQuery & { campaign_id?: string; include_inactive?: boolean }) =>
    req<Paged<AdGroupRow>>(`/api/ad-groups${_entityQS({ ...opts, envelope: 1 })}`),

  // ── Targets ─────────────────────────────────────────────────────────
  // ad_group_id: cross-tab incoming filter (v2 #5) — Ad Groups → Targets.
  // asin: reverse Product → Targets incoming filter (E3 / I3) — narrows to the
  // targets whose campaign advertises that ASIN.
  listTargets: (opts?: ListQuery & { expression_type?: string; campaign_id?: string; ad_group_id?: string; asin?: string }) =>
    req<TargetRow[]>(`/api/targets${_entityQS(opts)}`),
  /** Server-paged targets. Same endpoint: `envelope=1` swaps the bare row list
    *  for the paged envelope, so the legacy call above keeps working unchanged. */
  listTargetsPaged: (opts: PagedQuery & { expression_type?: string; campaign_id?: string; ad_group_id?: string; asin?: string }) =>
    req<Paged<TargetRow>>(`/api/targets${_entityQS(opts)}`),

  // ── Keywords ────────────────────────────────────────────────────────
  // ad_group_id: cross-tab incoming filter (v2 #5) — Ad Groups → Targets.
  listKeywordsEntity: (opts?: ListQuery & { match_type?: string; campaign_id?: string; ad_group_id?: string }) =>
    req<KeywordRow[]>(`/api/keywords${_entityQS(opts)}`),
  /** Server-paged keywords. The same endpoint: `envelope=1` swaps the bare row
   *  list for the paged envelope, so the legacy call above keeps working
   *  unchanged while pages migrate one at a time. */
  listKeywordsPaged: (opts: PagedQuery & { match_type?: string; campaign_id?: string; ad_group_id?: string; asin?: string }) =>
    req<Paged<KeywordRow>>(`/api/keywords${_entityQS(opts)}`),

  /** Targets › All (PR 3, G8): keywords ∪ product/auto targets as ONE
   *  server-paged list — sorting, the totals row and the KPI strip cover every
   *  matching row, not the 50,000 of each grain the browser used to merge.
   *  Always the paged envelope (there is no bare-list mode). */
  listTargetsAllPaged: (opts: TargetsAllQuery) =>
    req<Paged<TargetUnionRow>>(`/api/targets-all${_entityQS(opts)}`),

  // ── Product → Keywords tab (2026-07-08) ──────────────────────────────
  listProductKeywords: (asin: string, opts?: ListQuery) =>
    req<ProductKeywordRow[]>(`/api/products/${encodeURIComponent(asin)}/keywords${_entityQS(opts)}`),
  getProductKeywordDaily: (asin: string, keywordId: string, opts?: { from?: string; to?: string }) =>
    req<ProductKeywordDailyResponse>(
      `/api/products/${encodeURIComponent(asin)}/keywords/${encodeURIComponent(keywordId)}/daily${_entityQS(opts)}`,
    ),
  // Batch CPC bands (owner feedback #13) — one call for the whole table.
  getProductKeywordCpcBands: (asin: string, opts?: { from?: string; to?: string }) =>
    req<ProductKeywordCpcBandsResponse>(
      `/api/products/${encodeURIComponent(asin)}/keywords/cpc-bands${_entityQS(opts)}`,
    ),

  // ── Search Terms ────────────────────────────────────────────────────
  // ad_group_id: cross-tab incoming filter (v2 #5) — Ad Groups → Search Terms.
  // keyword_id: unified TRIGGER filter (E1/E3) — Target/keyword → its search
  //   terms. Filters on the RAW ppc_search_terms.keyword_id (inside the agg
  //   CTE), NOT the any_value alias.
  listSearchTerms: (opts?: ListQuery & { match_type?: string; campaign_id?: string; ad_group_id?: string; keyword_id?: string }) =>
    req<SearchTermRow[]>(`/api/search-terms${_entityQS(opts)}`),
  /** Server-paged search terms. See listTargetsPaged for the envelope contract. */
  listSearchTermsPaged: (opts: PagedQuery & { match_type?: string; campaign_id?: string; ad_group_id?: string; keyword_id?: string }) =>
    req<Paged<SearchTermRow>>(`/api/search-terms${_entityQS(opts)}`),

  // PPC A1 — preview (dry_run) or apply a negate/harvest against selected rows.
  // dry_run=true never writes; dry_run=false is still gated server-side on
  // ADBETTER_LIVE_WRITES (the response's live_writes_enabled says which).
  runSearchTermAction: (body: SearchTermActionRequest) =>
    req<SearchTermActionResponse>('/api/search-terms/action', {
      method: 'POST', body: JSON.stringify(body),
    }),

  // ── Product Ads ─────────────────────────────────────────────────────
  listProductAds: (opts?: ListQuery & { campaign_id?: string; asin?: string }) =>
    req<ProductAdRow[]>(`/api/product-ads${_entityQS(opts)}`),

  // ── Product Ads ROLLUP (ASIN parent → campaigns → ad-groups) ────────
  listProductAdsRollup: (opts?: ListQuery & {
    asin?: string
    marketplaces?: string  // CSV like 'US,CA'
  }) =>
    req<ProductAdRollupRow[]>(`/api/product-ads-rollup${_entityQS(opts)}`),

  // ── ASIN rollup ─────────────────────────────────────────────────────
  listAsin: (opts?: ListQuery) =>
    req<AsinRow[]>(`/api/asin${_entityQS(opts)}`),

  // ── Placements ──────────────────────────────────────────────────────
  listPlacements: (opts?: ListQuery & { placement?: string; campaign_id?: string }) =>
    req<PlacementRow[]>(`/api/placements${_entityQS(opts)}`),
  /** Server-paged placements. See listTargetsPaged for the envelope contract. */
  listPlacementsPaged: (opts: PagedQuery & { placement?: string; campaign_id?: string }) =>
    req<Paged<PlacementRow>>(`/api/placements${_entityQS(opts)}`),
  // v2 #6 — per-campaign placement spend shares over a window (fractions 0..1).
  listCampaignPlacementShares: (opts?: { from?: string; to?: string }) =>
    req<CampaignPlacementShareRow[]>(`/api/campaigns/placement-shares${_entityQS(opts)}`),
  // SM W1.3 — current placement adjustments for up to 200 campaigns (the bulk grids).
  getCampaignPlacementModifiers: (campaignIds: string[]) =>
    req<{ items: CampaignPlacementModifiers[]; missing: string[] }>(
      `/api/bulk/campaign-placements?campaign_ids=${encodeURIComponent(campaignIds.join(','))}`),

  // ── Hourly ──────────────────────────────────────────────────────────
  listHourly: (opts?: {
    asin?: string; hours_back?: number; limit?: number;
    from?: string; to?: string;
  }) =>
    req<HourlyRow[]>(`/api/hourly${_entityQS(opts)}`),
  getHourlyTimeseries: (opts?: { asin?: string; from?: string; to?: string }) =>
    req<HourlyTimeseriesPoint[]>(`/api/hourly/timeseries${_entityQS(opts)}`),
  // Round-4 Area B — per-hour ad rollup across keywords (PPC subtab).
  // AMS/BQ-backed; 168h max window; 60s server cache.
  listHourlyPpc: (opts?: { hours_back?: number; campaign_id?: string }) =>
    req<HourlyPpcRow[]>(`/api/hourly/ppc${_entityQS(opts)}`),
  // F3 — unified hourly. One endpoint backing All / Organic (total + ad
  // overlay) and PPC (per-hour ad rollup with keywords[] drill-down). NULL-
  // tolerant: a BQ failure / empty AMS window returns total-only rows with
  // ppc_available=false, never an error.
  listHourlyUnified: (opts?: {
    channel?: 'all' | 'organic'; asin?: string;
    from?: string; to?: string; hours_back?: number;
  }) =>
    req<HourlyUnifiedResponse>(`/api/hourly/unified${_entityQS(opts)}`),
  listHourlyUnifiedPpc: (opts?: { hours_back?: number; from?: string; to?: string }) =>
    req<HourlyUnifiedPpcResponse>(
      `/api/hourly/unified${_entityQS({ ...opts, channel: 'ppc' })}`,
    ),

  // ── AMS-backed live views (2026-05-26) ──────────────────────────────
  // Server-side cached 60s; BQ-direct (no local mirror). Backed by
  // joseph-claude.amazon.v_budget_pacing + v_sp_keyword_hourly.
  listBudgetPacing: (opts?: { ad_product?: 'sp' | 'sb' | 'sd' }) =>
    req<BudgetPacingResponse>(`/api/budget-pacing${_entityQS(opts)}`),
  listSpKeywordHourly: (opts?: {
    hours_back?: number; campaign_id?: string; keyword_id?: string;
  }) =>
    req<SpKeywordHourlyRow[]>(`/api/hourly/sp-keyword${_entityQS(opts)}`),

  // ── Products (catalog + optional PPC window) ────────────────────────
  // Passing `from`/`to` returns rows with PPC aggregates over the window;
  // omitting them returns catalog-only rows (perf fields null).
  listProducts: (opts?: ListQuery & {
    brand?: string; active?: boolean; from?: string; to?: string
    /** Narrow rollup math to one marketplace. SD spend (which has no
     *  marketplace dimension) still flows into the parent total + the
     *  "(unattributed)" child row. */
    marketplace?: 'US' | 'CA'
  }) =>
    // UI overhaul PR-6: the paged envelope, children grouped for the page.
    req<Paged<ProductRow>>(`/api/products${_entityQS({ ...opts, envelope: 1 })}`),

  // ── SKUs (one row per sku × marketplace; 7d + 30d windows in-view) ──
  listSkus: (opts?: ListQuery & { brand?: string; active?: boolean }) =>
    req<SkuRow[]>(`/api/skus${_entityQS(opts)}`),
  getProductDetail: (asin: string) =>
    req<ProductDetail>(`/api/products/${encodeURIComponent(asin)}`),

  // ── Operators + pending manual edits (Phase 3 — 2026-06-01) ──────────
  // Per-employee identity drives audit attribution + conflict detection.
  // Header selector writes operator slug to localStorage; every staging
  // call carries it in the body so the audit log can reconstruct who
  // changed what.
  listOperators: () =>
    req<{ operators: Operator[] }>('/api/operators'),
  // Every queue write here announces itself (lib/pendingEditsEvents.ts).
  stageManualEdit: (body: ManualEditStageBody) =>
    afterPendingWrite(req<{ id: number; staged_at: string; conflicts: ManualEditConflict[] }>(
      '/api/manual-edits',
      { method: 'POST', body: JSON.stringify(body) },
    )),
  // v2 #3b — stage N edits in ONE call through the shared bulk-staging
  // foundation (operators_api.stage_batch_manual_edits). Bid new_values are
  // clamped server-side to [0.02, 1000]; bad fields are reported in `failed`
  // (non-fatal) so a large selection never silently drops the good rows. Edits
  // land status='pending' for PendingTray review — push stays env-gated.
  stageBatchManualEdits: (operator_slug: string, edits: StageBatchEdit[]) =>
    afterPendingWrite(req<StageBatchResult>(
      '/api/manual-edits/stage-batch',
      { method: 'POST', body: JSON.stringify({ operator_slug, edits }) },
    )),
  // `notes_prefix` (PR-B) narrows to edits whose note starts with it — the
  // Suggests Queued tab passes SUGGESTS_NOTE_PREFIX (lib/suggestsQueue.ts).
  listManualEdits: (opts?: ListManualEditsOpts) =>
    req<{
      items: ManualEdit[]
      conflicts: { entity_type: string; entity_id: string; field: string }[]
      /** Only when `exclude_notes_prefix` was sent: how many pending rows it held back. */
      excluded_count?: number
    }>(
      `/api/manual-edits${_entityQS(opts)}`,
    ),
  applyManualEdit: (id: number, operator_slug: string) =>
    afterPendingWrite(req<{ id: number; applied_by: string; status: string; error_msg?: string; target?: { profile_id: string; ad_type: string; field: string } }>(
      `/api/manual-edits/${id}/apply`,
      { method: 'POST', body: JSON.stringify({ operator_slug }) },
    )),
  getLiveWritesStatus: () =>
    req<LiveWritesStatusResponse>(
      '/api/manual-edits/live-writes-status'),
  discardManualEdit: (id: number, operator_slug: string) =>
    afterPendingWrite(req<{ id: number; discarded_by: string; status: string }>(
      `/api/manual-edits/${id}/discard`,
      { method: 'POST', body: JSON.stringify({ operator_slug }) },
    )),
  /** Put a FAILED edit back in the queue as pending (PR-B review #8 — a failed
   *  apply used to leave every list and show nowhere). Only 'failed' rows. */
  requeueManualEdit: (id: number, operator_slug: string) =>
    afterPendingWrite(req<{ id: number; requeued_by: string; status: string }>(
      `/api/manual-edits/${id}/requeue`,
      { method: 'POST', body: JSON.stringify({ operator_slug }) },
    )),
  applyBatchManualEdits: (operator_slug: string, edit_ids: number[]) =>
    afterPendingWrite(req<ApplyBatchManualEditsResult>(
      '/api/manual-edits/apply-batch',
      { method: 'POST', body: JSON.stringify({ operator_slug, edit_ids }) },
    )),
  /** PR 24: start the background run that creates the staged new-campaign
   *  harvests one after another. Plain apply-batch still refuses them. */
  applyNewCampaigns: (operator_slug: string, edit_ids: number[]) =>
    afterPendingWrite(req<ApplyNewCampaignsStarted>(
      '/api/manual-edits/apply-new-campaigns',
      { method: 'POST', body: JSON.stringify({ operator_slug, edit_ids }) },
    )),
  /** One run's progress; 404 once the server has restarted. */
  getNewCampaignJob: (jobId: string) =>
    req<NewCampaignJob>(`/api/manual-edits/apply-new-campaigns/${encodeURIComponent(jobId)}`),
  /** The run in progress (if any) and the last few finished ones. */
  listNewCampaignJobs: () =>
    req<{ active: NewCampaignJob | null; recent: NewCampaignJob[] }>('/api/manual-edits/apply-new-campaigns'),
  listAudit: (opts?: { entity_type?: string; entity_id?: string; operator_slug?: string; status?: string; source?: string; limit?: number }) =>
    req<{ items: AuditEntry[] }>(`/api/audit${_entityQS(opts)}`),
  // #36 — the AMAZON side of the same trail: what MOVED on Amazon, from the
  // daily definitions snapshots, with a flag saying whether ClickPay has an
  // applied edit that explains it. See AmazonChangeEntry for what that flag
  // does and does NOT prove.
  listAuditAmazon: (opts?: { entity_type?: string; entity_id?: string; campaign_id?: string; changed_field?: string; date_from?: string; date_to?: string; limit?: number }) =>
    req<{ items: AmazonChangeEntry[]; source?: 'log' | 'view'; history_from?: string | null; unavailable?: boolean; reason?: string }>(
      `/api/audit/amazon${_entityQS(opts)}`),
  // Phase 5 Build 27 — on-demand trigger for the rule-run -> audit sweep
  // (also runs every 15 min off the scheduler). Admin-only + CSRF-guarded
  // (reqMut stamps X-Requested-With, same as every other /api/admin write).
  sweepAudit: () =>
    reqMut<{ swept_by: string; inserted: number }>('/api/audit/sweep'),

  // ── Tag mutators ────────────────────────────────────────────────────
  addProductTag: (asin: string, tag: string, color = 'gray') =>
    req<{ asin: string; tag: string; added: boolean }>(
      `/api/products/${encodeURIComponent(asin)}/tags`,
      { method: 'POST', body: JSON.stringify({ tag, color }) },
    ),
  deleteProductTag: (asin: string, tag: string) =>
    req<{ asin: string; tag: string; deleted: boolean }>(
      `/api/products/${encodeURIComponent(asin)}/tags/${encodeURIComponent(tag)}`,
      { method: 'DELETE' },
    ),

  // ── Competitor ASIN mutators ────────────────────────────────────────
  addCompetitorAsin: (asin: string, competitor_asin: string, note?: string) =>
    req<{ asin: string; competitor_asin: string; added: boolean }>(
      `/api/products/${encodeURIComponent(asin)}/competitors`,
      { method: 'POST', body: JSON.stringify({ competitor_asin, note }) },
    ),
  deleteCompetitorAsin: (asin: string, competitor_asin: string) =>
    req<{ asin: string; competitor_asin: string; deleted: boolean }>(
      `/api/products/${encodeURIComponent(asin)}/competitors/${encodeURIComponent(competitor_asin)}`,
      { method: 'DELETE' },
    ),

  // ── Product settings (gear icon) ────────────────────────────────────
  // Only send fields the user actually edited — undefined keys leave the
  // column unchanged; explicit null clears it (where the column nullable).
  // PR 2: no `cogs` — a cost is edited on the catalog record
  // (ops/lib/api.ts patchCatalogLandedCost) or on its purchase order, and the
  // backend answers a `cogs` key with a 400.
  patchProduct: (asin: string, patch: { active?: boolean; uid?: string | null }) =>
    req<{ asin: string; updated: boolean; patched: Record<string, unknown> }>(
      `/api/products/${encodeURIComponent(asin)}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    ),

  // ── Custom attribute mutators ───────────────────────────────────────
  upsertCustomAttr: (asin: string, key: string, value: string | null) =>
    req<{ asin: string; key: string; value: string | null; saved: boolean }>(
      `/api/products/${encodeURIComponent(asin)}/custom-attrs`,
      { method: 'PATCH', body: JSON.stringify({ key, value }) },
    ),
  deleteCustomAttr: (asin: string, key: string) =>
    req<{ asin: string; key: string; deleted: boolean }>(
      `/api/products/${encodeURIComponent(asin)}/custom-attrs/${encodeURIComponent(key)}`,
      { method: 'DELETE' },
    ),
})

export interface AdBetterEntityApi {
  getTimeseries: (opts: TimeseriesOpts) => Promise<TimeseriesPoint[]>
  getTimeseriesAmsTail: (opts: TimeseriesOpts) => Promise<TimeseriesPoint[]>
  getAmsTodaySpendByAsin: (asin_in?: string) => Promise<{ by_asin: Record<string, number>; as_of: string }>
  getProductsCoverage: (opts: { marketplace: 'US' | 'CA'; from: string; to: string; asins?: string[] }) => Promise<CoverageResponse>
  getCampaignsTodayLive: () => Promise<CampaignsTodayLiveResponse>
  listAdGroups: (opts?: ListQuery & { campaign_id?: string; include_inactive?: boolean }) => Promise<Paged<AdGroupRow>>
  listTargets: (opts?: ListQuery & { expression_type?: string; campaign_id?: string; ad_group_id?: string; asin?: string }) => Promise<TargetRow[]>
  listTargetsPaged: (opts: PagedQuery & { expression_type?: string; campaign_id?: string; ad_group_id?: string; asin?: string }) => Promise<Paged<TargetRow>>
  listKeywordsEntity: (opts?: ListQuery & { match_type?: string; campaign_id?: string; ad_group_id?: string }) => Promise<KeywordRow[]>
  listKeywordsPaged: (opts: PagedQuery & { match_type?: string; campaign_id?: string; ad_group_id?: string; asin?: string }) => Promise<Paged<KeywordRow>>
  listTargetsAllPaged: (opts: TargetsAllQuery) => Promise<Paged<TargetUnionRow>>
  listSearchTerms: (opts?: ListQuery & { match_type?: string; campaign_id?: string; ad_group_id?: string; keyword_id?: string }) => Promise<SearchTermRow[]>
  listSearchTermsPaged: (opts: PagedQuery & { match_type?: string; campaign_id?: string; ad_group_id?: string; keyword_id?: string }) => Promise<Paged<SearchTermRow>>
  runSearchTermAction: (body: SearchTermActionRequest) => Promise<SearchTermActionResponse>
  listProductAds: (opts?: ListQuery & { campaign_id?: string; asin?: string }) => Promise<ProductAdRow[]>
  listProductAdsRollup: (opts?: ListQuery & { asin?: string; marketplaces?: string }) => Promise<ProductAdRollupRow[]>
  listAsin: (opts?: ListQuery) => Promise<AsinRow[]>
  listPlacements: (opts?: ListQuery & { placement?: string; campaign_id?: string }) => Promise<PlacementRow[]>
  listPlacementsPaged: (opts: PagedQuery & { placement?: string; campaign_id?: string }) => Promise<Paged<PlacementRow>>
  listCampaignPlacementShares: (opts?: { from?: string; to?: string }) => Promise<CampaignPlacementShareRow[]>
  getCampaignPlacementModifiers: (campaignIds: string[]) => Promise<{ items: CampaignPlacementModifiers[]; missing: string[] }>
  listHourly: (opts?: { asin?: string; hours_back?: number; limit?: number; from?: string; to?: string }) => Promise<HourlyRow[]>
  getHourlyTimeseries: (opts?: { asin?: string; from?: string; to?: string }) => Promise<HourlyTimeseriesPoint[]>
  listHourlyPpc: (opts?: { hours_back?: number; campaign_id?: string }) => Promise<HourlyPpcRow[]>
  listHourlyUnified: (opts?: { channel?: 'all' | 'organic'; asin?: string; from?: string; to?: string; hours_back?: number }) => Promise<HourlyUnifiedResponse>
  listHourlyUnifiedPpc: (opts?: { hours_back?: number; from?: string; to?: string }) => Promise<HourlyUnifiedPpcResponse>
  listBudgetPacing: (opts?: { ad_product?: 'sp' | 'sb' | 'sd' }) => Promise<BudgetPacingResponse>
  listSpKeywordHourly: (opts?: { hours_back?: number; campaign_id?: string; keyword_id?: string }) => Promise<SpKeywordHourlyRow[]>
  listProducts: (opts?: ListQuery & {
    brand?: string; active?: boolean; from?: string; to?: string
    marketplace?: 'US' | 'CA'
  }) => Promise<Paged<ProductRow>>
  listSkus: (opts?: ListQuery & { brand?: string; active?: boolean }) => Promise<SkuRow[]>
  getProductDetail: (asin: string) => Promise<ProductDetail>
  // Product → Keywords tab (2026-07-08).
  listProductKeywords: (asin: string, opts?: ListQuery) => Promise<ProductKeywordRow[]>
  getProductKeywordDaily: (asin: string, keywordId: string, opts?: { from?: string; to?: string }) => Promise<ProductKeywordDailyResponse>
  getProductKeywordCpcBands: (asin: string, opts?: { from?: string; to?: string }) => Promise<ProductKeywordCpcBandsResponse>
  addProductTag: (asin: string, tag: string, color?: string) => Promise<{ added: boolean }>
  deleteProductTag: (asin: string, tag: string) => Promise<{ deleted: boolean }>
  addCompetitorAsin: (asin: string, competitor_asin: string, note?: string) => Promise<{ added: boolean }>
  deleteCompetitorAsin: (asin: string, competitor_asin: string) => Promise<{ deleted: boolean }>
  upsertCustomAttr: (asin: string, key: string, value: string | null) => Promise<{ saved: boolean }>
  deleteCustomAttr: (asin: string, key: string) => Promise<{ deleted: boolean }>
  patchProduct: (asin: string, patch: { active?: boolean; uid?: string | null })
    => Promise<{ asin: string; updated: boolean; patched: Record<string, unknown> }>
  // Phase 3 — operator + manual-edit endpoints.
  listOperators: () => Promise<{ operators: Operator[] }>
  stageManualEdit: (body: ManualEditStageBody) => Promise<{ id: number; staged_at: string; conflicts: ManualEditConflict[] }>
  stageBatchManualEdits: (operator_slug: string, edits: StageBatchEdit[]) => Promise<StageBatchResult>
  listManualEdits: (opts?: ListManualEditsOpts) => Promise<{
    items: ManualEdit[]
    conflicts: { entity_type: string; entity_id: string; field: string }[]
    excluded_count?: number
  }>
  applyManualEdit: (id: number, operator_slug: string) => Promise<{ id: number; applied_by: string; status: string; error_msg?: string; target?: { profile_id: string; ad_type: string; field: string } }>
  getLiveWritesStatus: () => Promise<LiveWritesStatusResponse>
  discardManualEdit: (id: number, operator_slug: string) => Promise<{ id: number; discarded_by: string; status: string }>
  requeueManualEdit: (id: number, operator_slug: string) => Promise<{ id: number; requeued_by: string; status: string }>
  applyBatchManualEdits: (operator_slug: string, edit_ids: number[]) => Promise<ApplyBatchManualEditsResult>
  applyNewCampaigns: (operator_slug: string, edit_ids: number[]) => Promise<ApplyNewCampaignsStarted>
  getNewCampaignJob: (jobId: string) => Promise<NewCampaignJob>
  listNewCampaignJobs: () => Promise<{ active: NewCampaignJob | null; recent: NewCampaignJob[] }>
  listAudit: (opts?: { entity_type?: string; entity_id?: string; operator_slug?: string; status?: string; source?: string; limit?: number }) =>
    Promise<{ items: AuditEntry[] }>
  listAuditAmazon: (opts?: { entity_type?: string; entity_id?: string; campaign_id?: string; changed_field?: string; date_from?: string; date_to?: string; limit?: number }) =>
    Promise<{ items: AmazonChangeEntry[]; source?: 'log' | 'view'; history_from?: string | null; unavailable?: boolean; reason?: string }>
  sweepAudit: () => Promise<{ swept_by: string; inserted: number }>
}

export type Operator = {
  id: number
  slug: string
  display_name: string
  email: string | null
  color: string
  last_active_at: string | null
}

/** GET /api/manual-edits query options. */
export type ListManualEditsOpts = {
  operator_slug?: string
  status?: string
  entity_type?: string
  scope?: 'mine' | 'others' | 'all'
  limit?: number
  /** Only edits whose note STARTS WITH this literal text (PR-B). */
  notes_prefix?: string
  /** Everything EXCEPT edits whose note starts with this (PR-B review): the
   *  Pending edits tray passes SUGGESTS_NOTE_PREFIX so queued ClickPay
   *  Suggests changes are applied from the Queued tab and nowhere else. Done
   *  server-side, so the tray's 200-row cap still covers the operator's own
   *  edits; the response's `excluded_count` says how many were held back. */
  exclude_notes_prefix?: string
}

/** POST /api/manual-edits/apply-batch — one entry per requested id, in order.
 *  `result.status` is the truthful per-edit outcome ('applied' /
 *  'staged_no_writes' / 'failed'); `ok: false` without a `result` is an
 *  HTTP-level refusal for that id (`status` = its HTTP code, e.g. 409 when
 *  the edit was no longer pending). */
export type ApplyBatchManualEditsResult = {
  applied_by: string
  results: {
    id: number
    ok: boolean
    error?: string
    status?: number
    result?: { id: number; applied_by: string; status: string; error_msg?: string }
  }[]
}

export type ManualEditStageBody = {
  operator_slug: string
  entity_type: string
  entity_id: string
  field: string
  old_value: string | null
  new_value: string
  notes?: string | null
}

export type ManualEditConflict = {
  id: number
  operator_slug: string
  old_value: string | null
  new_value: string
  staged_at: string | null
  notes: string | null
}

/** One edit in a stage-batch call (v2 #3b). Same shape as the single-row body
 *  minus operator_slug — the batch carries one operator for all edits. */
export type StageBatchEdit = {
  entity_type: string
  entity_id: string
  field: string
  old_value?: string | null
  new_value: string
  notes?: string | null
}

export type StageBatchResult = {
  operator_slug: string
  staged: Array<{
    index: number
    id: number | null
    entity_type: string
    entity_id: string
    field: string
    new_value: string
    staged_at: string | null
    conflicts: ManualEditConflict[]
  }>
  failed: Array<{
    index: number
    entity_type: string
    entity_id: string
    field: string
    error: string
  }>
  staged_count: number
  failed_count: number
}

/** GET /api/manual-edits/live-writes-status. `manual_pushes_to_amazon` is the
 *  OR of the two operator gates; the last two are the PR 5b switches that sit
 *  in front of staged harvests (harvest sending, new-campaign creation). */
export type LiveWritesStatusResponse = {
  live_writes_enabled: boolean
  manual_writes_enabled?: boolean
  manual_pushes_to_amazon?: boolean
  harvest_writes_enabled?: boolean
  campaign_creation_enabled?: boolean
  /** PR 24: id of the "Create all" run in progress on the server, or null. */
  creating_campaigns?: string | null
}

/** POST /api/manual-edits/apply-new-campaigns (202). */
export type ApplyNewCampaignsStarted = {
  job_id: string
  total: number
  queued: number[]
  refused: { id: number; reason: string }[]
}

/** One row's outcome in a "Create all" run. */
export type NewCampaignResult = {
  id: number
  status: 'applied' | 'staged_no_writes' | 'skipped' | 'failed' | 'refused'
  error: string | null
  campaign_id?: string | null
}

/** GET /api/manual-edits/apply-new-campaigns/{job_id}. */
export type NewCampaignJob = {
  job_id: string
  status: 'running' | 'done'
  total: number
  done: number
  current: { id: number; label: string } | null
  results: NewCampaignResult[]
  operator?: string
  started_at: string
  finished_at: string | null
}

export type ManualEdit = {
  id: number
  operator_slug: string
  operator_display: string | null
  operator_color: string | null
  entity_type: string
  entity_id: string
  field: string
  old_value: string | null
  new_value: string
  status: 'pending' | 'applied' | 'staged_no_writes' | 'failed' | 'discarded' | 'superseded'
  staged_at: string | null
  applied_at: string | null
  error_msg: string | null
  notes: string | null
  /** The key to MATCH this edit on, not the one it was stored under. A
   *  placement bucket reaches the tray under two spellings
   *  ('<campaign>:top' from ClickPay Suggests, '<campaign>:Top of Search
   *  on-Amazon' from the Placements page); this collapses them, so a queued
   *  suggestion is found by the table showing that bucket. Server-computed —
   *  never re-derive it here. */
  canonical_entity_id?: string | null
  has_conflict: boolean
  /** Human-readable context resolved server-side: which campaign / ad group /
   *  product (ASIN) this edit affects, plus a label (keyword / ASIN target /
   *  campaign name). Best-effort — fields absent when the entity can't be
   *  resolved. */
  context?: {
    campaign_name?: string | null
    ad_group_id?: string | null
    ad_group_name?: string | null
    asin?: string | null
    label?: string | null
    match_type?: string | null
    /** A staged harvest (entity_type 'harvest'): where it goes and where it
     *  came from. campaign_name is the destination (or the planned new
     *  campaign's name). */
    dest_kind?: 'ad_group' | 'new_campaign' | null
    bid?: number | null
    negate_source?: boolean | null
    source_campaign_name?: string | null
    source_ad_group_name?: string | null
    /** PR 24: what a staged new-campaign harvest will create. */
    daily_budget?: number | null
    portfolio_id?: string | null
    portfolio_name?: string | null
    start_paused?: boolean | null
    /** The bid was lowered by a house limit when it was staged. */
    bid_limited?: 'house_max' | 'budget' | null
    bid_formula?: number | null
    bid_at_house_max?: boolean | null
  } | null
  /** The value the entity holds RIGHT NOW, read at list time. Present only for
   *  pending ClickPay Suggests rows (the ones whose `old_value` is shown as
   *  "Current"); null when it could not be read. */
  live_value?: string | null
  /** True when `live_value` no longer equals the `old_value` this row was
   *  queued from — applying it would overwrite a newer change, so the server
   *  refuses it (status 'stale' since #69/#99; 'value_changed' before) and
   *  retires the row as 'superseded' with `superseded_reason`. */
  value_changed?: boolean
}

/** Fired on `window` after manual edits are PUSHED to Amazon and confirmed
 *  applied (status='applied' only — never staged/failed). EditableCell listens
 *  to flip the matching table cell to the new value instantly + truthfully;
 *  the value is also persisted server-side (operators_api._mirror_applied_value)
 *  so any refetch/reload shows it too. */
export const EDITS_APPLIED_EVENT = 'clickpay:edits-applied'

/** Fired on `window` to open the header's Pending changes tray from anywhere —
 *  the bell's "You have N pending changes" item (PR 8). PendingTray listens. */
export const OPEN_PENDING_TRAY_EVENT = 'clickpay:open-pending-tray'
export type AppliedEditDetail = {
  entity_type: string
  entity_id: string
  field: string
  new_value: string
}

export type AuditEntry = {
  id: number
  source: 'manual_edit' | 'rule_run' | 'admin'
  operator_slug: string | null
  operator_display: string | null
  operator_color: string | null
  entity_type: string
  entity_id: string
  field: string
  old_value: string | null
  new_value: string
  applied_at: string | null
  pending_id: number | null
  // Phase 5 Build 27 — the unified trail. `status` is the REAL outcome:
  // applied / staged_no_writes / failed for source='manual_edit',
  // applied / approval_applied for source='rule_run' (swept — see
  // backend/audit_sweep.py). Null only for a pre-124 manual row whose tray
  // entry no longer exists. rule_run_id/rule_name are populated for swept
  // rows only (null for manual_edit rows).
  status: string | null
  rule_run_id: number | null
  rule_name: string | null
}

/** #36 — one movement of an Amazon-side value between two daily definition
 *  snapshots (GET /api/audit/amazon over the ppc_definitions_changes view).
 *
 *  READ `matched_clickpay_edit` CAREFULLY. true = ClickPay has an applied
 *  audit row for the same entity landing between the two snapshots, so this
 *  movement is explained. false = ClickPay has NO such row, and that is ALL it
 *  means. It is NOT evidence anyone edited anything in Seller Central: Amazon's
 *  own automation (Dynamic Bidding, budget rules) moves these values with no
 *  human involved and leaves no ClickPay record. On the 2026-09-04 prod mirror
 *  2,955 keyword/target bid changes carry only 90 matches. Label the column
 *  "Made through ClickPay?", never "Changed in Seller Central". */
export type AmazonChangeEntry = {
  change_date: string | null
  /** The snapshot this movement is measured against. Usually the previous day,
   *  but wider whenever the sync skipped a day. */
  prev_snapshot_date: string | null
  entity_type: string
  entity_id: string
  /** The entity's name when known (keyword text, target expression, ad group
   *  or campaign name); absent or null shows the id (E1). */
  entity_label?: string | null
  campaign_id: string | null
  ad_group_id: string | null
  ad_type: string | null
  /** Which of state / bid / budget / tos / ros / pp moved. */
  changed_fields: string[]
  state_before: string | null
  state_after: string | null
  bid_before: number | null
  bid_after: number | null
  budget_before: number | null
  budget_after: number | null
  tos_before: number | null
  tos_after: number | null
  ros_before: number | null
  ros_after: number | null
  pp_before: number | null
  pp_after: number | null
  matched_clickpay_edit: boolean
  clickpay_applied_at: string | null
  clickpay_operator_slug: string | null
  clickpay_operator_display: string | null
  clickpay_operator_color: string | null
  clickpay_field: string | null
  clickpay_source: string | null
  clickpay_rule_run_id: number | null
  /** For a placement edit, the bucket it moved ('tos' | 'ros' | 'pp'), in the
   *  same vocabulary as `changed_fields`. The trail files placement edits at
   *  PLACEMENT grain ('<campaign>:Top of Search on-Amazon') while Amazon
   *  reports them as columns on the CAMPAIGN row; the server reconciles the
   *  two and reports which bucket matched. Null for every other kind of match
   *  and for no match. */
  clickpay_placement_bucket: string | null
}

export const apiEntity = api as unknown as typeof api & AdBetterEntityApi

// ─── ClickPay per-UID TACoS (product-review #5; /api/tacos) ────────────────
// TACoS = ad spend ÷ TOTAL sales, bridge-resolved per UID by the
// cp_uid_tacos view (migration 074). Rows are per (uid, marketplace,
// window) in NATIVE currency; `tacos` is null until the local mirrors
// cover the whole window (see the migration header for the semantics).
export type TacosRow = {
  uid: string
  marketplace: string
  window_days: number
  ad_spend: number | null
  ads_sales: number | null
  acos: number | null
  total_sales: number | null
  total_units: number | null
  tacos: number | null
  sales_window_complete: boolean
  ads_window_complete: boolean
  currency: string
}

export type TacosBulkResponse = {
  marketplace: string
  window_days: number
  count: number
  items: Record<string, TacosRow>
}

export type TacosCampaign = {
  campaign_id: string
  campaign_name: string | null
  ad_type: string | null
  confidence: string
  spend: number | null
  ads_sales: number | null
  acos: number | null
}

export type TacosDetailResponse = {
  uid: string
  rows: TacosRow[]
  campaigns: Record<string, TacosCampaign[]>
}

export const apiTacos = {
  /** One {uid: row} map per (marketplace, window) — table-column food. */
  bulk: (marketplace = 'Amazon US', window = 30) =>
    req<TacosBulkResponse>(
      `/api/tacos/bulk?marketplace=${encodeURIComponent(marketplace)}&window=${window}`,
    ),
  /** Every marketplace × window row + top campaigns for one uid. */
  detail: (uid: string) =>
    req<TacosDetailResponse>(`/api/tacos/${encodeURIComponent(uid)}`),
}

// ── Go-Live Preflight (clickpay/rules-preflight) ────────────────────────
//
// Read-only aggregation behind GET /api/rules-preflight — see
// backend/api/rules_preflight.py. Kept as a separate `preflightApi` export
// (same pattern as `approvalsApi`) so this is a pure append to this module.

export type PreflightRunStatusClass = 'ok' | 'fail' | 'skip' | 'running' | 'other'

export type PreflightRun = {
  id: number
  status: string
  status_class: PreflightRunStatusClass
  started_at: string | null
  finished_at: string | null
  records_matched: number | null
  records_changed: number | null
  dry_run: boolean | null
  error_message: string | null
  triggered_by: string | null
}

export type PreflightFreshness = {
  stale: boolean
  age_hours: number | null
  etl_table: string | null
  max_age_hours: number
  reason: string | null
  /** AMS-backed sources block live fires when the freshness signal is
   *  MISSING (fail-closed), not just when it is stale. */
  fail_closed: boolean
}

export type PreflightLastError = {
  run_id: number
  status: string
  at: string | null
  message: string
}

export type PreflightLastDryRun = {
  run_id: number
  status: string
  status_class: PreflightRunStatusClass
  at: string | null
  records_matched: number | null
  records_changed: number | null
}

/** The record-count cap that would refuse the NEXT fire, derived from the
 *  most recent run's matched count vs the rule's own cap columns — see
 *  backend/api/rules_preflight.py's `_blocking_cap` and
 *  frontend/src/lib/ruleHealth.ts's `blockingCap` (independent client-side
 *  derivation of the same truth table, for the Rules list which doesn't
 *  fetch this endpoint). Null when the rule has never run or matches
 *  comfortably under both caps. */
export type PreflightBlockingCap = {
  cap: 'max_records_per_fire' | 'max_records_per_day'
  cap_value: number
  matched: number
  times_over: number
}

export type PreflightRule = {
  id: number
  name: string
  rule_code: string | null
  rule_kind: string | null
  data_source: string
  /** False ⇒ the rule-update API rejects dry_run=false for this source. */
  data_source_verified: boolean
  action_type: string
  schedule_cron: string
  enabled: boolean
  dry_run: boolean
  test_run: boolean
  test_run_armed: boolean
  safety_rail: boolean
  cooldown_tag: string | null
  cooldown_days: number | null
  max_entity_changes_per_day: number | null
  // Phase 17 "cap-visibility" — the three caps that actually gate whether a
  // fire runs at all (max_entity_changes_per_day above is per-ENTITY, never
  // the one that blocks a whole fire).
  max_records_per_fire: number | null
  max_records_per_day: number | null
  max_spend_delta_per_fire: number | null
  /** Migration 153 — when true, blocking_cap is always null because the
   *  runner skips both row gates for this rule. */
  bypass_record_caps: boolean
  max_data_age_hours: number | null
  blocking_cap: PreflightBlockingCap | null
  /** PR 1c — edited after its last run started: blocking_cap is null and the
   *  warnings say "changed since its last run". The page's own derivation
   *  (ruleHealth.ts blockingCap) reads it so its chip agrees. Absent from an
   *  older server. */
  selection_changed_since_last_run?: boolean
  matched_45d: number
  changed_45d: number
  matched_14d: number
  /** 'inert' | 'stopped' | null — see ruleHealth.ts's ruleHealthBadge for
   *  the truth table (this field mirrors the SAME table computed
   *  server-side; the frontend also derives it independently for the Rules
   *  list, which doesn't hit this endpoint). */
  health_badge: 'inert' | 'stopped' | null
  freshness: PreflightFreshness
  last_runs: PreflightRun[]
  last_error: PreflightLastError | null
  /** Most recent dry-run fire — the guarded flip modal samples ITS changes
   *  via GET /api/runs/{run_id}/changes?limit=10 before allowing the flip. */
  last_dry_run: PreflightLastDryRun | null
  blockers: string[]
  warnings: string[]
  /** 'ask_first': dry_run false + requires_approval (its fires only queue
   *  changes for approval; never badged LIVE). */
  verdict: 'live' | 'ask_first' | 'blocked' | 'caution' | 'ready'
  /** Ask me first (with dry_run false). Optional for an older server. */
  requires_approval?: boolean
}

export type PreflightEngine = {
  /** Same fields as CircuitState minus the singleton row id. */
  circuit_breaker: {
    tripped: boolean
    tripped_at: string | null
    tripped_reason: string | null
    cleared_at: string | null
    cleared_by: string | null
  }
  /** Build 09 — the rollout-stage gate the runner ALSO checks before any
   *  live fire, independent of the env switches below. */
  rollout: {
    stage: string
    /** The stage stored in bbpro_rollout_state. Equals `stage` except while
     *  emergency stop is engaged: then `stage` reads 'off' (the verdict) and
     *  this is the stage clearing the stop restores. Null when unreadable. */
    stored_stage?: string | null
    emergency_stop: boolean
    /** The exact boolean the runner consults — render this directly rather
     *  than re-deriving "stage === 'full' && !emergency_stop". */
    permits_live_dispatch: boolean
    reason: string | null
    changed_by: string | null
    changed_at: string | null
  }
  pending_approvals: number
  /** ADBETTER_LIVE_WRITES master switch — READ-ONLY display; flipping it is
   *  an env/deploy action, never a UI action. */
  live_writes_env: boolean
  /** ADBETTER_RULE_WRITES — the SECOND stacked kill switch, required (with
   *  live_writes_env) for any rule-originated write. READ-ONLY display. */
  rule_writes_env: boolean
  /** BB_SYNC_STRICT — the BQ→DuckDB strict-sync gate. */
  sync_strict_env: boolean
  freshness: {
    verdict: 'pass' | 'stale' | 'no_rules'
    default_max_age_hours: number
    sources: Array<PreflightFreshness & { data_source: string }>
  }
}

export type PreflightResponse = {
  generated_at: string
  engine: PreflightEngine
  rules: PreflightRule[]
}

export const preflightApi = {
  /** runs = per-rule run-history depth (rendered as dots; default 5). */
  get: (runs = 5) => req<PreflightResponse>(`/api/rules-preflight?runs=${runs}`),
}

/** Build 09 — staged go-live + emergency stop. Admin-only server-side
 *  (CLICKPAY_AUTH=on); the frontend also hides these controls for
 *  non-admins (useAuth().isAdmin), same belt-and-suspenders pattern as the
 *  sidebar-layout editor. */
export const rolloutAdminApi = {
  setStage: (stage: string, reason?: string) =>
    reqMut<PreflightEngine['rollout']>('/api/admin/rollout-stage', { stage, reason }),
  emergencyStop: (reason?: string) =>
    reqMut<PreflightEngine['rollout']>('/api/admin/rules-emergency-stop', { reason }),
  emergencyClear: (reason?: string) =>
    reqMut<PreflightEngine['rollout']>('/api/admin/rules-emergency-clear', { reason }),
  /** Build 05 — release a rule parked at run status='needs_confirmation'.
   *  Admin-only server-side (CLICKPAY_AUTH=on); the frontend also hides
   *  the Confirm button for non-admins (useAuth().isAdmin), same
   *  belt-and-suspenders pattern as the rollout controls above. Fires a
   *  FRESH run (not a replay of the parked record set) — every other
   *  safety gate still applies. */
  confirmRun: (ruleId: number, reason?: string) =>
    reqMut<Run>(`/api/admin/rules/${ruleId}/confirm-run`, { reason }),
}

// ── Rule backtest (Phase 4 Build 06) ────────────────────────────────────
//
// GET /api/rules/{id}/backtest — pure read-only replay of a rule's match/
// resolution logic over a historical date range. See backend/engine/
// backtest.py for the full contract: never dispatches to Amazon, never
// writes bbpro_rule_runs/bbpro_rule_run_changes/bbpro_record_tags, never
// consumes Test Run. A GET (not POST) so it rides the auth middleware's
// ordinary read tier — no special permission beyond being signed in.

export type BacktestDayResult = {
  day: string
  matched: number
  would_fire: number
  noop: number
  placeholder_errors: number
  holdout_marked: number
  est_spend_delta: number
  /** Requested day is more recent than this rule's lag floor can
   *  represent — see `reason`. Never silently substituted for a nearby day. */
  insufficient_history: boolean
  error: string | null
  reason: string | null
}

export type BacktestTotals = {
  matched: number
  would_fire: number
  noop: number
  placeholder_errors: number
  holdout_marked: number
  est_spend_delta: number
  days_covered: number
  days_insufficient_history: number
  days_errored: number
}

export type BacktestResult = {
  rule_id: number
  rule_kind: string
  data_source: string
  entity_type: string | null
  start_date: string
  end_date: string
  lag_group: string
  days: BacktestDayResult[]
  /** Set (with `days` empty) when the rule's data source has no honest
   *  historical replay at all — e.g. a live/real-time BQ source. */
  unsupported: string | null
  totals: BacktestTotals
}

export const backtestApi = {
  run: (ruleId: number, startDate: string, endDate: string) =>
    req<BacktestResult>(
      `/api/rules/${ruleId}/backtest?start_date=${encodeURIComponent(startDate)}` +
      `&end_date=${encodeURIComponent(endDate)}`,
    ),
}

// ─── Automation coverage (Phase 10 Build 33; /api/automation/coverage) ─────
// How much of the work ClickPay RECORDED was done by a rule rather than by
// hand. `counted` is the denominator and it means exactly one thing: changes
// ClickPay recorded AND confirmed took effect. It is NOT every change made to
// the Amazon account — there is no console change-history ingest anywhere in
// this product — which is why the payload ships its own `scope` block and the
// UI repeats it. See backend/api/coverage_api.py.

/** Shared shape of every coverage grain: the three buckets + their total. */
export type CoverageBuckets = {
  automated: number
  manual: number
  other: number
  counted: number
  /** null (not 0) when `counted` is 0 — "no data" is not "no automation". */
  automated_pct: number | null
}

export type CoverageEntityRow = CoverageBuckets & { entity_type: string }
export type CoverageFieldRow = CoverageBuckets & { field: string }
export type CoverageWeekRow = CoverageBuckets & { week_start: string }

export type CoverageHotspot = {
  entity_type: string
  entity_id: string
  field: string
  edits: number
  operators: number
  last_edited_at: string | null
}

export type AutomationCoverage = {
  window: { days: number; as_of: string }
  /** false when the audit trail isn't readable (fresh/minimal DB) — the page
   *  says why instead of rendering a fabricated 0%. */
  available: boolean
  unavailable_reason: string | null
  scope: {
    denominator: string
    includes_amazon_console_changes: boolean
    blind_spots: string[]
  }
  totals: CoverageBuckets & { manual_pct: number | null }
  /** Recorded work that changed nothing, kept OUT of the ratio and shown
   *  separately so the recorded population still adds up. */
  not_counted: {
    staged_not_pushed: number
    failed: number
    outcome_unknown: number
    total: number
  }
  /** Source strings that are neither 'manual_edit' nor 'rule_run'. */
  other_sources: string[]
  /** What automation wanted to do but didn't. null = could not read. */
  proposed_only: { simulated_dry_run: number | null; awaiting_approval: number | null }
  /** Rule changes that fired but aren't swept into the trail yet — while this
   *  is > 0 the ratio is reading LOW, and the page says so. */
  lag: { unswept_automated: number | null }
  engine: {
    stage: string | null
    emergency_stop: boolean
    permits_live_dispatch: boolean
    reason: string | null
    changed_at: string | null
  } | null
  by_entity_type: CoverageEntityRow[]
  by_field: CoverageFieldRow[]
  by_week: CoverageWeekRow[]
  manual_hotspots: CoverageHotspot[]
}

export const coverageApi = {
  get: (days: number) => req<AutomationCoverage>(`/api/automation/coverage?days=${days}`),
}

/* ── Workflow proposals (Phase 12 Build 38) ────────────────────────────────
 * Describe a recurring job in a sentence; Claude drafts it as a workflow for
 * ClickPay's runner; ClickPay checks, step by step, whether it can actually
 * carry it out. Nothing here runs anything — promoting a draft creates a
 * workflow that is switched off.
 *
 * Reads are the ordinary tier (any signed-in employee). Writes go to
 * /api/admin/workflow-proposals so backend/auth/middleware.py's admin tier
 * catches them — see backend/api/workflow_proposals.py for why the prefix is
 * load-bearing.
 */

/** One step's verdict. `achievable` false is the only thing that blocks a
 *  draft from being promoted; warnings never do. */
export type WorkflowStepCheck = {
  /** 0-based position in the YAML, as the runner reads it. */
  index: number
  name: string
  /** The step's `type:` or `impl:` as written. */
  kind: string
  /** What that kind of step does, in plain English. Null for a kind ClickPay
   *  does not have. */
  kind_label: string | null
  achievable: boolean
  /** Written to be read by whoever is looking at the screen. */
  message: string
  /** The database's own words, when those are the useful part. Absolute paths
   *  are stripped server-side. */
  detail: string | null
  warnings: string[]
  /** True when this step is set to make the change for real rather than
   *  preview it. */
  writes_to_amazon: boolean
}

export type WorkflowCheck = {
  achievable: boolean
  checked_at: string
  summary: string
  /** Only populated when the draft could not be read far enough to produce
   *  per-step verdicts (bad YAML, no steps). */
  problems: string[]
  steps: WorkflowStepCheck[]
  warnings: string[]
}

/** What this installation can do, as the checker measured it. Shown so a
 *  reader can see what a verdict was measured against. */
export type WorkflowCapabilities = {
  step_types: string[]
  step_impls: string[]
  action_types: string[]
  llm_transport: 'test' | 'api' | 'cli' | 'unavailable'
  llm_transport_text: string
}

export type WorkflowProposal = {
  id: number
  /** The sentence somebody typed. */
  job_text: string
  title: string
  slug: string
  /** Null in the list response — the list does not carry every draft's full
   *  YAML. Always present on the single-proposal response. */
  steps_yaml: string | null
  check: WorkflowCheck
  checked_at: string | null
  achievable: boolean
  status: 'draft' | 'promoted'
  promoted_workflow_id: number | null
  promoted_at: string | null
  promoted_by: string | null
  draft_source: 'claude-cli' | 'typed-by-hand'
  drafted_by: string | null
  created_at: string | null
  updated_at: string | null
  updated_by: string | null
  capabilities?: WorkflowCapabilities
}

export type WorkflowProposalList = {
  proposals: WorkflowProposal[]
  capabilities: WorkflowCapabilities
  max_job_chars: number
}

export const workflowProposalsApi = {
  list: () => req<WorkflowProposalList>('/api/workflow-proposals'),
  get: (id: number) => req<WorkflowProposal>(`/api/workflow-proposals/${id}`),
  /** Give `job_text` to have Claude draft it, or `steps_yaml` to check
   *  something already written. Admin-only (middleware) + CSRF header, same
   *  contract as every other /api/admin write in this client. */
  create: (body: { job_text?: string; steps_yaml?: string; title?: string }) =>
    req<WorkflowProposal>('/api/admin/workflow-proposals', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    }),
  save: (id: number, body: { title?: string; steps_yaml: string }) =>
    req<WorkflowProposal>(`/api/admin/workflow-proposals/${id}`, {
      method: 'PUT',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    }),
  /** Creates the workflow in bbpro_workflow_definitions, switched off. Refuses
   *  (409) when the re-run check finds a step ClickPay cannot carry out. */
  promote: (id: number) =>
    req<WorkflowProposal>(`/api/admin/workflow-proposals/${id}/promote`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }),
  remove: (id: number) =>
    req<{ deleted: number }>(`/api/admin/workflow-proposals/${id}`, {
      method: 'DELETE',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }),
}

/* ── The workflow library (Phase 12 Build 37) ───────────────────────────────
 * /workflows — the recurring jobs we do, written down. Records live in the
 * database (migration 139) and are authored in the app by an admin, not in
 * this file: the canonical job list is an owner decision, and a code-resident
 * list would mean every edit needs an engineer and a deploy.
 *
 * Reads are the ordinary tier (any signed-in employee). Writes go to
 * /api/admin/workflows so backend/auth/middleware.py's admin tier catches them
 * — see backend/api/workflow_docs.py for why that prefix is load-bearing.
 */

export type WorkflowRole = 'ops' | 'ppc' | 'accounting' | 'owner'
export type WorkflowCadence = 'daily' | 'weekly' | 'monthly' | 'per-event'

export type WorkflowStep = {
  /** 1-based, assigned by the server in list order — never sent by the page. */
  n: number
  instruction: string
  /** In-app path this step happens on, or null when the step happens
   *  elsewhere ("call the freight forwarder"). */
  route: string | null
  watch_out: string | null
}

export type WorkflowDoc = {
  slug: string
  title: string
  role: WorkflowRole
  cadence: WorkflowCadence
  trigger_text: string
  steps: WorkflowStep[]
  /** What ClickPay does NOT do for this job. Shipped WITH the procedure so a
   *  reader never assumes the app covers a step it doesn't. */
  gaps: string[]
  sort_order: number
  published: boolean
  reviewed_at: string | null
  reviewed_by: string | null
  created_at: string | null
  updated_at: string | null
  updated_by: string | null
}

export type WorkflowLibrary = {
  workflows: WorkflowDoc[]
  roles: WorkflowRole[]
  cadences: WorkflowCadence[]
  /** Non-null when the store could not be read. "Empty" and "broken" are
   *  different answers and the page must not show one as the other. */
  store_error: string | null
}

/** What the editor sends. `n` is deliberately absent — the server numbers the
 *  steps so a printed procedure can never show a duplicated "Step 3". */
export type WorkflowDocInput = {
  /** True when this is meant to be a NEW workflow. The server then refuses
   *  (409) a web address that is already taken instead of replacing whatever
   *  is there — slugs are derived from the title, so two people naming a job
   *  similarly is an ordinary collision, not an exotic one. */
  create?: boolean
  title: string
  role: WorkflowRole
  cadence: WorkflowCadence
  trigger_text: string
  steps: { instruction: string; route?: string | null; watch_out?: string | null }[]
  gaps?: string[]
  sort_order?: number
  published?: boolean
  reviewed_at?: string | null
}

export const workflowsApi = {
  list: () => req<WorkflowLibrary>('/api/workflows'),
  get: (slug: string) => req<WorkflowDoc>(`/api/workflows/${encodeURIComponent(slug)}`),
  /** Admin-only (middleware) + CSRF header, same contract as every other
   *  /api/admin write in this client. */
  save: (slug: string, body: WorkflowDocInput) =>
    req<WorkflowDoc>(`/api/admin/workflows/${encodeURIComponent(slug)}`, {
      method: 'PUT',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    }),
  remove: (slug: string) =>
    req<{ deleted: string }>(`/api/admin/workflows/${encodeURIComponent(slug)}`, {
      method: 'DELETE',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }),
}

// ── ClickPay Suggests — server-paged envelope (owner feedback #12b) ─────────
// GET /api/recs?envelope=1 swaps the bespoke {groups, recommendations, ...}
// body for the repo-standard paged envelope (backend/api/list_paging.py
// PagedResponse: {count, total, offset, items, totals}) plus a `meta` block
// carrying the page chrome. The legacy un-enveloped call (api.listRecs above)
// is untouched — CommandCenter's SuggestsChip still reads it whole.

/** Page chrome the enveloped /api/recs carries alongside the contract keys:
 *  engine context, per-kind tab counts (post hidden/scope filtering, pre
 *  column-filter — the tab badges describe categories, not the active
 *  filter), the Hidden(n) count, scope info, and generation-truncated kinds
 *  (a kind that hit the 1000/kind generation cap renders "N+"). */
export type RecsPagedMeta = {
  engine: RecsEngineContext
  kind_counts: Partial<Record<RecommendationKind, number>>
  hidden_count: number
  /** #35: how many recs the "already staged / just applied" filter removed.
   *  Present so the page can say what it is hiding instead of silently
   *  shrinking. */
  settled_count: number
  /** PR-B: Suggests edits waiting in the Queued tab (all operators) — the
   *  tab's badge, counted server-side so the page never fetches the whole
   *  queue just to label a tab. */
  queued_count: number
  scope: RecsScopeInfo
  truncated_kinds: RecommendationKind[]
}

export type RecsPaged = Paged<Recommendation> & { meta: RecsPagedMeta }

/** W4.1 — GET /api/recs-campaign-rail. `campaign_id: null` = suggestions
 *  tied to no single campaign (dayparting, audiences). `kind_counts` is null
 *  unless a campaign was passed. */
export type RecsCampaignRail = {
  /** 'all' = every category (PR 10b: the All chip's rail). */
  kind: RecommendationKind | 'all'
  campaigns: { campaign_id: string | null; campaign_name: string | null; count: number }[]
  kind_counts: Partial<Record<RecommendationKind, number>> | null
  complete: boolean
}

export const apiRecsPaged = {
  /** Server-paged recommendations (Suggestions.tsx). `filter` is a
   *  JSON-serialized FilterSpec — the server rejects any column or op it
   *  cannot express EXACTLY (parity with the analytics envelopes), so a
   *  filter chip can never appear applied over unfiltered rows. */
  listRecsPaged: (opts: {
    /** Absent = every category (the All chip). */
    kind?: RecommendationKind
    includeHidden?: boolean
    limit?: number
    offset?: number
    sort?: string
    dir?: 'asc' | 'desc'
    search?: string
    filter?: string
    /** #35 — the server drops staged/just-applied recs by default. Pass false
     *  to get them back (the page's "Already handled (n)" toggle). */
    excludeSettled?: boolean
  }) => {
    const q = new URLSearchParams()
    q.set('envelope', '1')
    if (opts.kind) q.set('kind', opts.kind)
    if (opts.includeHidden) q.set('include_hidden', '1')
    if (opts.excludeSettled === false) q.set('exclude_settled', '0')
    if (opts.limit !== undefined) q.set('limit', String(opts.limit))
    if (opts.offset !== undefined) q.set('offset', String(opts.offset))
    if (opts.sort) q.set('sort', opts.sort)
    if (opts.dir) q.set('dir', opts.dir)
    if (opts.search) q.set('search', opts.search)
    if (opts.filter) q.set('filter', opts.filter)
    return req<RecsPaged>(`/api/recs?${q.toString()}`)
  },
  /** W4.1 — the Suggestions page's campaign rail: suggestions per campaign
   *  for one category, plus the per-category counts inside `campaign` when
   *  one is picked. Read-only (backend/api/recs_rail_api.py). */
  campaignRail: (opts: {
    /** 'all' counts every category. */
    kind: RecommendationKind | 'all'
    includeHidden?: boolean
    excludeSettled?: boolean
    /** A campaign id, or '__none__' for suggestions tied to no campaign. */
    campaign?: string
  }) => {
    const q = new URLSearchParams()
    q.set('kind', opts.kind)
    if (opts.includeHidden) q.set('include_hidden', '1')
    if (opts.excludeSettled === false) q.set('exclude_settled', '0')
    if (opts.campaign) q.set('campaign', opts.campaign)
    return req<RecsCampaignRail>(`/api/recs-campaign-rail?${q.toString()}`)
  },
  /** PR 10b — "Select all N matching → Stage": stage every suggestion the
   *  list's own filters match, across pages and categories. Same staging as
   *  stageRecsBulk (a Queued-tab row each; nothing is applied). */
  stageByQuery: (body: StageByQueryBody) =>
    afterPendingWrite(req<StageByQueryResult>('/api/recs/stage-by-query', {
      method: 'POST', body: JSON.stringify(body),
    })),
}

/** POST /api/recs/stage-by-query body (backend/api/recs_api.py). */
export type StageByQueryBody = {
  kind: RecommendationKind | 'all' | null
  include_hidden: boolean
  exclude_settled: boolean
  search: string | null
  /** The same FilterSpec JSON string the list GET takes. */
  filter: string | null
  /** Rows the operator un-ticked after choosing "all matching". */
  exclude_rec_keys: string[]
  max?: number
}

export type StageByQueryResult = {
  matched: number
  attempted: number
  truncated: boolean
  staged: StageResult[]
  errors: { rec_key: string; error: string }[]
  /** Categories with no Stage (negate / harvest / dayparting), by count. */
  skipped_by_kind: Partial<Record<RecommendationKind, number>>
  queued_total: number
  already_pending_total: number
}

// ── SellerMate W4.3: account audit (types in lib/auditApi.ts) ───────────────
export const apiAudit = {
  latest: (marketplaceId: string, history = 26) =>
    req<AuditLatest>(
      `/api/audit/latest?marketplace_id=${encodeURIComponent(marketplaceId)}&history=${history}`),
  snapshot: (id: number) => req<AuditOne>(`/api/audit/snapshots/${id}`),
  regenerate: (marketplaceId: string) =>
    req<{ marketplace_id: string; snapshot: AuditSnapshot }>('/api/audit/regenerate', {
      method: 'POST', body: JSON.stringify({ marketplace_id: marketplaceId }),
    }),
}

// ── SellerMate W1.10 — Product DNA reads ─────────────────────────────────
// lib/productDna.ts builds the /api/products/{asin}/dna/... paths and owns the
// response types; this is only the transport, so every read still goes through
// req (scope header, ApiError, cancellation).
export const apiProductDna = {
  get: <T>(path: string) => req<T>(path),
}

// ─── Advertising hub (PR 4; backend/api/ads_hub.py + /api/recs/for-rows) ───
// Read-only. Every edit the hub offers STAGES through the existing manual-edit
// and recs stage routes; nothing here writes.

/** A hub level that can carry suggestion chips (GET /api/recs/for-rows). */
export type RowSuggestionLevel = 'campaigns' | 'ad-groups' | 'targets' | 'search-terms' | 'placements'

/** One open ClickPay suggestion for a hub row (recs_api.row_suggestion). */
export type RowSuggestion = {
  rec_key: string
  kind: RecommendationKind
  /** rec.title / rec.plain_english_reason. */
  title: string
  reason: string
  current_value: number | string | null
  proposed_value: number | string | null
  confidence: 'high' | 'medium' | 'low'
  est_daily_spend_delta: number | null
  placement_bucket: 'TOS' | 'ROS' | 'PP' | null
  /** Bid / budget / placement: Stage turns it into ONE pending manual edit. */
  stageable: boolean
  /** The campaign the suggestion is filed under (rec.scope.campaign_id):
   *  where "Open in Suggestions" goes. */
  campaign_id?: string | null
  /** PR 10b: what Harvest… / Block… from the chip need. Null on the kinds
   *  that have no search term (bid / budget / placement). */
  search_term?: string | null
  ad_group_id?: string | null
  ad_group_name?: string | null
  profile_id?: string | null
  ad_type?: string | null
  asin?: string | null
}

/** Only rows with at least one open suggestion appear in `by_row`, keyed by
 *  the id the caller sent. */
export type RowSuggestions = { level: string; by_row: Record<string, RowSuggestion[]> }

export type PlacementBucketKey = 'tos' | 'ros' | 'pp'

/** One placement row of the campaign workspace — always present for a
 *  Sponsored Products campaign, with or without spend in the window. */
export type WorkspacePlacement = {
  bucket: PlacementBucketKey
  label: 'Top of search' | 'Rest of search' | 'Product pages'
  /** What the panel STAGES under: '<cid>:top' | '<cid>:rest_of' | '<cid>:product_page'. */
  entity_id: string
  /** The one key a pending edit on this bucket is matched on, whichever spelling wrote it. */
  canonical_entity_id: string
  /** Fraction (1.25 = +125%). */
  modifier: number | null
  modifier_state: 'set' | 'unknown'
  impressions: number; clicks: number; spend: number; sales: number; orders: number
  acos: number | null; cvr: number | null; ctr: number | null; cpc: number | null
  pending_edit: {
    id: number; old_value: string | null; new_value: number | null; operator_slug: string | null
    staged_at: string | null; from_suggestion: boolean
  } | null
  suggestion: RowSuggestion | null
}

export type BiddingStrategyState = 'set' | 'not_reported' | 'not_synced' | 'not_applicable'

/** GET /api/ads/campaign-workspace — one campaign's header facts and its
 *  three placement rows. The header MONEY comes from /api/campaigns. */
export type CampaignWorkspace = {
  campaign_id: string
  campaign_name: string | null
  ad_type: 'sp' | 'sb' | 'sd' | null
  targeting_type: string | null
  state: string | null
  daily_budget: number | null
  portfolio_id: string | null
  portfolio_name: string | null
  /** Amazon's raw value (LEGACY_FOR_SALES / AUTO_FOR_SALES / MANUAL / RULE_BASED); null = not synced or not SP. */
  bidding_strategy: string | null
  /** PR 22 (H-bid): what a null strategy means — `set` (value present),
   *  `not_reported` (synced, Amazon's campaign carries none), `not_synced`
   *  (not read yet), `not_applicable` (SB / SD). Absent on an old response. */
  bidding_strategy_state?: BiddingStrategyState | null
  goal_target_acos: number | null
  goal_target_acos_source: string | null
  window: { from: string; to: string; days: number }
  placements_supported: boolean
  placements: WorkspacePlacement[]
  off_amazon_spend: number
}

/** GET /api/ads/scope-labels — names for the ids a hub URL carries. */
export type ScopeLabels = {
  portfolio: { id: string; name: string | null } | null
  campaign: { id: string; name: string | null } | null
  ad_group: { id: string; name: string | null; campaign_id: string | null; campaign_name: string | null } | null
  target: {
    id: string; kind: 'keyword' | 'target'; label: string | null; match_type: string | null
    ad_group_id: string | null; ad_group_name: string | null; campaign_id: string | null; campaign_name: string | null
  } | null
}

export type ScopeLabelsQuery = { portfolio_id?: string; campaign_id?: string; ad_group_id?: string; target_id?: string }

function hubQs(params: Record<string, string | undefined>): string {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v) q.set(k, v)
  return q.toString()
}

export const adsHubApi = {
  getCampaignWorkspace: (p: { campaign_id: string; from?: string; to?: string }) =>
    req<CampaignWorkspace>(`/api/ads/campaign-workspace?${hubQs(p)}`),
  getScopeLabels: (p: ScopeLabelsQuery) =>
    req<ScopeLabels>(`/api/ads/scope-labels?${hubQs(p)}`),
}
